import React, {useMemo, useState} from "react";
import {Link} from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import {normalizeRoles, ROLES} from "../../../auth/roles";

const ROLE_LABELS = {
  ALL: "Wszyscy",
  ADMIN: "Administrator",
  MANAGER: "Kierownik",
  HR: "Kadry / HR",
  EMPLOYEE: "Pracownik",
  USER: "Użytkownik"
};

const modules = [
  {
    id: "start",
    title: "Pierwsze uruchomienie Qstaff",
    icon: "fas fa-compass",
    roles: ["ADMIN", "MANAGER", "HR"],
    summary: "Kompletny przewodnik od pustej instalacji do gotowego systemu: konfiguracja, pracownicy, koszty, dostępność, RCP, planowanie, publikacja i raporty.",
    links: [
      {path: "/app/company", label: "Dane firmy", roles: ["ADMIN"]},
      {path: "/app/settings", label: "Ustawienia globalne", roles: ["ADMIN"]},
      {path: "/app/department", label: "Działy", roles: ["ADMIN"]},
      {path: "/app/employees", label: "Pracownicy", roles: ["ADMIN", "HR", "MANAGER"]},
      {path: "/app/resource-plan", label: "Plany zapotrzebowania", roles: ["ADMIN", "MANAGER"]},
      {path: "/app/scheduler", label: "Grafik", roles: ["ADMIN", "MANAGER"]},
      {path: "/app/schedule-reports", label: "Raporty", roles: ["ADMIN", "MANAGER"]}
    ],
    articles: [
      {
        title: "Pełna kolejność konfiguracji nowej instalacji",
        lead: "Jeżeli widzisz Qstaff pierwszy raz, wykonuj poniższe kroki po kolei. Późniejsze moduły korzystają z danych utworzonych wcześniej, dlatego rozpoczęcie od grafiku zwykle kończy się pustą obsadą, błędnymi kosztami albo brakiem kandydatów.",
        steps: [
          "Zaloguj się jako Administrator i zacznij od danych firmy oraz Ustawień globalnych. Jeżeli pracujesz jako HR lub Kierownik, część kroków administracyjnych musi wcześniej wykonać Administrator.",
          "Sprawdź i uzupełnij podstawowe słowniki: kraje i województwa, płeć, etapy zatrudnienia, typy nieobecności, typy badań, certyfikatów, dokumentów, umów, stawek, stanowisk i pozostałe słowniki używane w Twojej organizacji.",
          "Skonfiguruj czas pracy i kalendarz: systemy/czasy pracy, dni wolne oraz reguły globalne potrzebne do kontroli norm, odpoczynków, kolejnych dni pracy, nieobecności i alertów.",
          "Zbuduj strukturę organizacyjną: utwórz działy, lokalizacje i strefy, typy/poziomy stanowisk oraz stanowiska. Przypisz stanowiska do właściwych działów i — tam gdzie ma to znaczenie — do lokalizacji.",
          "Skonfiguruj zatrudnienie i koszty: dodaj firmy zatrudniające lub agencje, typy wynagrodzeń, podstawy i stawki ZUS/KRUS oraz parametry potrzebne do wyliczania rzeczywistego kosztu pracy.",
          "Przygotuj obsługę umów: typy umów, szablony dokumentów oraz wzorce numeracji dla lat i typów dokumentów, z których będziesz korzystać.",
          "Skonfiguruj konta użytkowników i role pomocnicze. Pamiętaj, że pracownik w kartotece i konto użytkownika to dwie różne rzeczy. Kierownika lub zastępcę przypisujesz w definicji działu; po powiązaniu konta z tym pracownikiem standardowe uprawnienia kierownicze wynikają z tej funkcji automatycznie.",
          "Dodaj pracowników. Uzupełnij dane osobowe i firmowe, status uczeń/student/KRUS, firmę zatrudniającą, działy, stanowiska, reguły czasu pracy, dane kontaktowe oraz kanały komunikacji.",
          "Dodaj aktywne umowy pracowników i stawki. Przy wielu umowach wskaż właściwą umowę domyślną, a przy wielu stanowiskach sprawdź źródło stawki dla każdego przypisania pracownik × stanowisko.",
          "Skonfiguruj kwalifikacje: szkolenia, certyfikaty, badania, umiejętności oraz testy szkoleniowe. Następnie uzupełnij te dane w kartotekach pracowników i sprawdź wymagania stanowisk, ponieważ mogą blokować przydział do grafiku.",
          "Uzupełnij dostępność, nieobecności i reguły indywidualne pracowników. Jeżeli korzystasz z pracowników weekendowych/tygodniowych, przygotuj także sterowanie sezonowością.",
          "Przygotuj Panel pracownika: opublikuj regulaminy, wzory dokumentów i potrzebne materiały, skonfiguruj proces kontaktu z firmą oraz — jeżeli ma być używany — terminarz spotkań HR.",
          "Skonfiguruj komunikację e-mail/SMS i wykonaj próbę wysyłki. Pracownik musi mieć poprawny adres lub numer telefonu oraz włączony odpowiedni kanał komunikacji.",
          "Skonfiguruj RCP: karty pracowników, terminale/czytniki, strefy i lokalizacje. Wykonaj próbne zdarzenia START, przerwa i END, aby sprawdzić, czy system poprawnie buduje sesję pracy.",
          "Jeżeli planujesz według sprzedaży i kosztów, skonfiguruj parametry ekonomiczne: sprzedaż planowaną, %COL TARGET, SPMH TARGET, minimalną i maksymalną obsadę oraz — jeżeli korzystasz — integrację pogodową.",
          "Utwórz plan zapotrzebowania. Określ dla każdego dnia, działu i stanowiska ilu pracowników potrzebujesz oraz w jakich godzinach. Plan zapotrzebowania mówi ile osób potrzeba, ale jeszcze nie wskazuje konkretnych pracowników.",
          "Utwórz grafik roboczy, uruchom generator i przeczytaj diagnostykę. Sprawdź braki obsady, odrzuconych kandydatów, koszty, normy czasu pracy i ostrzeżenia. Następnie wykonaj potrzebne poprawki ręczne w kalendarzu grafiku.",
          "Opublikuj grafik i sprawdź wysyłkę powiadomień. Zaloguj się testowo jako pracownik i upewnij się, że widzi Mój grafik, powiadomienie oraz funkcje składania wniosków i zamiany zmian.",
          "Po rozpoczęciu pracy porównuj grafik z RCP. Korzystaj z raportów plan vs wykonanie, kosztów, spóźnień, nadgodzin, norm miesięcznych, SPMH oraz eksportów dla księgowości/Gang."
        ],
        tip: "Najlepiej skonfigurować najpierw jeden dział, kilka stanowisk i 2–3 testowych pracowników, przejść cały proces aż do publikacji i RCP, a dopiero potem wprowadzać pełną bazę.",
        warning: "Nie pomijaj umów, stawek, dostępności ani kwalifikacji. Pracownik może istnieć w kartotece, ale generator prawidłowo go odrzuci, jeżeli nie spełnia warunków przypisania."
      },
      {
        title: "Etap 1 — firma, ustawienia globalne i słowniki",
        lead: "Ten etap tworzy wspólną konfigurację, z której korzystają kartoteki pracowników, umowy, grafiki, RCP i raporty.",
        bullets: [
          "Dane firmy: uzupełnij podstawowe dane organizacji i sprawdź ustawienia dotyczące dokumentów pracowniczych.",
          "Ustawienia globalne: przejrzyj limity i progi używane przez grafiki, nieobecności, RCP, szkolenia, oceny, eksport Gang i integrację pogodową.",
          "Czas pracy i dni wolne: skonfiguruj elementy używane do norm, odpoczynków, kolejnych dni pracy i miesięcznych rozliczeń.",
          "Słowniki kadrowe: przygotuj co najmniej te typy nieobecności, badań, certyfikatów, dokumentów, umów i wynagrodzeń, których faktycznie będziesz używać.",
          "Słowniki pomocnicze, takie jak kraje, województwa, NFZ, urzędy skarbowe, formy prawne czy etapy zatrudnienia, uzupełnij zgodnie z zakresem danych prowadzonych w Qstaff."
        ],
        note: "Nie każdy słownik musi być rozbudowany od pierwszego dnia. Ważne jest natomiast, aby pozycje używane później w kartotekach i umowach istniały zanim rozpoczniesz masowe wprowadzanie pracowników.",
        warning: "Jeżeli w późniejszym formularzu brakuje wartości na liście wyboru, zwykle oznacza to brak odpowiedniej pozycji w słowniku, a nie błąd samego formularza."
      },
      {
        title: "Etap 2 — struktura organizacyjna: działy, lokalizacje i stanowiska",
        steps: [
          "Utwórz działy odpowiadające rzeczywistej organizacji pracy.",
          "Dla każdego działu ustaw kierownika i ewentualnego zastępcę. Możesz wybrać dowolnego aktywnego pracownika; jeżeli nie ma jeszcze stanowiska Funkcyjnego lub Kierowniczego w tym dziale, wybierz takie stanowisko w tym samym formularzu, a Qstaff przypisze je automatycznie przy zapisie.",
          "Utwórz lokalizacje i strefy tam, gdzie chcesz rozliczać SPMH lub kontrolować miejsce odbicia RCP.",
          "Utwórz typy/poziomy stanowisk oraz konkretne stanowiska.",
          "Przypisz stanowiska do działów. Jedno stanowisko może należeć do określonego działu, a pracownik może mieć kilka stanowisk i dzięki temu pracować w więcej niż jednym dziale.",
          "Dla stanowisk, które mają stałe miejsce pracy, przypisz właściwą lokalizację. Dla stanowisk przemieszczających się pozostaw konfigurację zgodną z rzeczywistym sposobem pracy."
        ],
        tip: "Najpierw odwzoruj strukturę firmy, a dopiero potem dodawaj dużą liczbę pracowników. Dzięki temu od razu przypiszesz ich do właściwych działów i stanowisk.",
        warning: "Samo stanowisko Funkcyjne lub Kierownicze nie nadaje dostępu do funkcji kierowniczych. Standardowe uprawnienia wynikają dopiero z przypisania pracownika jako Kierownika lub Zastępcy konkretnego działu; indywidualne wyjątki nadal można ustawić w uprawnieniach użytkownika."
      },
      {
        title: "Etap 3 — zatrudnienie, ZUS/KRUS, stawki i umowy",
        bullets: [
          "Dodaj podmioty zatrudniające: firmę własną oraz agencje pracy, jeśli z nich korzystasz. Dla agencji uzupełnij parametry wpływające na koszt, np. prowizję.",
          "Sprawdź podstawy ZUS, stawki ZUS dla właściwego roku i parametry składki wypadkowej. Status uczeń/student oraz KRUS wpływają na sposób kalkulacji kosztu.",
          "Przygotuj typy i formy rozliczenia umów: godzinowe albo miesięczne, zgodnie z używanymi modelami zatrudnienia.",
          "Jeżeli korzystasz z generatora dokumentów, przygotuj szablony umów oraz wzorce numeracji dla odpowiednich typów dokumentów i lat.",
          "Po utworzeniu pracownika dodaj jego umowę lub umowy. Przy kilku równoczesnych umowach wybierz jedną jako domyślną dla grafiku i kosztów.",
          "Przy każdym stanowisku pracownika sprawdź źródło stawki. Qstaff może korzystać ze stawki wynikającej z umowy, definicji stanowiska lub wartości ustawionej indywidualnie."
        ],
        note: "Koszt używany przez grafiki i raporty nie jest wyłącznie stawką brutto. System uwzględnia dane zatrudnienia, składki, status pracownika i ewentualny koszt agencji.",
        warning: "Brak aktywnej umowy lub niespójne źródło stawki może powodować brak kandydata w generatorze albo niepełną kalkulację kosztu."
      },
      {
        title: "Etap 4 — konta użytkowników i uprawnienia",
        lead: "Kartoteka pracownika przechowuje dane osoby, a konto użytkownika umożliwia logowanie. Te dwa elementy muszą być prawidłowo powiązane.",
        bullets: [
          "ADMIN: konfiguracja całego systemu, słowników, użytkowników i ustawień.",
          "HR: kartoteki, umowy, stawki, absencje, szkolenia, oceny i dokumenty w zakresie przewidzianym dla kadr.",
          "MANAGER: planowanie, wnioski, urlopy działowe, RCP i raporty w zakresie zarządzanych działów.",
          "EMPLOYEE/MINION: samoobsługa pracownika — własny grafik, wnioski, powiadomienia, dokumenty, szkolenia i inne funkcje Panelu pracownika.",
          "Nie trzeba ręcznie nadawać roli MANAGER osobie zapisanej jako Kierownik lub Zastępca działu. Jeżeli ma powiązane konto aplikacji, Qstaff automatycznie dołącza profil uprawnień MANAGER w zakresie zarządzanych działów.",
          "Przy koncie pracownika upewnij się, że konto wskazuje właściwą kartotekę pracownika."
        ],
        tip: "Po skonfigurowaniu ról wykonaj test logowania na jednym koncie HR, Kierownika i Pracownika. To najszybszy sposób, aby znaleźć błędne zakresy dostępu przed uruchomieniem systemu dla wszystkich."
      },
      {
        title: "Etap 5 — kompletna kartoteka pracownika",
        steps: [
          "Dodaj dane osobowe, kontakt awaryjny, adresy i dane potrzebne kadrom.",
          "Uzupełnij dane firmowe: etap/status zatrudnienia, firmę zatrudniającą oraz informacje uczeń/student/KRUS.",
          "Przypisz dział główny, stanowiska i ewentualnie kilka stanowisk z różnych działów.",
          "Dodaj umowę lub umowy i sprawdź ich okres obowiązywania.",
          "Ustaw stawki dla stanowisk pracownika oraz ich źródło.",
          "Uzupełnij indywidualne reguły czasu pracy, np. minimalną/maksymalną liczbę godzin albo stałe ograniczenia dostępności, jeśli dotyczą tej osoby.",
          "Dodaj szkolenia, certyfikaty, badania, umiejętności oraz wymagane dokumenty.",
          "Jeżeli pracownik będzie używał RCP, przypisz mu kartę i pozostałe dane identyfikacyjne używane przez terminal.",
          "Jeżeli ma otrzymywać wiadomości, uzupełnij poprawny e-mail/telefon i włącz właściwy kanał komunikacji.",
          "Jeżeli ma korzystać z aplikacji, utwórz/powiąż konto użytkownika i nadaj odpowiednią rolę."
        ],
        tip: "Przed przejściem do planowania otwórz kartotekę testowego pracownika i sprawdź ją od początku do końca. Jeden kompletny wzorzec ułatwia późniejsze masowe wprowadzanie danych."
      },
      {
        title: "Etap 6 — szkolenia, certyfikaty, badania i testy",
        bullets: [
          "Zdefiniuj typy szkoleń, certyfikatów i badań używanych w organizacji.",
          "Przypisz wymagania do stanowisk tam, gdzie pracownik bez danej kwalifikacji nie powinien pracować.",
          "W kartotece pracownika zapisz ukończone szkolenia, certyfikaty, terminy ważności i osoby szkolące/oceniające.",
          "Jeżeli używasz testów szkoleniowych, przygotuj test, pytania, klucz odpowiedzi, liczbę podejść i zakres pracowników.",
          "Sprawdź ustawienie limitu niezaliczonych testów, jeżeli wynik testu ma wpływać na dopuszczenie do grafiku.",
          "Przetestuj jeden przypadek pracownika spełniającego wymagania i jeden przypadek pracownika, którego system powinien odrzucić."
        ],
        warning: "Generator może celowo nie przydzielić dostępnego pracownika, jeżeli nie ma wymaganej kwalifikacji albo przekroczył limit niezaliczonych testów."
      },
      {
        title: "Etap 7 — dostępność, nieobecności i sezonowość",
        steps: [
          "Uzupełnij standardową dostępność pracowników i ich stałe ograniczenia, np. tylko weekendy albo tylko dni robocze.",
          "Dodaj niestandardowe okresy dostępności lub niedostępności oraz zaplanowane nieobecności.",
          "Sprawdź Grafik dostępności w układzie odpowiadającym planowanemu okresowi i upewnij się, że dane są zgodne z kartotekami.",
          "Jeżeli w dziale obowiązuje limit jednoczesnych urlopów, ustaw go w konfiguracji działu, a następnie sprawdź miesięczny widok Urlopy działów.",
          "Przy zmianie sezonu użyj sterowania sezonowością zamiast ręcznie edytować każdą osobę."
        ],
        note: "Dostępność jest jednym z podstawowych filtrów generatora. Jeżeli system nie widzi pracownika jako dostępnego w danym dniu, pozostałe kryteria nie pomogą w jego przydzieleniu."
      },
      {
        title: "Etap 8 — Panel pracownika, zasoby HR i komunikacja",
        bullets: [
          "W Zasobach pracownika opublikuj regulaminy, wzory dokumentów, instrukcje oraz linki, które pracownicy mają widzieć w Panelu pracownika.",
          "Jeżeli korzystasz ze spotkań HR, przygotuj dostępność konsultacji indywidualnych i zasady spotkań grupowych.",
          "Sprawdź możliwość kontaktu pracownika z kierownikiem/HR oraz poprawność listy przełożonych.",
          "Skonfiguruj kanały e-mail i SMS. W środowisku testowym można użyć symulatora komunikacji; przed produkcją sprawdź właściwą konfigurację operatora SMS/e-mail.",
          "Przetestuj na koncie pracownika: widok zasobów, własnych dokumentów, spotkań HR, wniosków i powiadomień."
        ],
        warning: "Sama publikacja zasobu nie wystarczy, jeżeli pracownik nie ma konta lub nie ma uprawnienia do Panelu pracownika."
      },
      {
        title: "Etap 9 — konfiguracja i test RCP",
        steps: [
          "Dodaj karty RCP i przypisz je do właściwych pracowników.",
          "Dodaj terminale/czytniki i skonfiguruj ich autoryzację.",
          "Powiąż terminale ze strefami/lokalizacjami tam, gdzie miejsce odbicia ma być kontrolowane.",
          "Sprawdź ustawienia dotyczące brakującego START, maksymalnej przerwy oraz innych alertów RCP.",
          "Wykonaj testową sesję: START → początek przerwy → koniec przerwy → END.",
          "Po utworzeniu opublikowanego grafiku wykonaj drugi test i sprawdź plan vs wykonanie, spóźnienie, wcześniejsze wyjście oraz nadgodziny."
        ],
        tip: "Najpierw testuj RCP na jednym pracowniku i jednym terminalu. Dopiero po poprawnym plan vs wykonanie dodawaj pozostałe terminale i karty."
      },
      {
        title: "Etap 10 — sprzedaż, pogoda, %COL, SPMH i limity obsady",
        bullets: [
          "Wprowadź sprzedaż planowaną dla okresów, w których chcesz planować obsadę ekonomicznie.",
          "Ustaw %COL TARGET i/lub SPMH TARGET zgodnie z przyjętym sposobem kontroli kosztu pracy.",
          "Zdefiniuj minimalną i maksymalną obsadę dla dni/lokalizacji, dla których chcesz otrzymywać ostrzeżenia o brakach albo nadmiarze pracowników.",
          "Jeżeli używasz pogody do wspomagania zapotrzebowania, włącz integrację, ustaw współrzędne Parku, strefę czasową i sprawdź pobranie prognozy.",
          "Po utworzeniu grafiku porównuj %COL PLAN i SPMH PLAN z targetami. Po zakończeniu dnia uzupełniaj dane wykonania i analizuj rzeczywiste SPMH."
        ],
        note: "Te dane nie są konieczne do samego ręcznego utworzenia zmiany, ale są potrzebne, jeżeli Qstaff ma wspierać planowanie obsady względem sprzedaży i pełnego kosztu pracy."
      },
      {
        title: "Etap 11 — plan zapotrzebowania, grafik i publikacja",
        steps: [
          "Utwórz plan zapotrzebowania dla właściwego typu i zakresu dat.",
          "Dla każdego dnia zdefiniuj potrzebne stanowiska, liczbę osób i godziny pracy. Jeżeli używasz planowania działowego, sprawdź właściwy dział.",
          "Utwórz grafik roboczy dla zgodnego okresu i typu planu.",
          "Uruchom generator obsady i obserwuj postęp oraz diagnostykę.",
          "Przejrzyj nieobsadzone pozycje i powody odrzucenia kandydatów. Nie poprawiaj danych w ciemno — najpierw sprawdź konkretną przyczynę.",
          "W kalendarzu grafiku wykonaj ręczne korekty godzin, stanowisk, dostępności lub nieobecności, jeżeli są potrzebne.",
          "Sprawdź godziny tygodniowe/miesięczne, kolejne dni pracy, konflikty zmian, koszt, %COL/SPMH i pozostałe ostrzeżenia.",
          "Opublikuj grafik dopiero po zaakceptowaniu diagnostyki.",
          "Sprawdź Centrum powiadomień i statusy wysyłki, a następnie zaloguj się na konto testowego pracownika i potwierdź, że widzi opublikowany grafik."
        ],
        warning: "Plan zapotrzebowania, grafik roboczy i opublikowany grafik to trzy różne etapy. Sama obecność planu zapotrzebowania nie tworzy pracownikowi zmiany."
      },
      {
        title: "Etap 12 — kontrola po uruchomieniu i raporty",
        bullets: [
          "Codziennie sprawdzaj Tablicę: braki obsady, problemy RCP, kończące się umowy, nieobecności i inne alerty wymagające reakcji.",
          "Porównuj planowane i wykonane godziny na podstawie grafiku oraz RCP.",
          "Kontroluj spóźnienia, wcześniejsze wyjścia, przerwy, nadgodziny, normy miesięczne i bilans godzin.",
          "Analizuj koszty według pracowników, stanowisk i stawek oraz wskaźniki %COL/SPMH.",
          "Przed pierwszym rozliczeniem wykonaj test raportu księgowego i eksportu Gang na danych testowych, a dopiero potem użyj go dla pełnego okresu.",
          "Po każdej większej zmianie konfiguracji sprawdź ponownie jeden pełny scenariusz: plan → grafik → publikacja → RCP → raport."
        ],
        tip: "Qstaff jest systemem zależności. Najszybszą metodą diagnozy problemu jest ustalenie, na którym etapie łańcucha brakuje danych: pracownik → umowa/stawka → stanowisko/kwalifikacje → dostępność → plan → grafik → publikacja → RCP → raport."
      },
      {
        title: "Checklista — system gotowy do pracy",
        lead: "Przed udostępnieniem Qstaff większej grupie użytkowników sprawdź poniższe punkty.",
        bullets: [
          "Dane firmy i ustawienia globalne są uzupełnione.",
          "Podstawowe słowniki zawierają używane w firmie wartości.",
          "Działy, stanowiska, lokalizacje i kierownicy są przypisani zgodnie z rzeczywistą organizacją.",
          "Stawki ZUS/KRUS, firmy zatrudniające, typy umów, szablony i numeracja są przygotowane dla bieżącego roku.",
          "Każdy testowy pracownik ma dział/stanowisko, aktywną umowę, poprawną stawkę, dostępność i wymagane kwalifikacje.",
          "Konta ADMIN/HR/MANAGER/EMPLOYEE mają poprawne role i zakres danych.",
          "Panel pracownika zawiera potrzebne zasoby i działa na koncie pracownika.",
          "E-mail/SMS został przetestowany, a status wysyłki jest widoczny.",
          "Karta i terminal RCP tworzą poprawną sesję pracy.",
          "Plan zapotrzebowania generuje grafik dla testowego działu.",
          "Grafik można poprawić ręcznie, opublikować i zobaczyć z konta pracownika.",
          "Powiadomienie o publikacji dociera do testowego pracownika.",
          "RCP po opublikowanej zmianie jest widoczne jako plan vs wykonanie.",
          "Raport czasu pracy i kosztów zwraca dane zgodne z testowym scenariuszem.",
          "Eksport księgowy/Gang został sprawdzony przed pierwszym użyciem produkcyjnym."
        ],
        warning: "Jeżeli którykolwiek z punktów testowego scenariusza nie działa, nie zwiększaj jeszcze skali danych. Najpierw usuń problem na jednym dziale i kilku pracownikach."
      }
    ]
  },
  {
    id: "dashboard",
    title: "Dashboard / Tablica",
    icon: "fas fa-th-large",
    roles: ["ADMIN", "HR", "MANAGER", "EMPLOYEE"],
    summary: "Szybki przegląd tego, co dzieje się dziś i jutro, oraz skróty do najważniejszych modułów.",
    links: [{path: "/app/dashboard", label: "Otwórz Tablicę", roles: ["ADMIN", "HR", "MANAGER", "EMPLOYEE"]}],
    articles: [
      {
        title: "Dashboard kierownika i administratora",
        lead: "To ekran do codziennego sprawdzenia sytuacji operacyjnej bez przeklikiwania się przez wszystkie raporty.",
        bullets: [
          "Obsada dzisiaj i jutro - ile zmian jest obsadzonych, a ile nadal ma braki.",
          "Planowane i wykonane roboczogodziny.",
          "Wnioski oczekujące na decyzję.",
          "Problemy RCP, np. brak odbicia lub spóźnienia.",
          "Plan vs wykonanie dla obsady, godzin i sprzedaży.",
          "SPMH oraz %COL w kontekście planu i wykonania.",
          "Sytuacja w poszczególnych działach i lista rzeczy wymagających uwagi."
        ],
        tip: "Dobrą praktyką jest rozpoczęcie dnia właśnie od Tablicy: najpierw braki na dziś, potem problemy na jutro, a dopiero później dalsze planowanie."
      },
      {
        title: "Dashboard pracownika",
        bullets: [
          "Informuje, czy pracownik ma zmianę dzisiaj i jutro.",
          "Pokazuje najbliższe zmiany oraz powiadomienia.",
          "Daje szybkie wejście do własnego grafiku, danych, powiadomień i Bazy Wiedzy."
        ]
      }
    ]
  },
  {
    id: "employees",
    title: "Pracownicy i kartoteka",
    icon: "fas fa-users",
    roles: ["ADMIN", "HR", "MANAGER"],
    summary: "Centralna kartoteka: dane osobowe, zatrudnienie, kwalifikacje, nieobecności, dostępność, reguły grafiku i historia pracy.",
    links: [{path: "/app/employees", label: "Pracownicy", roles: ["ADMIN", "HR", "MANAGER"]}],
    articles: [
      {
        title: "Lista pracowników - do czego służy",
        lead: "Lista jest punktem wejścia do całej kartoteki pracowniczej. Możesz wyszukiwać i filtrować pracowników po danych widocznych w kolumnach.",
        bullets: [
          "Login, imię i nazwisko.",
          "Umiejętności.",
          "Działy wynikające z działu głównego i aktywnych stanowisk.",
          "Etap zatrudnienia, status uczeń/student i KRUS.",
          "Oznaczenie pracownika jako niechcianego.",
          "Status aktualnej umowy."
        ],
        tip: "Eksport do Excela bierze cały aktualnie przefiltrowany zbiór pracowników, a nie tylko bieżącą stronę tabeli. Filtr Działy działa również przy pracowniku przypisanym do kilku działów, a wszystkie jego działy trafiają do jednej tekstowej komórki Działy. Przycisk Kolumny pozwala wybrać pola widoczne na liście. Filtr Archiwum ma trzy wartości: Aktywni, Archiwalni i Wszyscy; eksport respektuje zarówno ten wybór, jak i filtry kolumn tabeli."
      },
      {
        title: "Dodawanie pracownika - dane podstawowe",
        steps: [
          "Kliknij Dodaj.",
          "Nadaj login i wprowadź wymagane dane osobowe.",
          "Jeżeli pracownik ma logować się do Qstaff, zaznacz utworzenie użytkownika aplikacji, wybierz role i ustaw hasło. Przy nowym koncie domyślnie zaznaczana jest rola Pracownik, ale administrator może ustawić inny potrzebny zestaw ról.",
          "Uzupełnij kontakt, adresy, dokument tożsamości, konto bankowe i dane urzędowe. Jeśli rachunek należy do rodzica lub innej osoby, wyłącz opcję Rachunek należy do pracownika i uzupełnij dane oświadczenia.",
          "Ustaw status ucznia/studenta albo KRUS, jeśli dotyczy.",
          "Przypisz kartę RCP, jeżeli pracownik ma już kartę w systemie.",
          "Zapisz pracownika. Po pierwszym zapisie pojawią się dodatkowe sekcje danych firmowych i historii pracy."
        ],
        note: "Pole Niechciany służy do oznaczenia osoby, której nie należy automatycznie przydzielać do pracy. Warto wpisać również powód."
      },
      {
        title: "Zdjęcie pracownika w kartotece",
        lead: "Zdjęcie pracownika jest przechowywane przez backend Qstaff i nie zależy od katalogu plików frontendu ani od komputera, na którym uruchomiono aplikację.",
        steps: [
          "Otwórz kartotekę istniejącego pracownika.",
          "W sekcji Zdjęcie pracownika wybierz Dodaj zdjęcie albo Zmień zdjęcie.",
          "Wybierz plik JPG, PNG lub WEBP o rozmiarze do 8 MB.",
          "Po przesłaniu zdjęcie jest od razu zapisane i widoczne w kartotece. Nie trzeba osobno zapisywać całego formularza pracownika.",
          "Zdjęcie można usunąć przyciskiem Usuń zdjęcie."
        ],
        note: "Zdjęcie może dodać administrator, Kadry / HR albo kierownik mający dostęp do danego pracownika. Ten sam formularz działa w przeglądarce na telefonie."
      },
      {
        title: "Dokumenty pracownika i folder pracowniczy",
        lead: "Qstaff pokazuje w jednym miejscu zarówno dokumenty przekazywane przez pracownika do HR, jak i pliki znajdujące się w jego katalogu pracowniczym na serwerze, w tym wygenerowane umowy.",
        steps: [
          "Otwórz kartotekę istniejącego pracownika i przejdź do zakładki Dokumenty.",
          "W sekcji dokumentów HR możesz dodać Legitymację uczniowską / studencką, Zgodę na wypłatę na rachunek osoby trzeciej albo Inny dokument dla HR. Obsługiwane są PDF, JPG i PNG do 10 MB.",
          "Niżej znajduje się sekcja Dokumenty z folderu pracownika. Pokazuje ona pliki z katalogu skonfigurowanego w Ustawieniach aplikacji, również z podfolderów takich jak Umowy.",
          "Dla obsługiwanych formatów użyj Podgląd. Każdy plik można pobrać przyciskiem Pobierz.",
          "Umowa zapisana z modułu tworzenia i edycji umów przyciskiem zapisu do folderu pracownika pojawia się w tej sekcji automatycznie."
        ],
        note: "Administrator i Kadry / HR mają dostęp zgodnie z uprawnieniem Dokumenty pracownika - podgląd. Domyślnie również Kierownik otrzymuje podgląd dokumentów pracowników ze swoich zarządzanych działów, bez prawa dodawania i usuwania dokumentów HR."
      },
      {
        title: "Wypłata wynagrodzenia na konto rodzica lub innej osoby",
        lead: "Qstaff rozróżnia rachunek należący do pracownika od rachunku osoby trzeciej i wymaga jawnego potwierdzenia zgody na taki sposób wypłaty.",
        steps: [
          "W sekcji Status pracownika i rozliczenia wpisz numer konta bankowego.",
          "Jeżeli konto nie należy do pracownika, wyłącz Rachunek należy do pracownika.",
          "Wpisz imię i nazwisko właściciela rachunku.",
          "Włącz Zgoda na wypłatę na rachunek osoby trzeciej i podaj datę oświadczenia.",
          "Zapisz kartotekę pracownika.",
          "Przejdź do zakładki Dokumenty i dołącz podpisany skan lub PDF jako Zgoda na wypłatę na rachunek osoby trzeciej."
        ],
        warning: "Backend nie pozwoli zapisać rachunku osoby trzeciej bez numeru rachunku, danych właściciela, potwierdzonej zgody i daty oświadczenia.",
        note: "Jeżeli rachunek należy do pracownika, pola dotyczące osoby trzeciej są automatycznie czyszczone po zapisie. Zakładka Dokumenty sygnalizuje brak skanu oświadczenia, gdy konto należy do innej osoby."
      },
      {
        title: "Dane firmowe pracownika",
        lead: "Ta część decyduje nie tylko o kartotece HR, ale również o tym, czy pracownik może zostać użyty w grafiku.",
        bullets: [
          "Firma zatrudniająca i etap zatrudnienia.",
          "Dział główny i przełożony.",
          "Stanowiska pracownika - jedna osoba może mieć więcej niż jedno stanowisko, także w kilku działach.",
          "Umiejętności miękkie oraz opis umiejętności.",
          "Szkolenia i certyfikaty.",
          "Pochwały i przewinienia.",
          "Badania lekarskie.",
          "Nieobecności.",
          "Indywidualne reguły grafiku i dostępność."
        ],
        note: "Dział główny jest informacją organizacyjną i może pozostać pusty, jeżeli pracownik ma co najmniej jedno aktywne stanowisko należące do działu. Rzeczywisty zakres działów pracownika jest uzupełniany przez działy stanowisk. Zmiana działu głównego nie wymaga usuwania stanowisk z innych działów."
      },
      {
        title: "Pracownik w kilku działach",
        lead: "Qstaff pozwala przypisać jednego pracownika do stanowisk należących do różnych działów bez duplikowania jego kartoteki.",
        bullets: [
          "Dział główny pozostaje podstawową informacją organizacyjną pracownika.",
          "Każde aktywne stanowisko zachowuje własny dział.",
          "Lista pracowników pokazuje łączną listę działów wynikającą z działu głównego i stanowisk.",
          "Generator i uprawnienia kierownika mogą korzystać z przypisania stanowiska do właściwego działu."
        ],
        note: "Nie trzeba usuwać stanowisk przy zmianie działu głównego."
      },
      {
        title: "Źródło stawki na stanowisku pracownika",
        lead: "Przy każdym stanowisku pracownika można wskazać, skąd ma zostać pobrana stawka bazowa. Wybrana wartość jest następnie zapisywana przy konkretnym przypisaniu pracownik + stanowisko.",
        bullets: [
          "Z umowy - dostępne tylko wtedy, gdy pracownik ma obowiązującą umowę z określoną formą rozliczenia i stawką.",
          "Z definicji stanowiska - dostępne tylko wtedy, gdy stanowisko ma zdefiniowaną stawkę.",
          "Indywidualna - pozwala wpisać stawkę bezpośrednio dla tego pracownika i stanowiska, godzinowo albo miesięcznie.",
          "Po wyborze Umowy lub Definicji stanowiska backend zapisuje kopię stawki. Późniejsza zmiana stawki globalnej nie zmienia automatycznie już zapisanego przypisania pracownika."
        ],
        warning: "Zmiana umowy albo globalnej stawki stanowiska nie powinna przepisywać historii ani istniejących przypisań. Jeżeli chcesz świadomie pobrać nową wartość, zmień źródło stawki i wybierz je ponownie."
      },
      {
        title: "Archiwum pracowników",
        lead: "Pracownika, którego dane i historia muszą pozostać w systemie, można przenieść do archiwum zamiast usuwać.",
        steps: [
          "Otwórz kartotekę pracownika i wybierz Przenieś do archiwum.",
          "Opcjonalnie wpisz powód archiwizacji.",
          "Zarchiwizowany pracownik znika z domyślnej listy aktywnych pracowników.",
          "Na liście ustaw filtr Archiwum na Archiwalni albo Wszyscy, aby zobaczyć pracowników zarchiwizowanych.",
          "Z kartoteki pracownika można użyć Przywróć z archiwum."
        ],
        warning: "Archiwizacja wyłącza powiązane konto użytkownika Qstaff. Przywrócenie kartoteki nie włącza konta automatycznie - dostęp do aplikacji powinien zostać nadany świadomie przez administratora."
      },
      {
        title: "Wiele równoczesnych umów",
        lead: "Jeden pracownik może posiadać kilka równolegle obowiązujących umów, np. z różnymi podmiotami lub na różnych zasadach rozliczenia.",
        bullets: [
          "Kartoteka pokazuje listę wszystkich umów pracownika.",
          "Każda umowa może mieć własną firmę zatrudniającą lub agencję; pozwala to odwzorować równoległe zatrudnienie bezpośrednie i agencyjne.",
          "Jedną umowę można oznaczyć jako Domyślną do grafiku i kosztów.",
          "Jeżeli dla pracownika nie ma jeszcze umowy domyślnej, system automatycznie wyznacza pierwszą zapisaną umowę jako domyślną.",
          "Ustawienie innej umowy jako domyślnej automatycznie usuwa to oznaczenie z pozostałych umów pracownika.",
          "Przy ocenie dostępności w danym dniu pracownik jest traktowany jako posiadający umowę, jeżeli obowiązuje co najmniej jedna z jego umów."
        ],
        note: "Umowa domyślna służy jako jednoznaczne źródło dla kalkulacji kosztu i procesów grafiku, gdy kilka umów obowiązuje jednocześnie. Kalkulacja prowizji agencji korzysta w pierwszej kolejności z firmy zatrudniającej zapisanej na wybranej umowie."
      },
      {
        title: "Wyjazdy służbowe w kartotece",
        steps: [
          "Otwórz kartotekę pracownika i przejdź do sekcji Wyjazdy służbowe.",
          "Dodaj typ wyjazdu, okres, miejsce docelowe i - jeśli dotyczy - środek transportu, numer rejestracyjny oraz dystans.",
          "Po rozliczeniu oznacz wyjazd jako rozliczony.",
          "Wyjazd można później edytować lub usunąć."
        ],
        note: "Kierownik może zarządzać wyjazdami tylko pracowników znajdujących się w jego zakresie dostępu; HR i Administrator mają szerszy dostęp zgodnie z rolą."
      },
      {
        title: "Alerty dotyczące umów",
        lead: "Dashboard kierownika i HR pokazuje pracowników bez aktywnej umowy oraz umowy zbliżające się do końca.",
        bullets: [
          "Pracownik bez żadnej obowiązującej umowy jest oznaczany na Tablicy jako pilny alert.",
          "Każda aktywna umowa kończąca się w skonfigurowanym okresie ostrzegawczym trafia do alertów - także wtedy, gdy pracownik ma równocześnie inną aktywną umowę.",
          "Tablica pokazuje liczbę problemów oraz najbliższą kończącą się umowę z nazwiskiem pracownika, numerem umowy i datą końca.",
          "Po wybraniu działu alerty są ograniczane do pracowników należących do tego działu.",
          "Kierownik widzi tylko alerty pracowników ze swojego zakresu dostępu."
        ]
      },
      {
        title: "Stanowiska, szkolenia i certyfikaty a grafik",
        bullets: [
          "Pracownik musi posiadać stanowisko zgodne z pozycją, na którą ma zostać przydzielony.",
          "Jeżeli stanowisko wymaga szkolenia lub certyfikatu, system uwzględnia te wymagania przy ocenie kwalifikacji do zmiany.",
          "Wygasły certyfikat albo brak wymaganego szkolenia może być twardą przyczyną odrzucenia kandydata.",
          "Wyniki testów szkoleniowych również mogą wpływać na dopuszczenie do grafiku."
        ],
        tip: "Jeżeli generator nie przydziela konkretnej osoby, użyj Diagnostyki obsady w grafiku zamiast zgadywać przyczynę."
      },
      {
        title: "Reguły czasu pracy pracownika",
        bullets: [
          "Minimalna i maksymalna liczba godzin tygodniowo.",
          "Wymagana liczba dni wolnych w tygodniu.",
          "Maksymalna liczba kolejnych dni pracy.",
          "Ograniczenie Tylko weekendy i dni wolne.",
          "Niestandardowa dostępność może czasowo rozszerzyć lub ograniczyć standardowe ustawienia."
        ],
        note: "Reguły są używane podczas generowania i ręcznego przydzielania zmian. Część naruszeń może wymagać świadomego odstępstwa i uzasadnienia, a części nie wolno ominąć."
      },
      {
        title: "Historia pracy pracownika",
        lead: "Zakładka Historia pracy składa zdarzenia RCP w sesje pracy i pozwala zobaczyć faktyczną obecność pracownika.",
        bullets: [
          "Początek i koniec pracy.",
          "Łączny czas pracy.",
          "Przerwy.",
          "Stanowisko i lokalizacja, jeśli zostały zapisane.",
          "Źródło danych RCP oraz kompletność sesji.",
          "Podsumowanie spóźnień z ostatnich 12 miesięcy: liczba spóźnień, suma i średnia minut, największe spóźnienie oraz procent punktualności względem opublikowanego grafiku."
        ]
      },
      {
        title: "Karty zadań i rozwój pracownika",
        bullets: [
          "W kartotece pracownika można tworzyć zadania rozwojowe z terminem wykonania i statusem.",
          "Dla pracownika na umowie o pracę Kadry / HR mogą ustawić roczny budżet szkoleniowy; system pokazuje kwotę wydaną i pozostałą.",
          "Szkolenie może mieć koszt, termin testu oraz termin późniejszej weryfikacji umiejętności.",
          "Po ukończeniu szkolenia bez ręcznie podanego terminu system domyślnie ustawia weryfikację miesiąc później i wysyła przypomnienie osobie prowadzącej."
        ]
      },
      {
        title: "Automatyczna ocena pracownika",
        bullets: [
          "Ocena ogólna jest liczona z ocen umiejętności, stanowisk, ukończonych szkoleń, wyników testów szkoleniowych oraz pochwał i przewinień.",
          "Każda z pięciu kategorii ma osobną wagę definiowaną w Ustawieniach aplikacji.",
          "Dla testów system liczy średnią procentową ze wszystkich zapisanych podejść pracownika i przelicza ją na skalę ocen szkoleń, np. 80% daje 4,00 przy skali 0-5.",
          "Waga konkretnej pochwały lub przewinienia wpływa na wynik kategorii zdarzeń.",
          "System zapisuje historię zmian wyniku i cyklicznie przelicza oceny; Kadry / HR mogą również uruchomić przeliczenie ręcznie.",
          "Generator grafiku nie traktuje tej oceny wyłącznie jako informacji w profilu - przed generowaniem wylicza bieżące wyniki kandydatów i może używać ich jako jednego z kryteriów rankingu."
        ]
      },
      {
        title: "Kaucje i vouchery",
        bullets: [
          "Kadry / HR mogą zapisać pobraną od pracownika kaucję: przedmiot, kwotę jednostkową, ilość, datę pobrania i zwrotu.",
          "System zapisuje użytkownika, który pobrał kaucję oraz użytkownika, który zarejestrował jej zwrot.",
          "Dla voucherów zapisywana jest data, ilość, opis, potwierdzenie zgody / odbioru oraz osoba wydająca.",
          "Z kartoteki pracownika można wygenerować miesięczny raport Excel z osobnymi arkuszami Kaucje i Vouchery dla całej firmy."
        ]
      }
    ]
  },
  {
    id: "users-access",
    title: "Użytkownicy, konta i role",
    icon: "fas fa-user-shield",
    roles: ["ADMIN"],
    summary: "Pracownik jest kartoteką HR, a użytkownik jest kontem logującym się do Qstaff. Te dane są połączone loginem, ale pełnią różne funkcje.",
    links: [
      {path: "/app/users", label: "Użytkownicy aplikacji", roles: ["ADMIN"]},
      {path: "/app/roles", label: "Definicje ról", roles: ["ADMIN"]},
      {path: "/app/employees", label: "Pracownicy", roles: ["ADMIN"]}
    ],
    articles: [
      {
        title: "Pracownik a użytkownik - najważniejsze rozróżnienie",
        lead: "To nie są dwa określenia tego samego rekordu.",
        bullets: [
          "Pracownik przechowuje dane kadrowe, dział, stanowiska, umowy, kwalifikacje, dostępność, nieobecności i dane potrzebne do grafiku.",
          "Użytkownik przechowuje konto logowania: login, hasło, aktywność konta i role dostępu.",
          "Konto pracownika jest łączone z jego kartoteką przez ten sam login.",
          "Nie każdy pracownik musi mieć konto aplikacji. Pracownik bez konta nadal może być planowany w grafiku.",
          "Może też istnieć konto techniczne lub administracyjne bez kartoteki pracownika."
        ],
        warning: "Nie twórz drugiego konta z innym loginem dla tej samej osoby. Powiązanie pracownika z kontem jest oparte na loginie."
      },
      {
        title: "Jak utworzyć konto dla pracownika",
        steps: [
          "Wejdź w Pracownicy i dodaj nową osobę albo otwórz istniejącą kartotekę.",
          "Zaznacz Utwórz użytkownika aplikacji.",
          "Wybierz role konta. Przy nowym koncie pracownika interfejs domyślnie proponuje rolę Pracownik; możesz dodać albo zmienić role zgodnie z faktycznymi obowiązkami tej osoby.",
          "Ustaw hasło i zapisz kartotekę.",
          "Konto pojawi się również w Użytkownicy aplikacji. Od tej chwili role są odczytywane z tego samego konta użytkownika."
        ],
        tip: "Jeśli pracownik już ma konto, w kartotece zobaczysz jego aktualne role i informację, czy konto jest aktywne. Konto wyłączasz w module Użytkownicy aplikacji, nie przez usunięcie pracownika."
      },
      {
        title: "Role, uprawnienia i wyjątki indywidualne",
        lead: "Rola w Qstaff jest domyślnym profilem uprawnień. Administrator może dodatkowo nadać lub odebrać pojedyncze uprawnienie konkretnemu użytkownikowi bez tworzenia nowej roli.",
        bullets: [
          "W Administracja → Definicje ról ustawiasz domyślny zestaw uprawnień dla każdej roli.",
          "Uprawnienia są pogrupowane według funkcji: administracja, pracownicy i HR, grafiki i planowanie, RCP i raporty oraz panel pracownika.",
          "Przy uprawnieniu można określić zakres danych: własne dane, zarządzane działy, wybrane działy albo cała firma.",
          "W Administracja → Użytkownicy aplikacji przypisujesz role oraz ustawiasz wyjątki indywidualne.",
          "Dziedzicz z roli oznacza brak wyjątku. Nadaj indywidualnie dodaje prawo użytkownikowi. Odbierz indywidualnie blokuje prawo nawet wtedy, gdy wynika ono z roli.",
          "Jeżeli użytkownik ma kilka ról, uprawnienia ról są sumowane, a indywidualne odebranie ma pierwszeństwo.",
          "Funkcja Kierownika lub Zastępcy działu automatycznie dołącza profil MANAGER do efektywnych uprawnień powiązanego konta. Nie trzeba zapisywać tej roli ręcznie na użytkowniku.",
          "Frontend ukrywa funkcje bez uprawnienia, ale ostateczna kontrola dostępu zawsze należy do backendu."
        ],
        warning: "Nie twórz kolejnych prawie identycznych ról tylko po to, aby zrobić jeden wyjątek dla jednej osoby. Do tego służą indywidualne uprawnienia użytkownika."
      },
      {
        title: "Użytkownicy aplikacji",
        bullets: [
          "Lista pokazuje login, powiązanego pracownika, dział, stan konta i role przypisane bezpośrednio. Profil MANAGER wynikający z funkcji Kierownika/Zastępcy działu jest wyliczany organizacyjnie i nie wymaga zapisu jako ręczna rola użytkownika.",
          "Administrator może zmienić role, wyłączyć lub ponownie włączyć konto i ustawić nowe hasło.",
          "Login istniejącego konta jest niezmienny, ponieważ jest identyfikatorem powiązania z kartoteką pracownika.",
          "Wyłączenie konta blokuje kolejne logowanie i odświeżenie sesji, ale nie usuwa kartoteki ani historii pracownika.",
          "Konta powiązanego z pracownikiem nie usuwa się. Jeżeli nie powinno mieć dostępu, należy je wyłączyć."
        ]
      },
      {
        title: "Co oznaczają role systemowe",
        bullets: [
          "ADMIN - pełna administracja systemem i konfiguracją.",
          "MANAGER - funkcje kierownika, w szczególności planowanie, grafiki, wnioski, RCP i raporty w dozwolonym zakresie.",
          "HR - kartoteki pracowników, umowy, oceny, rozliczenia kaucji/voucherów i rozwój pracownika.",
          "EMPLOYEE - strefa pracownika, jego grafik, dane i operacje pracownicze.",
          "MINION - strefa pracownika w zakresie własnych danych i samoobsługi: panel pracownika, grafik, wnioski, dostępność, dokumenty, szkolenia, powiadomienia i kontakt z HR.",
          "USER - podstawowa rola ogólna używana przez część starszych funkcji.",
          "GUEST jest rolą systemową zachowaną ze względu na zgodność z obecnymi danymi."
        ],
        warning: "Role systemowe są chronione przed usunięciem i zmianą nazwy, aby zachować spójność istniejących kont. Faktyczny dostęp do funkcji wynika jednak z przypisanych uprawnień, a nie z samej nazwy roli."
      },
      {
        title: "Definicje ról a faktyczne uprawnienia",
        lead: "Rola jest profilem uprawnień. Własna rola zaczyna działać bez zmian w kodzie po przypisaniu jej odpowiednich uprawnień.",
        bullets: [
          "W Definicjach ról tworzysz rolę, nadajesz jej nazwę i wybierasz uprawnienia z macierzy funkcji Qstaff.",
          "Dla uprawnień dotyczących danych możesz ustawić zakres: własne dane, zarządzane działy, wybrane działy albo cała firma.",
          "Systemowe role są chronione przed zmianą nazwy i usunięciem, ale ich profil uprawnień można konfigurować.",
          "Dodatkowej roli nie można usunąć, dopóki jest przypisana do któregoś użytkownika.",
          "Po zapisaniu zmian backend wykorzystuje uprawnienia do kontroli API, a frontend na ich podstawie udostępnia moduły i akcje."
        ]
      },
      {
        title: "Osoby zatwierdzające nieobecności",
        lead: "Lista zatwierdzających nie jest przypadkową listą wszystkich pracowników.",
        bullets: [
          "Na listę trafiają kierownicy i zastępcy zapisani w definicji działu.",
          "Na listę trafiają również pracownicy mający aktywne konto użytkownika z rolą MANAGER albo ADMIN.",
          "Role są sprawdzane na koncie User, czyli w tym samym miejscu, z którego korzysta logowanie i autoryzacja."
        ],
        tip: "Jeżeli kogoś brakuje na liście zatwierdzających, sprawdź najpierw czy jest zapisany jako Kierownik lub Zastępca właściwego działu i czy ma powiązane aktywne konto. Ręczna rola MANAGER jest potrzebna tylko jako wyjątek."
      }
    ]
  },
  {
    id: "contracts",
    title: "Umowy, stawki i koszty",
    icon: "fas fa-file-contract",
    roles: ["ADMIN", "HR", "MANAGER"],
    summary: "Stawki są przypisywane do konkretnego pracownika i stanowiska, mogą zmieniać się w czasie, a centralny kalkulator łączy je z umową, ZUS/KRUS i prowizją firmy zatrudniającej.",
    links: [
      {path: "/app/employees", label: "Pracownicy", roles: ["ADMIN", "HR", "MANAGER"]},
      {path: "/app/contract", label: "Umowy", roles: ["ADMIN", "HR"]},
      {path: "/app/zus-rate", label: "Stawki ZUS", roles: ["ADMIN"]},
      {path: "/app/schedule-reports", label: "Raporty grafiku", roles: ["ADMIN", "MANAGER"]}
    ],
    articles: [
      {
        title: "Dlaczego ważna umowa jest tak ważna dla grafiku",
        lead: "Pracownik może istnieć w kartotece, ale bez ważnej umowy generator może go odrzucić.",
        bullets: [
          "Umowa powinna obejmować termin planowanej pracy.",
          "W umowie określany jest m.in. typ rozliczenia i stawka godzinowa lub miesięczna.",
          "Lista pracowników pokazuje skrócony status aktualnej umowy."
        ],
        warning: "Jeżeli grafik dotyczy np. 20 sierpnia, umowa kończąca się 19 sierpnia nie jest ważna dla tej zmiany."
      },
      {
        title: "Skąd system bierze stawkę pracownika - kolejność źródeł",
        lead: "Koszt grafiku, generator i raporty korzystają z jednego centralnego kalkulatora. Dla konkretnego pracownika, stanowiska i dnia system wybiera pierwszą dostępną stawkę według poniższej kolejności.",
        steps: [
          "Ręczna stawka wpisana dla konkretnej zmiany - ma najwyższy priorytet.",
          "Aktywna stawka okresowa pracownik + stanowisko obowiązująca w dniu zmiany.",
          "Domyślna stawka przypisana pracownikowi na danym stanowisku.",
          "Stawka godzinowa z aktywnej umowy rozliczanej godzinowo.",
          "Dla umowy miesięcznej: kwota miesięczna podzielona przez normę godzin danego miesiąca skorygowaną o wymiar etatu z umowy o pracę.",
          "Jeżeli żadne wcześniejsze źródło nie dostarczy stawki, używana jest stawka podstawowa stanowiska."
        ],
        note: "Dane historyczne mogą zawierać stawkę godzinową zapisaną w starszej umowie o innej formie rozliczenia. System potrafi użyć jej jako mechanizmu zgodności wstecznej i sygnalizuje to w kalkulacji.",
        warning: "Jeżeli dla tej samej osoby i stanowiska nakłada się więcej niż jedna aktywna stawka okresowa, kalkulator nie wybiera jednej losowo - koszt zostaje oznaczony jako niepełny."
      },
      {
        title: "Stawki okresowe pracownik × stanowisko",
        lead: "Ta funkcja pozwala zachować historię zmian wynagrodzenia bez nadpisywania stawki, która obowiązywała wcześniej.",
        steps: [
          "Otwórz kartotekę pracownika, przejdź do Dane firmowe → Stanowiska i przy wybranym stanowisku kliknij Stawki i koszt.",
          "Podaj datę Od, opcjonalną datę Do oraz wybierz formę Godzinowa albo Miesięczna.",
          "Dla formy godzinowej podaj brutto/h i opcjonalnie netto/h. Dla formy miesięcznej podaj brutto/mies. i opcjonalnie netto/mies.",
          "Aktywne okresy dla tego samego pracownika i stanowiska nie mogą się nakładać. Starsze rekordy bez pola formy są traktowane jako godzinowe.",
          "W prawej części okna wybierz dzień i użyj Przelicz. Dla stawki miesięcznej system użyje normy czasu pracy miesiąca i pokaże efektywną stawkę godzinową oraz pełny koszt pracodawcy."
        ],
        tip: "Jeżeli pracownik wykonuje dwie różne prace z różnymi stawkami, nie twórz jednej uśrednionej stawki. Ustaw właściwe stawki dla odpowiednich stanowisk — raport rozliczy później godziny osobno."
      },
      {
        title: "Podsumowanie kosztów w kartotece pracownika",
        lead: "Na początku sekcji Stanowiska znajduje się zbiorczy podgląd kosztu wszystkich zapisanych aktywnych stanowisk pracownika na wybrany dzień.",
        bullets: [
          "Dla każdego stanowiska widzisz efektywną stawkę brutto i netto na godzinę, źródło stawki, profil ZUS/KRUS/uczeń-student, składki pracodawcy, prowizję agencji oraz całkowity koszt jednej roboczogodziny.",
          "Status Kompletny oznacza, że centralny kalkulator miał dane potrzebne do policzenia pełnego kosztu pracodawcy. Status Niepełny wymaga sprawdzenia konfiguracji wskazanej w oknie Stawki i koszt.",
          "Data podglądu ma znaczenie: może wybrać inną stawkę okresową, inną umowę, inny profil ucznia/studenta lub inną roczną konfigurację ZUS.",
          "Podsumowanie korzysta z danych zapisanych w bazie. Po zmianie stawki bazowej w kartotece najpierw zapisz pracownika, a następnie użyj Przelicz."
        ],
        note: "Kierownik może używać podglądu kosztu przy planowaniu. Zmiana stawek okresowych jest dostępna dla HR i administratora."
      },
      {
        title: "Stawka miesięczna i koszt jednej godziny",
        bullets: [
          "Stawka miesięczna może pochodzić z umowy, stawki przypisanej pracownikowi na stanowisku albo stawki okresowej pracownik × stanowisko.",
          "Kwota miesięczna jest dzielona przez normę godzin miesiąca obliczaną z dni poniedziałek–piątek z wyłączeniem skonfigurowanych dni wolnych.",
          "Dla umowy o pracę pełnoetatowa norma miesiąca jest dodatkowo mnożona przez wymiar etatu zapisany na umowie, np. 50% zmniejsza normę do połowy.",
          "W kalkulacji widoczna jest norma miesiąca użyta do przeliczenia. Efektywna stawka godzinowa może więc różnić się między miesiącami przy tej samej kwocie miesięcznej.",
          "Techniczna stawka godzinowa służy do planowania kosztu grafiku i raportów; nie zmienia miesięcznej formy rozliczenia zapisanej w źródle."
        ]
      },
      {
        title: "Snapshot kosztu na zmianie - dlaczego historia się nie przelicza",
        lead: "W chwili utworzenia lub przeliczenia przypisania do zmiany Qstaff zapisuje na zmianie komplet danych kosztowych obowiązujących w tym momencie.",
        bullets: [
          "Zapisywana jest stawka bazowa oraz jej źródło.",
          "Zapisywany jest procent i koszt składek pracodawcy.",
          "Zapisywana jest prowizja firmy/agencji zatrudniającej.",
          "Zapisywany jest profil ubezpieczeniowy, użyta podstawa i stawka ZUS, firma zatrudniająca oraz dokument umowy.",
          "Dla umowy miesięcznej zapisywana jest również norma miesiąca użyta do przeliczenia.",
          "Raport historycznego grafiku korzysta z tego snapshotu zamiast bezwarunkowo przeliczać starą zmianę według dzisiejszych stawek."
        ],
        tip: "Dzięki temu podwyżka albo zmiana prowizji agencji nie powinna zmieniać kosztu już zaplanowanej historycznej zmiany. Przy świadomej edycji przypisania koszt jest przeliczany ponownie."
      },
      {
        title: "ZUS, uczeń/student, KRUS i firma zatrudniająca",
        bullets: [
          "Koszt pracodawcy nie jest tym samym co stawka wypłacana pracownikowi.",
          "Status ucznia i studenta ma własny profil kosztowy.",
          "KRUS jest rozliczany oddzielnie.",
          "Dla zwykłego ZUS używane są skonfigurowane składki i podstawy.",
          "Jeżeli pracownik jest zatrudniony przez agencję lub inną firmę, do kosztu może dojść prowizja."
        ],
        warning: "Brak konfiguracji kosztów może spowodować oznaczenie kosztu jako niepełnego. Nie należy wtedy traktować wyliczenia jako pełnego kosztu pracodawcy."
      },
      {
        title: "Wzorce i numeracja umów",
        bullets: [
          "Szablony treści umów przechowują treść dokumentu i pola dynamiczne, np. numer umowy, dane pracownika lub wykonawcy, dane firmy, daty i stawki.",
          "W szablonie można wstawiać także miejscowość i datę zawarcia umowy, reprezentanta firmy, stanowisko, miejsce wykonywania pracy, wymiar czasu pracy, warunki zatrudnienia oraz datę rozpoczęcia pracy. Wartości te są pobierane z zapisanej umowy, a tam gdzie ma to sens system może użyć danych firmy lub pracownika jako wartości domyślnej.",
          "Dla stawek godzinowych i miesięcznych dostępne są pola kończące się na _WORDS oraz funkcja {{SLOWNIE(NAZWA_POLA)}}. Dla wynagrodzeń funkcja zapisuje wartość po polsku łącznie ze złotymi i groszami, a dla pozostałych obsługiwanych pól liczbowych zapisuje liczbę słownie. Jeżeli użyjesz NET_MONTHLY_RATE lub NET_HOURLY_RATE, a wartość netto nie została zapisana w umowie, funkcja SLOWNIE użyje odpowiadającej wartości brutto zamiast pozostawić puste miejsce.",
          "Edytor szablonu obsługuje pogrubienie, kursywę, podkreślenie, rozmiar tekstu, wyrównanie oraz wstawianie tabel o wskazanej liczbie wierszy i kolumn. Formatowanie i tabele są zachowywane zarówno w podglądzie dokumentu, jak i w wygenerowanym pliku DOCX.",
          "Z poziomu zapisanej umowy można najpierw podejrzeć treść po podstawieniu danych, a następnie wygenerować plik DOCX na podstawie wskazanego szablonu albo aktywnego szablonu przypisanego do typu umowy.",
          "Wzorzec numeracji jest niezależny od szablonu treści. Określa format numeru dla konkretnego typu umowy i roku. Numer końcowy nadaje system dopiero podczas zapisu umowy.",
          "Numeracja może resetować się co miesiąc albo być ciągła przez cały rok. Użyty wzorzec numeracji jest blokowany, a licznik nie cofa się po usunięciu umowy."
        ]
      }
    ]
  },
  {
    id: "organization",
    title: "Działy, stanowiska i konfiguracja",
    icon: "fas fa-sitemap",
    roles: ["ADMIN"],
    summary: "Struktura organizacyjna jest podstawą planów zapotrzebowania, grafików, uprawnień kierowników i raportów.",
    links: [
      {path: "/app/department", label: "Działy", roles: ["ADMIN"]},
      {path: "/app/position", label: "Stanowiska", roles: ["ADMIN"]},
      {path: "/app/locations", label: "Lokalizacje", roles: ["ADMIN"]},
      {path: "/app/non-working-days", label: "Dni wolne od pracy", roles: ["ADMIN"]},
      {path: "/app/working-time", label: "Wymiary czasu pracy", roles: ["ADMIN"]},
      {path: "/app/settings", label: "Ustawienia globalne", roles: ["ADMIN"]}
    ],
    articles: [
      {
        title: "Dział",
        bullets: [
          "Grafik jest tworzony dla konkretnego działu.",
          "Plan zapotrzebowania i stanowiska muszą być zgodne z działem.",
          "Kierownik i zastępca są funkcjami organizacyjnymi działu. Powiązane konto otrzymuje automatycznie standardowy profil uprawnień MANAGER w zakresie zarządzanych działów.",
          "Kierownik lub zastępca musi zachować w tym dziale aktywne stanowisko Funkcyjne albo Kierownicze. Qstaff blokuje usunięcie ostatniego takiego przypisania, zmianę stanowiska na zwykłe oraz archiwizację osoby pełniącej funkcję do czasu wskazania zastępstwa.",
          "Dział może mieć ustawiony limit jednoczesnych urlopów oraz akcję po jego przekroczeniu: ostrzeżenie albo blokadę zatwierdzenia.",
          "W dziale można ustawić domyślną kolejność kryteriów generatora, domyślne cele %COL/SPMH, budżet roboczogodzin oraz minimalną i maksymalną obsadę.",
          "Dział może nadpisywać globalne reguły planowania: liczbę dni wolnych, maksymalną liczbę kolejnych dni pracy, próg ostrzeżenia oraz wyprzedzenie próśb o wolne i urlop. Ustawienie pracownika ma pierwszeństwo przed ustawieniem działu.",
          "Dla każdego typu okresu można wskazać domyślny plan zapotrzebowania, który będzie automatycznie podpowiadany przy tworzeniu grafiku."
        ]
      },
      {
        title: "Stanowisko",
        bullets: [
          "Stanowisko określa rodzaj pracy wykonywanej w grafiku.",
          "Pracownik może posiadać kilka stanowisk.",
          "Do stanowiska mogą być przypisane wymagania kwalifikacyjne, np. szkolenia i certyfikaty.",
          "Stanowisko może posiadać własną stawkę podstawową.",
          "Każde stanowisko ma poziom organizacyjny: Zwykłe, Funkcyjne albo Kierownicze."
        ],
        note: "Poziom stanowiska służy do organizacji, filtrowania i raportowania. Sam nie nadaje dostępu do programu. Wyjątkiem organizacyjnym jest przypisanie pracownika jako Kierownika lub Zastępcy działu: wtedy powiązane konto automatycznie otrzymuje standardowy profil kierowniczy dla tego działu."
      },
      {
        title: "Stanowisko a lokalizacja",
        lead: "Stanowisko opisuje rodzaj wykonywanej pracy, a lokalizacja opisuje miejsce, w którym ta praca jest wykonywana w danym czasie. To dwa niezależne wymiary.",
        bullets: [
          "Jedno stanowisko może występować w wielu lokalizacjach. Przykład: Obsługa Atrakcji może pracować rano przy atrakcji A, a później przy atrakcji B.",
          "W jednej lokalizacji może pracować wiele stanowisk, np. Obsługa Atrakcji, Technik i Kierownik.",
          "Nazwa może być taka sama po obu stronach. Słodka Chmurka może być nazwą stanowiska oraz osobno nazwą fizycznej lokalizacji Słodka Chmurka.",
          "Stanowisko może mieć opcjonalną Domyślną lokalizację. Jest to tylko podpowiedź przy tworzeniu zmiany, a nie stałe ograniczenie stanowiska do jednego miejsca.",
          "Lokalizację zapisuje się na konkretnym odcinku zmiany. Jeżeli pracownik zmienia miejsce w ciągu dnia, zaplanuj kolejne, nienakładające się odcinki pracy z odpowiednimi lokalizacjami."
        ],
        example: "Przykład: 10:00–14:00 Obsługa Atrakcji / Labirynt Luster, 14:00–18:00 Obsługa Atrakcji / Latająca Chata. Stanowisko pozostaje takie samo, zmienia się tylko lokalizacja."
      },
      {
        title: "Poziom stanowiska - Zwykłe, Funkcyjne i Kierownicze",
        lead: "To oznaczenie mówi, jaką rolę organizacyjną pełni stanowisko w firmie. Nie należy go mylić z rolami użytkownika w Qstaff.",
        bullets: [
          "Zwykłe - standardowe stanowisko wykonawcze, np. kasjer, pomoc kuchenna lub pracownik atrakcji.",
          "Funkcyjne - stanowisko z dodatkową odpowiedzialnością operacyjną, np. lider zmiany lub koordynator.",
          "Kierownicze - stanowisko kierownicze w strukturze organizacyjnej.",
          "Poziomu stanowiska można używać do filtrowania list, oznaczeń na grafiku i raportów oraz do przyszłych reguł organizacyjnych.",
          "Pracownik może mieć stanowisko Funkcyjne lub Kierownicze bez dostępu kierowniczego. Dostęp powstaje wtedy, gdy zostanie dodatkowo wskazany jako Kierownik lub Zastępca konkretnego działu.",
          "Rola MANAGER może nadal zostać nadana ręcznie jako wyjątek, ale podstawowy model nie wymaga jej ręcznego przypisywania osobom pełniącym funkcję Kierownika lub Zastępcy."
        ],
        warning: "Nie ustawiaj poziomu Kierownicze tylko po to, aby nadać dostęp do programu. Pracownik musi zostać wskazany jako Kierownik lub Zastępca działu. Qstaff pilnuje wtedy, aby miał w tym samym dziale aktywne stanowisko Funkcyjne lub Kierownicze."
      },
      {
        title: "Słowniki - po co ich tyle",
        lead: "Słowniki pozwalają administratorowi zmieniać dane używane w formularzach bez modyfikowania kodu programu.",
        bullets: [
          "Typy nieobecności, szkoleń, certyfikatów i badań.",
          "Etapy zatrudnienia - dla każdego etapu można określić, czy zapis pracownika wymaga działu oraz czy wymaga również stanowiska. Stanowisko może być wymagane tylko razem z działem; dział może być wymagany samodzielnie.",
          "Pochwały i przewinienia.",
          "Typy umów i stawek.",
          "Dni wolne od pracy i wymiary czasu pracy.",
          "Dane urzędowe, np. kraje, województwa, urzędy skarbowe, NFZ i konfiguracja ZUS."
        ],
        tip: "Etap typu Poczekalnia może nie wymagać ani działu, ani stanowiska. Przy dodawaniu pracownika po wskazaniu działu lista stanowisk jest ograniczana do stanowisk z tego działu."
      },
      {
        title: "Dni wolne od pracy - kalendarz firmy",
        lead: "W słowniku Dni wolne od pracy administrator wskazuje konkretne daty, które nie są standardowymi dniami roboczymi. Sobót i niedziel nie trzeba dodawać - system rozpoznaje je automatycznie.",
        steps: [
          "Otwórz Administracja → Słowniki → Dni wolne od pracy i wybierz rok.",
          "Dodaj datę, nazwę oraz opcjonalny skrót i opis. Ta sama nazwa święta może występować w kolejnych latach, ale jednej daty nie można dodać dwa razy.",
          "Po zapisaniu data jest przekazywana do kalendarza grafiku i Grafiku dostępności oraz do mechanizmu wyliczania norm czasu pracy.",
          "Pracownik oznaczony jako Tylko weekendy i dni wolne może być kwalifikowany na taki dzień, a pracownik Tylko dni robocze jest wtedy traktowany jak niedostępny zgodnie ze swoją regułą.",
          "Przy umowie miesięcznej dzień wolny zmniejsza miesięczną normę godzin używaną do przeliczenia kosztu jednej roboczogodziny."
        ],
        note: "Dzień wolny nie blokuje ręcznego zaplanowania pracy. Qstaff traktuje go jako dzień niestandardowo wolny przy normach i regułach dostępności, ponieważ Park może działać również w święta.",
        warning: "Dni wolne są definiowane jako konkretne daty. Przy kolejnym roku trzeba wprowadzić właściwe daty dla tego roku."
      },
      {
        title: "Wymiary czasu pracy - 100%, 50% i inne",
        lead: "Słownik Wymiary czasu pracy opisuje procent etatu używany w umowach o pracę. 100% odpowiada bazowej normie 8 h na standardowy dzień roboczy, 50% - 4 h, 75% - 6 h itd.",
        steps: [
          "Otwórz Administracja → Słowniki → Wymiary czasu pracy.",
          "Dodaj nazwę, skrót i procent etatu, np. Pełny etat / 1/1 / 100% albo Pół etatu / 1/2 / 50%.",
          "W formularzu umowy o pracę wybierz Wymiar czasu pracy. Dla nowych i edytowanych umów o polityce EMPLOYMENT jest to pole wymagane.",
          "System mnoży pełnoetatową normę miesiąca przez procent etatu. Przykładowo norma 168 h dla 100% daje 84 h dla 50%.",
          "Tak wyliczona norma jest używana w raportach czasu pracy oraz przy przeliczaniu wynagrodzenia miesięcznego na efektywną stawkę godzinową i koszt grafiku."
        ],
        note: "Stare umowy o pracę bez przypisanego wymiaru pozostają zgodne wstecznie i są liczone jak 100% etatu. Przy ich edycji należy już wskazać właściwy wymiar."
      }
    ]
  },
  {
    id: "availability",
    title: "Dostępność, nieobecności i sezonowość",
    icon: "fas fa-calendar-check",
    roles: ["ADMIN", "MANAGER"],
    summary: "Informacja kiedy pracownik może lub nie może pracować oraz jak zmieniać dostępność grupowo w sezonie.",
    links: [{path: "/app/employee-availability-schedule", label: "Grafik dostępności", roles: ["ADMIN", "MANAGER"]}],
    articles: [
      {
        title: "Jak czytać Grafik dostępności",
        bullets: [
          "Dostępny - pracownik może pracować w tym terminie.",
          "Preferowany - pracownik może pracować i system może premiować tę dostępność przy generowaniu.",
          "Niedostępny - pracownika nie należy przydzielać.",
          "Nieobecność - np. urlop, L4 lub inny typ absencji.",
          "Zaplanowana zmiana - pracownik jest już użyty w grafiku."
        ],
        note: "W kalendarzu są też podsumowania godzin oraz ostrzeżenia związane z regułami czasu pracy."
      },
      {
        title: "Dodawanie dostępności z kalendarza",
        steps: [
          "Wybierz dział i okres.",
          "Kliknij komórkę pracownik × dzień.",
          "Wybierz dodanie dostępności/niedostępności albo nieobecności.",
          "W razie potrzeby podaj godziny obowiązywania wpisu.",
          "Zapisz. Wpis będzie brany pod uwagę przez generator grafiku."
        ]
      },
      {
        title: "Sterowanie sezonowością – do czego służy",
        lead: "Sterowanie sezonowością pozwala jednym działaniem nadać wielu pracownikom pełną dostępność na określony czas. Jest przeznaczone przede wszystkim do przechodzenia między trybem poza sezonem i sezonem, bez ręcznej edycji kartoteki każdej osoby.",
        bullets: [
          "Pracownik może mieć bazowe ograniczenie: tylko weekendy, tylko dni robocze albo pełną dostępność.",
          "Zmiana sezonowa nie usuwa ani nie przestawia bazowego ograniczenia pracownika. Dodaje tymczasową pełną dostępność obowiązującą tylko od wskazanej daty początkowej do końcowej.",
          "Dzięki temu pracownik oznaczony na co dzień jako tylko weekendowy może być planowany również w dni robocze w czasie sezonu letniego.",
          "Po zakończeniu okresu sezonowego wpis przestaje działać automatycznie i ponownie obowiązują zwykłe ograniczenia pracownika.",
          "Zmiany sezonowe są tworzone jako pakiety. Jeden pakiet może objąć wielu pracowników tego samego działu i później można go usunąć w całości jednym działaniem.",
          "W kartotece pracownika sezonowa dostępność jest widoczna jako wpis tylko do odczytu. Kierownik i administrator mają w tej sekcji przycisk Sterowanie sezonowością, który otwiera centralny panel i wskazuje tego pracownika."
        ],
        example: "Przykład: pracownik ma ustawione TYLKO WEEKENDY. Dla pakietu „Sezon letni 2026” ustawiono 01.07.2026–31.08.2026. W lipcu i sierpniu może być planowany także od poniedziałku do piątku. Od 01.09.2026 system ponownie respektuje jego bazowe ograniczenie tylko do weekendów."
      },
      {
        title: "Sterowanie sezonowością – krok po kroku",
        steps: [
          "Otwórz Grafik dostępności i kliknij Sterowanie sezonowością albo użyj przycisku Sterowanie sezonowością w kartotece konkretnego pracownika.",
          "Wybierz dział. Administrator może pracować na wszystkich działach, a kierownik tylko na dziale, którego jest kierownikiem lub zastępcą.",
          "Wybierz grupę bazową: Wszyscy z ograniczeniem, Tylko weekendowi, Tylko dni robocze albo Wszyscy pracownicy działu.",
          "Ustaw datę Od i Do. Jeden pakiet może obejmować maksymalnie 730 dni.",
          "Opcjonalnie wpisz nazwę sezonu, np. Sezon letni 2026. Bez nazwy system utworzy nazwę na podstawie zakresu dat.",
          "Zaznacz wybranych pracowników albo użyj Zaznacz wszystkich.",
          "Kliknij Włącz pełną dostępność. System utworzy jeden pakiet sezonowy dla zaznaczonych osób.",
          "W prawej części okna możesz sprawdzić aktywne i historyczne pakiety wraz z okresem oraz listą objętych pracowników.",
          "Jeżeli chcesz wcześniej wycofać zmianę, użyj Usuń przy właściwym pakiecie. Usunięcie pakietu przywraca działanie bazowych zasad dostępności."
        ],
        note: "System nie pozwoli utworzyć nakładającego się okresu sezonowego dla pracownika, który ma już sezonową pełną dostępność w tym samym czasie."
      },
      {
        title: "Co ma pierwszeństwo i jak wpływa to na grafik",
        lead: "Sezonowość jest świadomym wyjątkiem od ograniczenia tylko weekendy / tylko dni robocze, ale nie jest pozwoleniem na ignorowanie pozostałych reguł planowania.",
        bullets: [
          "Ręcznie wpisana NIEDOSTĘPNOŚĆ ma pierwszeństwo przed sezonową pełną dostępnością.",
          "Nieobecność, np. urlop lub L4, nadal blokuje pracownika.",
          "Sezonowa dostępność nie przydziela pracownika automatycznie do zmiany. Informuje generator, że w tym okresie bazowe ograniczenie dni tygodnia nie powinno go wykluczać.",
          "Pozostałe warunki generatora nadal obowiązują, np. brak nakładania zmian, wymagane szkolenia i certyfikaty oraz reguły czasu pracy.",
          "Zwykły wpis DOSTĘPNY lub PREFEROWANY nie służy do blokowania sezonowości. Jeżeli trzeba wyłączyć konkretny dzień albo godziny w czasie sezonu, użyj wpisu NIEDOSTĘPNY albo właściwej nieobecności.",
          "Pakiet jest przypisany do konkretnego działu i może obejmować tylko aktywnych pracowników dostępnych do przypisania do tego działu."
        ],
        warning: "Nie używaj sezonowości do wpisywania urlopów, L4 ani pojedynczych wyjątków godzinowych. Do tego służą nieobecności i ręczne wpisy dostępności/niedostępności w kalendarzu."
      }
    ]
  },
  {
    id: "resource-plans",
    title: "Plany zapotrzebowania",
    icon: "fas fa-clipboard-list",
    roles: ["ADMIN", "MANAGER"],
    summary: "Plan określa ilu ludzi, na jakich stanowiskach i w jakich godzinach firma potrzebuje przed generowaniem grafiku.",
    links: [{path: "/app/resource-plan", label: "Plany zapotrzebowania", roles: ["ADMIN", "MANAGER"]}],
    articles: [
      {
        title: "Czym jest plan zapotrzebowania",
        lead: "Plan zapotrzebowania nie przydziela nazwisk. Jest szablonem mówiącym generatorowi, jakiej obsady potrzebujesz.",
        bullets: [
          "Dział, którego dotyczy zapotrzebowanie.",
          "Zakres i typ planu.",
          "Dni oraz godziny pracy.",
          "Stanowiska lub pule stanowisk.",
          "Wymagana liczba pracowników."
        ]
      },
      {
        title: "Typy planów i kopiowanie dnia",
        bullets: [
          "Plan może odpowiadać różnym sposobom planowania okresu, np. tygodniowemu lub miesięcznemu.",
          "Jeżeli wiele dni ma identyczne zapotrzebowanie, można przygotować dzień wzorcowy i skopiować go na pozostałe dni planu.",
          "Każda pozycja planu wymagająca godzin musi mieć poprawnie wpisane godziny rozpoczęcia i zakończenia."
        ]
      },
      {
        title: "Pula stanowisk",
        lead: "Jedna pozycja zapotrzebowania może pozwalać generatorowi dobrać osobę z kilku wskazanych stanowisk.",
        bullets: [
          "Jeżeli zaznaczysz konkretne stanowiska, kandydat musi należeć do jednej z tych grup.",
          "W przypadku pozycji bez stanowiska system może obsługiwać zapotrzebowanie ogólne zgodnie z regułami generatora.",
          "Stanowiska funkcyjne nie powinny być przypadkowo używane do wypełniania zwykłych braków ogólnych."
        ]
      },
      {
        title: "Plan jest zapisany, ale grafik nadal jest pusty",
        warning: "Sam plan nie gwarantuje przydziału. Generator musi jeszcze znaleźć osoby spełniające wymagania stanowiska, umowy, dostępności, kwalifikacji i czasu pracy.",
        tip: "Jeżeli wszystkie pozycje stały się otwartymi zmianami, przejdź do grafiku i sprawdź Diagnostykę obsady dla jednej konkretnej pozycji."
      }
    ]
  },
  {
    id: "schedules",
    title: "Grafiki pracy",
    icon: "fas fa-calendar-alt",
    roles: ["ADMIN", "MANAGER", "EMPLOYEE"],
    summary: "Tworzenie, generowanie, ręczna korekta, publikacja, druk, eksport i późniejsza kontrola grafiku.",
    links: [
      {path: "/app/scheduler", label: "Grafik", roles: ["ADMIN", "MANAGER"]},
      {path: "/app/my-schedule", label: "Mój grafik", roles: ["EMPLOYEE"]}
    ],
    articles: [
      {
        title: "Tworzenie nowego grafiku",
        steps: [
          "Kliknij utworzenie nowego grafiku.",
          "Nadaj nazwę i wybierz dział.",
          "Wybierz typ okresu: dzienny, weekendowy, tygodniowy, dwutygodniowy, miesięczny albo własny zakres.",
          "Nowy grafik jest Roboczy. Na tym etapie można go wielokrotnie zmieniać."
        ]
      },
      {
        title: "Generowanie obsady",
        steps: [
          "Wybierz plan zapotrzebowania pasujący do działu i okresu grafiku.",
          "Ustaw kolejność kryteriów przydziału, jeżeli chcesz zmienić priorytety.",
          "Uruchom generowanie całego okresu albo wybranego zakresu.",
          "Przejrzyj podsumowanie: przydzieleni pracownicy, braki i otwarte zmiany.",
          "Przy interesującej pozycji rozwiń Ranking kandydatów, aby sprawdzić, dlaczego wybrano konkretną osobę."
        ],
        note: "Domyślna kolejność kryteriów jest ustawieniem działu. W pojedynczym uruchomieniu generatora można ją tymczasowo zmienić; przycisk Przywróć domyślne odtwarza kolejność zapisaną dla działu. Rekomendacja generatora nie blokuje późniejszej ręcznej zmiany obsady."
      },
      {
        title: "Kolejność kryteriów - koszt, ocena, dni i preferencja",
        lead: "Generator nie używa jednego sztywnego trybu. Kryteria są stosowane po kolei od góry, podobnie jak wielopoziomowe sortowanie.",
        bullets: [
          "Najniższy koszt - porównuje pełny koszt pracodawcy z centralnego kalkulatora kosztów dla pracownika, stanowiska i dnia zmiany.",
          "Najwyższa ocena - porównuje bieżącą ogólną ocenę pracownika, a nie tylko ocenę na jednym stanowisku.",
          "Najmniej dni pracy - preferuje osobę z mniejszą liczbą już zaplanowanych dni w tygodniu obejmującym zmianę.",
          "Preferowana dostępność - premiuje osobę, która oznaczyła cały zakres zmiany jako preferowany, a nie jedynie dostępny.",
          "Możesz ustawić np. koszt → ocena → dni → preferowana dostępność albo dowolną inną kolejność tych czterech kryteriów."
        ],
        example: "Jeżeli koszt jest pierwszy, tańszy kwalifikujący się kandydat wygrywa nawet wtedy, gdy drugi ma wyższą ocenę. Ocena rozstrzyga dopiero wtedy, gdy wcześniejsze kryterium nie rozróżni kandydatów."
      },
      {
        title: "Jakiej oceny używa generator",
        bullets: [
          "Przed rozpoczęciem generowania system wylicza aktualną ocenę ogólną dla dostępnych pracowników.",
          "Ocena uwzględnia kategorie i wagi skonfigurowane dla ocen pracownika: umiejętności miękkie, stanowiska, szkolenia, testy szkoleniowe oraz zdarzenia krytyczne.",
          "Nie trzeba czekać na cykliczne zadanie przeliczające zapisany wynik profilu, aby generator uwzględnił bieżące dane.",
          "Ocena na konkretnym stanowisku nadal istnieje i jest używana jako dodatkowy tie-breaker, ale nie zastępuje oceny ogólnej w kryterium Najwyższa ocena."
        ]
      },
      {
        title: "Ranking kandydatów i tie-breakery",
        lead: "Po generowaniu można rozwinąć ranking kwalifikujących się osób dla każdej pozycji zapotrzebowania.",
        bullets: [
          "Ranking pokazuje miejsce, pracownika i stanowisko użyte do kwalifikacji.",
          "Pokazuje pełny koszt na godzinę oraz informację, czy koszt jest kompletny.",
          "Pokazuje ocenę ogólną i ocenę na konkretnym stanowisku.",
          "Pokazuje liczbę zaplanowanych dni i godzin w tygodniu oraz preferowaną dostępność.",
          "Wynik PRZYDZIELONY oznacza osoby wybrane do wymaganej obsady; pozostałe są pokazane jako rezerwowe.",
          "Jeżeli po wszystkich ustawionych kryteriach nadal jest remis, generator kolejno bierze pod uwagę deficyt do minimalnych godzin tygodniowych, mniejszą liczbę zaplanowanych godzin, wyższą ocenę stanowiskową i na końcu nazwisko."
        ],
        tip: "Ranking służy do audytu decyzji generatora. Jeżeli oczekiwany pracownik jest niżej, porównaj kolumny od lewej zgodnie z ustawioną kolejnością kryteriów."
      },
      {
        title: "Dlaczego system odrzuca pracownika",
        bullets: [
          "Brak ważnej umowy.",
          "Nieprawidłowy etap zatrudnienia albo pracownik nieaktywny/niechciany.",
          "Brak wymaganego stanowiska.",
          "Niedostępność lub nieobecność.",
          "Kolizja z inną zmianą.",
          "Brak wymaganego szkolenia, certyfikatu lub negatywny wynik reguł szkoleniowych.",
          "Naruszenie twardych zasad czasu pracy."
        ],
        tip: "Otwórz pozycję i przeczytaj sekcję Diagnostyka obsady. Pokazuje powody odrzucenia kandydatów."
      },
      {
        title: "Kalendarz roboczy grafiku",
        lead: "Kalendarz jest głównym miejscem ręcznej pracy nad grafikiem. Pracownicy są w wierszach, a kolejne dni w kolumnach, dzięki czemu można poprawiać grafik bez przechodzenia do osobnej kartoteki pracownika.",
        steps: [
          "Otwórz Grafik i wybierz grafik z listy.",
          "W sekcji Kalendarz roboczy wybierz widok: dzień, tydzień, 2 tygodnie, miesiąc albo dowolny zakres. Domyślny jest tydzień.",
          "Kliknij komórkę pracownik × dzień, aby dodać zmianę, dostępność, niedostępność albo nieobecność.",
          "Kliknij istniejącą zmianę w komórce, aby od razu zmienić pracownika, stanowisko, lokalizację, godziny, stawkę lub status. Z tego samego okna można również usunąć zmianę.",
          "Po zapisie kalendarz odświeża obsadę, godziny, dostępności, nieobecności i porównanie z RCP."
        ],
        tip: "Przełącznik Ukryj pracowników bez zmian działa dla aktualnie widocznego okresu i jest zapamiętywany w przeglądarce. Dzięki temu po odświeżeniu strony nie trzeba ustawiać go ponownie."
      },
      {
        title: "Godziny, normy tygodniowe i alerty w kalendarzu",
        bullets: [
          "W kolumnie Godziny widzisz plan dla aktualnie wyświetlanego zakresu, sumę godzin tygodniowych i miesięcznych oraz analogiczne dane z RCP.",
          "Jeżeli pracownik ma ustawione minimalne lub maksymalne godziny tygodniowe, kalendarz pokazuje jego normę obok podsumowania godzin.",
          "Przy zbliżeniu się do 90% maksymalnej liczby godzin tygodniowych pojawia się ostrzeżenie. Po przekroczeniu limitu podsumowanie godzin jest oznaczone krytycznie.",
          "Kalendarz pokazuje też, ile godzin brakuje do minimalnej wartości w bieżącym tygodniu.",
          "W każdej komórce z pracą widoczna jest liczba godzin danego dnia. System pokazuje również ostrzeżenie przy 5 kolejnych dniach pracy i oznaczenie krytyczne przy osiągnięciu maksymalnej liczby kolejnych dni skonfigurowanej dla pracownika lub systemu."
        ],
        note: "Godziny tygodniowe i miesięczne uwzględniają aktywne zmiany pracownika także z innych grafików. Dzięki temu ręczna korekta jednego działu nie ukrywa obciążenia wynikającego z pracy w innym grafiku."
      },
      {
        title: "Plan vs wykonanie bezpośrednio w kalendarzu",
        bullets: [
          "Przy zmianie mogą być widoczne rzeczywiste godziny rozpoczęcia i zakończenia z RCP, przepracowany czas oraz przerwy.",
          "Spóźnienie, wcześniejsze wyjście, brak rozpoczęcia RCP i ryzyko braku obsady są oznaczane bezpośrednio przy zmianie.",
          "Stopka komórki pokazuje plan dnia i wykonanie RCP, a podsumowanie działu agreguje plan, wykonanie i alerty dla każdego dnia.",
          "Sekcja Plan vs wykonanie RCP pod kalendarzem pokazuje szczegóły każdej zmiany w aktualnie widocznym zakresie.",
          "Przycisk Reaguj przy problemie RCP otwiera dzień pracownika, aby można było oznaczyć nieobecność albo skorygować obsadę."
        ]
      },
      {
        title: "Ręczne poprawianie grafiku",
        bullets: [
          "Możesz kliknąć konkretną zmianę i zmienić pracownika, stanowisko, lokalizację lub godziny.",
          "Z poziomu kalendarza można dodać nową zmianę albo usunąć istniejącą.",
          "Z tego samego miejsca można dodać dostępność, niedostępność lub nieobecność pracownika, w tym nieobecność nieusprawiedliwioną.",
          "System nadal sprawdza kwalifikację pracownika, dostępność, kolizje i reguły czasu pracy."
        ],
        warning: "Ręczna zmiana nie wyłącza walidacji. Twardych blokad, takich jak kolizja zmian lub brak ważnej umowy, nie można ominąć. Reguły dopuszczające świadome odstępstwo wymagają uzasadnienia."
      },
      {
        title: "Otwarta zmiana / brak obsady",
        lead: "Jeżeli generator nie znalazł osoby, pozycja może zostać jako otwarta zmiana.",
        bullets: [
          "Kierownik widzi ją jako brak obsady.",
          "Może obsadzić ją ręcznie.",
          "Pracownicy mogą zgłaszać chęć przejęcia otwartej zmiany, a kierownik podejmuje decyzję."
        ]
      },
      {
        title: "Publikacja, zmiany i archiwizacja",
        bullets: [
          "Roboczy - grafik można normalnie przygotowywać i poprawiać.",
          "Opublikowany - jest oficjalnie dostępny dla pracowników.",
          "Późniejsze zmiany w opublikowanym grafiku mogą generować powiadomienia.",
          "Archiwalny - grafik historyczny, którego nie używa się już do bieżącego planowania."
        ],
        warning: "Przed publikacją zawsze sprawdź otwarte zmiany oraz krytyczne ostrzeżenia. Publikacja nie naprawia braków obsady."
      },
      {
        title: "Druk grafiku i eksport",
        lead: "Drukowanie jest dostępne z poziomu otwartego grafiku. Każdy wydruk dotyczy wyłącznie działu przypisanego do tego grafiku, więc grafiki działów są drukowane oddzielnie.",
        steps: [
          "Otwórz Grafik pracy, wybierz właściwy grafik działu i kliknij Drukuj.",
          "Dla Arkusza jednodniowego wybierz konkretny dzień. Wydruk grupuje obsadę według stanowisk i pokazuje pracownika, lokalizację, godziny oraz czas zmiany.",
          "Dla Arkusza tygodniowego wskaż dowolny dzień tygodnia. System wyznacza zakres od poniedziałku do niedzieli i ogranicza go do dat otwartego grafiku.",
          "Dla Arkusza stanowiskowego wybierz zakres dat oraz wszystkie albo tylko wskazane stanowiska. Przy wydruku wielu stanowisk każde stanowisko rozpoczyna się od nowej strony.",
          "Sprawdź liczbę aktywnych zmian w podsumowaniu i kliknij Drukuj. W oknie drukowania przeglądarki możesz wybrać drukarkę albo zapisać dokument jako PDF."
        ],
        bullets: [
          "Zmiany anulowane są pomijane.",
          "Nieobsadzone pozycje pozostają na wydruku i są oznaczone jako BRAK OBSADY.",
          "Nagłówek wydruku zawiera nazwę działu, nazwę grafiku i jego status.",
          "Eksport XLSX/CSV jest niezależny od drukowania i służy do dalszej obróbki danych."
        ],
        tip: "Jeżeli potrzebujesz wydruku dla innego działu, otwórz grafik tego działu i uruchom drukowanie ponownie. Dzięki temu dane działów nie są mieszane na jednym wydruku."
      },
      {
        title: "Mój grafik - widok pracownika",
        lead: "Pracownik widzi własne opublikowane zmiany, a nie pełny grafik całego działu.",
        note: "Jeżeli grafik jest nadal Roboczy, pracownik nie powinien traktować go jako oficjalnego rozkładu pracy."
      }
    ]
  },
  {
    id: "requests",
    title: "Wnioski, urlopy i zamiany zmian",
    icon: "fas fa-inbox",
    roles: ["ADMIN", "MANAGER", "EMPLOYEE"],
    summary: "Workflow pracownik → kierownik: prośby, urlopy, otwarte zmiany i zamiany zmian.",
    links: [
      {path: "/app/schedule-requests", label: "Wnioski do grafiku", roles: ["ADMIN", "MANAGER"]},
      {path: "/app/my-schedule", label: "Mój grafik i wnioski", roles: ["EMPLOYEE"]},
      {path: "/app/notifications", label: "Powiadomienia", roles: ["EMPLOYEE"]}
    ],
    articles: [
      {
        title: "Pełny obieg wniosku - kto robi co",
        steps: [
          "Pracownik składa prośbę o wolne, urlop, deklarację dostępności, zgłoszenie do otwartej zmiany albo propozycję zamiany.",
          "Przy zamianie najpierw decyzję podejmuje drugi pracownik. Dopiero po jego akceptacji wniosek trafia do kierownika.",
          "Kierownik widzi wnioski swojego działu w ekranie Wnioski do grafiku i może filtrować je po statusie, dziale, typie oraz pracowniku.",
          "Przed zatwierdzeniem system ponownie sprawdza aktualne warunki. W przypadku urlopu kontrolowany jest również limit urlopów działu.",
          "Zatwierdzenie zmienia dane operacyjne: zapisuje nieobecność lub dostępność, obsadza otwartą zmianę albo zamienia pracowników między zmianami.",
          "Jeżeli zatwierdzone wolne lub urlop kolidują z istniejącą zmianą, pracownik zostaje z niej zdjęty, a pozycja staje się otwartą zmianą do ponownego obsadzenia.",
          "Pracownik otrzymuje informację o decyzji i widzi aktualny status w swoim grafiku oraz w historii wniosków."
        ],
        warning: "Status Oczekuje na kierownika oznacza, że decyzja drugiego pracownika - jeśli była wymagana - została już zakończona. Status Czeka na drugiego pracownika nie powinien być zatwierdzany przez kierownika."
      },
      {
        title: "Prośba o wolne / urlop",
        steps: [
          "Pracownik składa wniosek dla określonego terminu.",
          "System sprawdza minimalne wyprzedzenie skonfigurowane osobno dla prośby o wolne i urlopu. Po przekroczeniu terminu backend odrzuca wniosek, a formularz pokazuje najwcześniejszą dopuszczalną datę.",
          "Wniosek trafia do kierownika.",
          "Kierownik sprawdza wpływ na obsadę i zatwierdza albo odrzuca.",
          "Po zatwierdzeniu nieobecność jest uwzględniana przy grafiku i dostępności."
        ],
        note: "Wyprzedzenie może być ustawione dla konkretnego działu. Jeżeli dział nie ma własnej wartości, obowiązuje ustawienie globalne. Wartość 0 pozwala złożyć wniosek na dziś, o ile pozostałe reguły na to pozwalają."
      },
      {
        title: "Zamiana zmian",
        lead: "Zamiana nie powinna odbywać się wyłącznie między dwoma pracownikami bez kontroli systemu.",
        steps: [
          "Pracownik proponuje zamianę konkretnej zmiany z inną osobą.",
          "Druga osoba akceptuje albo odrzuca propozycję.",
          "Dopiero zaakceptowana przez obie strony propozycja trafia do decyzji kierownika.",
          "Po zatwierdzeniu system aktualizuje obsadę i unieważnia konkurencyjne propozycje dotyczące tej samej zmiany."
        ]
      },
      {
        title: "Przejęcie otwartej zmiany",
        bullets: [
          "Pracownik widzi w Mój grafik tylko te otwarte zmiany z wybranego okresu, do których aktualnie spełnia warunki przydziału.",
          "Na jedną zmianę może zgłosić się więcej niż jedna osoba.",
          "Samo zgłoszenie nie obsadza zmiany - do czasu decyzji kierownika pozostaje ona otwarta.",
          "Kierownik wybiera osobę, która otrzyma zmianę.",
          "Przy zatwierdzeniu system jeszcze raz sprawdza kolizje, dostępność, umowę i reguły czasu pracy.",
          "Po obsadzeniu pozycji pozostałe oczekujące zgłoszenia do tej samej otwartej zmiany są automatycznie odrzucane."
        ]
      }
    ]
  },
  {
    id: "vacations",
    title: "Urlopy działowe",
    icon: "fas fa-umbrella-beach",
    roles: ["ADMIN", "MANAGER"],
    summary: "Miesięczny widok urlopów całego działu oraz kontrola limitu jednocześnie nieobecnych osób.",
    links: [{path: "/app/department-vacations", label: "Urlopy działów", roles: ["ADMIN", "MANAGER"]}],
    articles: [
      {
        title: "Miesięczny widok urlopów",
        bullets: [
          "Pracownicy są pokazani w wierszach, a dni miesiąca w kolumnach.",
          "Filtr Dział zawiera pozycję Wszystkie, która łączy w jednym widoku wszystkie działy dostępne dla zalogowanego użytkownika.",
          "Widać, kto ma zatwierdzony urlop oraz które wnioski nadal czekają.",
          "Pod dniem można sprawdzić liczbę osób nieobecnych.",
          "Podczas przewijania listy pracowników filtr działu i miesiąca oraz nagłówek dni pozostają widoczne."
        ]
      },
      {
        title: "Limit urlopów w dziale",
        bullets: [
          "Limit i akcję po jego przekroczeniu ustawia się w Słowniki → Działy firmy podczas edycji konkretnego działu, a nie w panelu Urlopy działów.",
          "Dział może mieć ustawioną maksymalną liczbę osób na urlopie jednocześnie.",
          "Tryb Ostrzegaj informuje o przekroczeniu, ale nie blokuje decyzji.",
          "Tryb Blokuj nie pozwala zatwierdzić kolejnego urlopu ponad limit.",
          "Do limitu są liczone tylko typy nieobecności oznaczone w słowniku jako urlopowe."
        ]
      }
    ]
  },
  {
    id: "rcp",
    title: "RCP i rzeczywisty czas pracy",
    icon: "fas fa-clock",
    roles: ["ADMIN", "MANAGER"],
    summary: "Rejestr wejść, wyjść i przerw oraz porównanie rzeczywistego czasu pracy z grafikiem.",
    links: [
      {path: "/app/rcp-events", label: "Rejestr RCP", roles: ["ADMIN", "MANAGER"]},
      {path: "/app/rcp-terminals", label: "Terminale RCP", roles: ["ADMIN", "MANAGER"]},
      {path: "/app/schedule-reports", label: "Raporty czasu pracy", roles: ["ADMIN", "MANAGER"]}
    ],
    articles: [
      {
        title: "Jakie zdarzenia zapisuje RCP",
        bullets: [
          "WORK_START - rozpoczęcie pracy.",
          "WORK_END - zakończenie pracy.",
          "BREAK_START - rozpoczęcie przerwy.",
          "BREAK_END - zakończenie przerwy.",
          "ZONE_ENTRY - wejście do skonfigurowanej strefy, np. pomieszczenia socjalnego.",
          "ZONE_EXIT - wyjście ze strefy."
        ],
        note: "Źródłem zdarzenia może być karta, PIN, wpis ręczny, import, dane testowe MOCK, symulator albo uwierzytelniony fizyczny terminal/centrala ze źródłem TERMINAL."
      },
      {
        title: "Plan vs wykonanie RCP",
        bullets: [
          "Rzeczywista godzina wejścia i wyjścia.",
          "Łączny przepracowany czas i przerwy, także dla trwającej jeszcze sesji.",
          "Spóźnienie i wcześniejsze wyjście z uwzględnieniem tolerancji ustawionych w konfiguracji.",
          "Bilans planu do rozliczenia względem faktycznie przepracowanego czasu.",
          "Nadgodziny.",
          "Brak rozpoczęcia pracy albo niepełna sesja RCP.",
          "Ryzyko braku obsady wynikające z braku rozpoczęcia, spóźnienia lub wcześniejszego wyjścia."
        ],
        tip: "Przy ryzyku obsady w grafiku użyj akcji Reaguj. Pozwala przejść do dnia pracownika, oznaczyć nieobecność albo skorygować obsadę. Opublikowany grafik z bieżącym dniem odświeża dane RCP automatycznie."
      },
      {
        title: "Fizyczne terminale, czytniki i centralki",
        bullets: [
          "Każdy czytnik, tablet albo bramka centrali ma osobny kod, typ, aktywność i klucz API. Klucz nie jest przechowywany jawnie - backend zapisuje jego skrót SHA-256.",
          "Terminal można przypisać do lokalizacji i strefy RCP. Dzięki temu lokalizacja nie jest przyjmowana z dowolnego pola wysłanego przez urządzenie, tylko z zaufanej konfiguracji terminala.",
          "Neutralny sprzętowo endpoint /rcp-terminal-ingest/{kod}/events przyjmuje zdarzenia po nagłówku X-RCP-API-Key. Adapter producenta może mapować własny protokół do tego kontraktu bez zmiany logiki Qstaff.",
          "Jeżeli terminal ma od razu pokazać pracownikowi informację zwrotną, użyj /rcp-terminal-ingest/{kod}/events-with-feedback. Odpowiedź zawiera zapisane zdarzenie oraz nastrój POSITIVE/NEUTRAL/NEGATIVE, emotikonę, komunikat i sugerowaną zmianę punktów.",
          "Terminal wykorzystany w historii RCP nie może zostać usunięty. Należy go dezaktywować, aby zachować audyt historycznych zdarzeń."
        ],
        warning: "Dokumentacja projektu nie zawiera protokołu konkretnego modelu czytnika/centralki. Qstaff udostępnia gotowy interfejs integracyjny; jeżeli urządzenie nie potrafi wysłać tego formatu bezpośrednio, potrzebny jest mały adapter dla danego producenta."
      },
      {
        title: "Karta, PIN i foto-rejestracja",
        bullets: [
          "Odbicie może identyfikować pracownika kartą RCP. Jeżeli wraz z kartą przesłano PIN, backend sprawdza go z PIN-em zapisanym na aktywnej i ważnej karcie.",
          "Terminal może również przesłać identyfikator pracownika i PIN bez numeru karty. PIN jest wtedy weryfikowany na karcie przypisanej temu pracownikowi.",
          "Możliwe jest także odbicie samym PIN-em, jeżeli w aktywnych i ważnych kartach ten PIN jednoznacznie wskazuje jednego pracownika. Przy duplikacie terminal musi przesłać identyfikator pracownika.",
          "Zdjęcie przy odbiciu jest archiwizowanym dowodem, kto stanął przy terminalu. Obecny kod nie wykonuje biometrycznego rozpoznawania twarzy i specyfikacja projektu nie dostarcza modelu ani algorytmu takiego rozpoznawania."
        ],
        warning: "Nie traktuj samego pola employeeId jako PIN-u. Jeżeli terminal pracuje w trybie PIN, powinien przesłać pin, a przy nieunikalnym PIN-ie także employeeId."
      },
      {
        title: "Emotikonka i natychmiastowa informacja zwrotna",
        bullets: [
          "Po odbiciu endpoint z końcówką events-with-feedback zwraca informację przeznaczoną bezpośrednio do wyświetlenia na czytniku lub tablecie.",
          "Punktualne rozpoczęcie pracy zwraca pozytywną emotikonę. Spóźnienie ponad tolerancję z Ustawień zwraca komunikat negatywny z liczbą minut.",
          "Odbicie w niewłaściwej strefie daje od razu negatywną informację. Przy wyjściu ze strefy SOCIAL system porównuje długość pobytu z limitem strefy.",
          "Po powrocie z przerwy system sprawdza łączny czas przerw w bieżącej sesji względem globalnego limitu RCP.",
          "Odpowiedź zawiera również suggestedPointsDelta: +1, 0 albo -1. Jest to gotowy sygnał do punktowania, ale nie zmienia automatycznie oceny pracownika bez osobnej reguły biznesowej, ponieważ specyfikacja mówi jedynie, że emotikonka może wpływać na punktowanie."
        ],
        tip: "Ten sam mechanizm jest widoczny w Symulatorze odbicia, więc można sprawdzić zachowanie terminala bez usuwania ani wyłączania generatorów danych testowych."
      },
      {
        title: "Strefy, rejon odbicia i zdjęcie",
        bullets: [
          "Stanowisko może mieć wymaganą strefę RCP, a terminal własną strefę. Przy rozpoczęciu i zakończeniu pracy system porównuje oba miejsca i zapisuje wynik kontroli.",
          "Nieprawidłowa strefa nie usuwa zdarzenia - pozostaje ono w audycie i generuje czytelny alert zgodności.",
          "Terminal może mieć tryb zdjęcia: brak, opcjonalne albo wymagane. Dla trybu wymaganego odbicie bez zdjęcia jest odrzucane.",
          "Zdjęcia JPG, PNG i WEBP do 5 MB są sprawdzane także po sygnaturze pliku, archiwizowane przy konkretnym zdarzeniu i dostępne z Rejestru RCP dla uprawnionych użytkowników."
        ]
      },
      {
        title: "Zmiana lokalizacji w trakcie pracy",
        lead: "Jedna sesja pracy nie musi należeć w całości do jednego miejsca. Qstaff dzieli rzeczywisty czas pracy na segmenty lokalizacyjne.",
        bullets: [
          "WORK_START rozpoczyna sesję w lokalizacji wynikającej z terminala lub zdarzenia, jeżeli lokalizacja jest znana.",
          "ZONE_ENTRY do innej lokalizacji zamyka poprzedni segment i rozpoczyna kolejny bez kończenia całej sesji pracy.",
          "Przerwa nie jest przypisywana do roboczogodzin lokalizacji. Po zakończeniu przerwy naliczanie czasu lokalizacji jest wznawiane.",
          "Raport SPMH wg lokalizacji sumuje minuty z tych segmentów, więc np. 4 h w miejscu A i 4 h w miejscu B nie są liczone jako 8 h w miejscu rozpoczęcia pracy.",
          "Czas, dla którego nie udało się rozpoznać lokalizacji, jest raportowany osobno jako Bez rozpoznanej lokalizacji zamiast znikać z analizy."
        ],
        example: "10:00 WORK_START w A, 14:00 ZONE_ENTRY w B, 18:00 WORK_END w B daje 4 h dla A oraz 4 h dla B."
      },
      {
        title: "Przerwy i pobyt w pomieszczeniach socjalnych",
        bullets: [
          "Strefa może być oznaczona jako SOCIAL oraz jako liczona do czasu przerwy.",
          "ZONE_ENTRY rozpoczyna pobyt w strefie, a ZONE_EXIT go kończy. Przy strefie liczonej jako przerwa ten czas trafia również do sesji pracy.",
          "Dla strefy można ustawić maksymalny czas pojedynczego pobytu. Przekroczenie generuje alert.",
          "Niezależnie od limitu konkretnej strefy istnieje globalny maksymalny łączny czas przerw w sesji RCP; 0 wyłącza ten alert."
        ]
      },
      {
        title: "Symulator terminala RCP",
        bullets: [
          "W Rejestrze RCP użyj przycisku Symulator odbicia i wybierz pracownika oraz godzinę.",
          "Dostępne akcje zależą od bieżącego stanu: rozpoczęcie pracy, wyjście na przerwę, powrót z przerwy i zakończenie pracy.",
          "Symulator może automatycznie powiązać odbicie z najbliższą zmianą z grafiku i stanowiskiem.",
          "W dzisiejszym widoku grafiku przy pracowniku znajduje się skrót RCP otwierający symulator od razu dla tej osoby.",
          "Zdarzenia z symulatora mają źródło SIMULATOR, ale są liczone przez ten sam mechanizm czasu pracy i raportów co przyszłe odbicia z fizycznego terminala."
        ],
        warning: "SIMULATOR jest przeznaczony do testów i ręcznej symulacji działania terminala. Źródło zdarzenia pozostaje widoczne w rejestrze."
      },
      {
        title: "Alert o nieusprawiedliwionych nieobecnościach",
        bullets: [
          "Próg alertu jest definiowany w Ustawieniach w sekcji reguł planowania czasu pracy; domyślna wartość to 2.",
          "Każdy zapis nieobecności typu NN (Nieobecność nieusprawiedliwiona) jest liczony jako jedno zdarzenie, niezależnie od liczby dni objętych tym wpisem.",
          "Po osiągnięciu progu pracownik pojawia się na Tablicy w sekcji Wymaga uwagi wraz z liczbą zapisanych NN.",
          "Filtr działu na Tablicy ogranicza alerty do pracowników przypisanych do wybranego działu; kierownik widzi wyłącznie swój zakres dostępu.",
          "Pracownicy zarchiwizowani nie są pokazywani w alertach."
        ],
        note: "Specyfikacja nie wskazuje okresu rozliczeniowego dla tego alertu, dlatego licznik obejmuje całą zarejestrowaną historię zdarzeń NN pracownika."
      },
      {
        title: "Dane MOCK",
        warning: "MOCK służy wyłącznie do testowania integracji i raportów. Nie jest rzeczywistym odbiciem pracownika.",
        note: "Docelowy import z zewnętrznego systemu RCP może zasilać ten sam rejestr bez zmiany sposobu analizowania danych w Qstaff."
      }
    ]
  },
  {
    id: "economics",
    title: "Sprzedaż, prognozy i budżet pracy",
    icon: "fas fa-chart-line",
    roles: ["ADMIN", "MANAGER"],
    summary: "Ekonomiczna warstwa grafiku: łączy sprzedaż, liczbę pracowników, roboczogodziny i koszt pracy, żeby ocenić czy zaplanowana obsada ma sens biznesowy.",
    links: [{path: "/app/scheduler", label: "Grafiki i budżet pracy", roles: ["ADMIN", "MANAGER"]}],
    articles: [
      {
        title: "Do czego służy ten moduł",
        lead: "Plan zapotrzebowania odpowiada na pytanie: ilu ludzi i na jakich stanowiskach potrzebuję. Sprzedaż, prognozy i budżet pracy odpowiada na drugie pytanie: czy przy takim grafiku liczba ludzi, liczba godzin i koszt pracy mają sens względem spodziewanego utargu?",
        bullets: [
          "Moduł działa na danych konkretnego grafiku i porównuje PLAN z WYKONANIEM.",
          "Po stronie PLANU bierze pod uwagę zaplanowane zmiany, planowany koszt pracy, sprzedaż planowaną albo prognozę oraz ustawione targety.",
          "Po stronie WYKONANIA wykorzystuje sprzedaż wykonaną oraz rzeczywiste godziny i koszt pracy wynikające z RCP.",
          "Dla każdego dnia system może sygnalizować brak obsady, nadmiar obsady, przekroczenie budżetu godzin, przekroczenie %COL albo zbyt niskie SPMH.",
          "Celem nie jest automatyczne zwalnianie ludzi z grafiku, tylko dostarczenie kierownikowi informacji, czy obsada jest ekonomicznie uzasadniona i gdzie warto ją skorygować."
        ],
        tip: "Najlepiej traktować ten moduł jako kontrolę ekonomiczną grafiku po jego wygenerowaniu oraz jako narzędzie do bieżącego reagowania w dniu pracy."
      },
      {
        title: "Jakie dane można ustawić dla konkretnego dnia",
        bullets: [
          "Sprzedaż planowana - przewidywany utarg danego dnia. Jeśli ją wpiszesz, ma pierwszeństwo przed prognozami.",
          "Sprzedaż wykonana - rzeczywista sprzedaż po zakończeniu albo w trakcie dnia.",
          "Prognozowana liczba gości - informacja pomocnicza przy ocenie spodziewanego ruchu.",
          "Pogoda - może zostać wpisana ręcznie albo pobrana automatycznie ze skonfigurowanego dostawcy. System zapisuje m.in. kod i opis pogody, temperaturę minimalną i maksymalną, sumę oraz prawdopodobieństwo opadów i maksymalną prędkość wiatru.",
          "Korekta zapotrzebowania wg pogody - procentowa zmiana bazowej liczby wymaganych osób dla dnia, używana przez generator grafiku, np. -20% albo +15%.",
          "%COL TARGET - docelowy lub maksymalny udział kosztu pracy w sprzedaży.",
          "SPMH TARGET - docelowa minimalna sprzedaż przypadająca na jedną roboczogodzinę.",
          "Budżet roboczogodzin - maksymalna liczba godzin pracy, którą chcesz przeznaczyć na dany dzień.",
          "Minimalna obsada - liczba osób, poniżej której nie powinno się schodzić.",
          "Maksymalna obsada - liczba osób, powyżej której system może sygnalizować nadmiar.",
          "Uwagi - dodatkowa informacja dla osoby planującej lub analizującej dzień."
        ]
      },
      {
        title: "Pogoda a zapotrzebowanie na pracowników",
        lead: "Specyfikacja wymaga tworzenia zapotrzebowania także względem warunków pogodowych. Qstaff nie narzuca jednej reguły biznesowej, ponieważ wpływ deszczu, temperatury czy wiatru na frekwencję Parku musi wynikać z decyzji osoby planującej.",
        bullets: [
          "Administrator włącza integrację w Ustawieniach i podaje współrzędne lokalizacji, strefę czasową oraz adres dostawcy prognozy.",
          "W szczegółach grafiku przycisk Pobierz pogodę pobiera dane dla widocznego zakresu i zapisuje je przy odpowiednich dniach.",
          "Kierownik widzi opis pogody, temperaturę, opady i wiatr, a następnie może wpisać Korektę zapotrzebowania wg pogody w procentach.",
          "Podczas generowania grafiku opcja Uwzględnij korektę zapotrzebowania wg pogody przelicza bazową liczbę osób z planu zapotrzebowania przed przydzieleniem pracowników.",
          "Przykład: bazowe zapotrzebowanie to 10 osób. Korekta -20% daje 8 osób, a +15% daje po zaokrągleniu 12 osób.",
          "Jeżeli korekta ma zmniejszyć już istniejący wygenerowany grafik, użyj opcji usunięcia istniejących zmian w okresie i wygeneruj okres ponownie. Bez tej opcji generator nie usuwa sam istniejących zmian."
        ],
        warning: "Pobrana prognoza pogody nie zmienia zapotrzebowania automatycznie według ukrytego algorytmu. Specyfikacja nie definiuje zależności typu deszcz = określony procent mniej pracowników, dlatego procentową korektę ustala kierownik na podstawie warunków i wiedzy biznesowej.",
        tip: "Dla dni o dużym znaczeniu najpierw pobierz świeżą prognozę, ustaw korektę pogodową, a dopiero potem uruchom generator. Przy zmianie prognozy możesz zaktualizować pogodę i przeliczyć grafik ponownie."
      },
      {
        title: "SPRZEDAŻ PLAN, WYKONANIE i prognozy",
        lead: "Sprzedaż jest podstawą do wyliczenia %COL i SPMH. Nie musisz jednak wpisywać ręcznego planu sprzedaży dla każdego dnia.",
        bullets: [
          "Jeżeli wpisano SPRZEDAŻ PLAN dla dnia, ta wartość jest podstawą wskaźników planowych.",
          "Jeżeli nie ma SPRZEDAŻ PLAN, system używa prognozy dziennej.",
          "Jeżeli nie ma prognozy dziennej, prognoza miesięczna jest dzielona równomiernie przez liczbę dni miesiąca.",
          "Jeżeli nie ma również prognozy miesięcznej, prognoza roczna jest dzielona przez liczbę dni roku.",
          "Prognozy mogą zawierać sprzedaż, liczbę gości, opis pogody i uwagi.",
          "SPRZEDAŻ WYKONANIE jest używana do porównania z rzeczywistymi godzinami i kosztami RCP."
        ],
        example: "Przykład: dla sierpnia zapisujesz prognozę miesięczną 3 100 000 zł. System uzyskuje z niej około 100 000 zł dziennie. Jeśli dla konkretnej soboty wpiszesz ręcznie SPRZEDAŻ PLAN = 180 000 zł, ten ręczny plan ma pierwszeństwo przed prognozą miesięczną."
      },
      {
        title: "%COL - udział kosztu pracy w sprzedaży",
        lead: "COL oznacza Cost of Labour. Pokazuje jaki procent sprzedaży pochłania koszt pracy pracowników.",
        bullets: [
          "%COL PLAN = zaplanowany koszt pracy / sprzedaż planowa × 100%.",
          "%COL WYKONANIE = rzeczywisty koszt pracy z RCP / sprzedaż wykonana × 100%.",
          "%COL TARGET określa poziom, którego nie chcesz przekraczać.",
          "Na podstawie sprzedaży i targetu system wylicza maksymalny dopuszczalny koszt pracy dla dnia.",
          "Jeżeli planowany koszt jest wyższy od limitu, system pokazuje nadwyżkę kosztu i sygnalizuje przekroczenie targetu."
        ],
        example: "Przykład: sprzedaż planowana = 50 000 zł, koszt grafiku = 7 500 zł. %COL PLAN = 15%. Jeśli %COL TARGET = 14%, maksymalny koszt pracy wynosi 7 000 zł, więc grafik jest o 500 zł ponad limitem."
      },
      {
        title: "SPMH - sprzedaż na roboczogodzinę",
        lead: "SPMH oznacza Sales Per Man Hour, czyli sprzedaż przypadającą na jedną roboczogodzinę pracownika.",
        bullets: [
          "SPMH PLAN = sprzedaż planowa / zaplanowane roboczogodziny.",
          "SPMH WYKONANIE = sprzedaż wykonana / rzeczywiście przepracowane godziny z RCP.",
          "SPMH TARGET określa oczekiwaną minimalną sprzedaż przypadającą na jedną godzinę pracy.",
          "Na podstawie sprzedaży i targetu system wylicza maksymalną liczbę roboczogodzin, przy której target nadal będzie spełniony.",
          "Jeżeli liczba zaplanowanych godzin jest wyższa, system pokazuje ile godzin jest ponad limitem wynikającym z SPMH."
        ],
        example: "Przykład: sprzedaż planowana = 50 000 zł, zaplanowano 400 h pracy. SPMH PLAN = 125 zł/h. Przy SPMH TARGET = 140 zł/h maksymalny budżet wynikający z tego targetu to około 357 h, więc grafik ma około 43 h za dużo."
      },
      {
        title: "Dlaczego jednocześnie kontrolujemy %COL, SPMH i budżet godzin",
        bullets: [
          "Budżet roboczogodzin kontroluje wyłącznie liczbę godzin.",
          "%COL kontroluje koszt tych godzin w odniesieniu do sprzedaży.",
          "SPMH kontroluje produktywność - ile sprzedaży przypada na jedną godzinę pracy.",
          "Grafik może mieścić się w budżecie godzin, ale być za drogi, jeśli obsadzono go drogimi pracownikami - wtedy budżet godzin jest OK, ale %COL może być zły.",
          "Grafik może mieć dobry %COL dzięki taniej obsadzie, ale nadal mieć zbyt wiele roboczogodzin - wtedy SPMH może być zbyt niskie.",
          "System niezależnie kontroluje więc liczbę osób, liczbę godzin, koszt, %COL i SPMH."
        ],
        tip: "Nie traktuj jednego wskaźnika jako zamiennika pozostałych. Najbardziej użyteczny jest wspólny obraz wszystkich ograniczeń."
      },
      {
        title: "Minimalna i maksymalna obsada",
        bullets: [
          "Dla konkretnego dnia możesz ustawić minimalną i maksymalną liczbę osób.",
          "Jeżeli obsada spadnie poniżej minimum albo w grafiku pozostają otwarte, nieobsadzone zmiany, dzień jest traktowany jako niedoobsadzony.",
          "Jeżeli liczba osób przekracza maksimum, system pokazuje nadmiar obsady.",
          "Minimalna obsada jest zabezpieczeniem także dla funkcji Puszczamy do domu - system nie powinien rekomendować redukcji, która zejdzie poniżej wymaganego minimum.",
          "System pilnuje również pokrycia stanowiska, więc redukcja nie powinna pozostawić stanowiska całkowicie bez aktywnej osoby."
        ],
        example: "Przykład: minimum = 8 osób, maksimum = 15 osób. Grafik z 17 osobami oznacza nadmiar 2 osób. Grafik z 7 osobami oznacza niedobór nawet wtedy, gdy pozostałe wskaźniki ekonomiczne wyglądają dobrze."
      },
      {
        title: "Puszczamy do domu - symulacja wcześniejszego wyjścia",
        lead: "Funkcja służy do reagowania w dniu pracy, gdy rzeczywisty ruch i sprzedaż są słabsze od założeń, a obsada okazuje się za duża.",
        bullets: [
          "Wskazujesz godzinę wcześniejszego wyjścia, np. 14:30.",
          "System analizuje pracowników, którzy o tej godzinie nadal mają aktywne zmiany.",
          "Dla każdego kandydata pokazuje oszczędność roboczogodzin i kosztu.",
          "Pokazuje też przewidywany %COL i SPMH po skróceniu konkretnej zmiany.",
          "Sprawdza liczbę osób, która pozostanie po skróceniu, minimalną obsadę oraz liczbę osób pozostających na danym stanowisku.",
          "Jeżeli dzień już ma braki, redukcja zejdzie poniżej minimum albo zostawi stanowisko bez obsady, kandydat jest oznaczany jako ryzykowny.",
          "Awaryjne wykonanie takiej decyzji wymaga świadomego nadpisania zabezpieczenia i podania uzasadnienia."
        ],
        example: "Przykład: o 14:00 widzisz +25 h ponad budżet, %COL = 19% przy target 14% i SPMH = 90 zł/h przy target 130 zł/h. Otwierasz Puszczamy do domu, ustawiasz 14:30 i porównujesz, którego pracownika można zwolnić wcześniej oraz jak zmieni to koszt, %COL, SPMH i obsadę stanowiska.",
        warning: "To narzędzie wspierające decyzję kierownika. Sam alert ekonomiczny nie usuwa pracownika z grafiku."
      },
      {
        title: "SPMH według lokalizacji",
        lead: "Raport lokalizacyjny pozwala połączyć sprzedaż wykonaną konkretnego miejsca z rzeczywistymi roboczogodzinami przepracowanymi właśnie w tym miejscu.",
        bullets: [
          "Sprzedaż wykonaną zapisujesz dla dnia i lokalizacji.",
          "Roboczogodziny pochodzą z RCP i są dzielone na lokalizacje zgodnie z rzeczywistymi przejściami pracownika między miejscami.",
          "SPMH lokalizacji = sprzedaż wykonana lokalizacji / rzeczywiste roboczogodziny RCP w tej lokalizacji.",
          "Stanowisko nie decyduje o lokalizacji. Ta sama Obsługa Atrakcji może zasilać w ciągu dnia kilka lokalizacji.",
          "Jeżeli RCP nie zawiera informacji o miejscu dla części czasu pracy, raport pokazuje ten czas jako Bez rozpoznanej lokalizacji."
        ],
        note: "Domyślna lokalizacja stanowiska ułatwia planowanie, ale nie zastępuje faktycznej lokalizacji z RCP przy analizie WYKONANIA."
      },
      {
        title: "PLAN kontra WYKONANIE",
        lead: "Po podłączeniu danych RCP moduł pozwala sprawdzić, czy rzeczywisty dzień potoczył się tak, jak zakładał grafik i budżet.",
        bullets: [
          "Godziny PLAN pokazują godziny z grafiku, a WYKONANIE rzeczywiste godziny z RCP.",
          "Koszt PLAN pokazuje koszt zaplanowanej obsady, a WYKONANIE koszt wynikający z faktycznie przepracowanego czasu.",
          "Sprzedaż PLAN jest porównywana ze sprzedażą wykonaną.",
          "%COL PLAN i WYKONANIE pokazują, czy udział kosztów pracy w sprzedaży pogorszył się lub poprawił.",
          "SPMH PLAN i WYKONANIE pokazują zmianę produktywności roboczogodzin.",
          "W podsumowaniu widać także liczbę dni z brakami, nadmiarem obsady, przekroczonym budżetem godzin i alertami ekonomicznymi."
        ],
        example: "Przykład: PLAN - sprzedaż 50 000 zł, 350 h, koszt 6 500 zł, %COL 13%, SPMH 142,86 zł/h. WYKONANIE - sprzedaż 43 000 zł, 375 h, koszt 7 200 zł, %COL 16,74%, SPMH 114,67 zł/h. Widać jednocześnie niższą sprzedaż i 25 h więcej pracy, więc oba wskaźniki ekonomiczne pogorszyły się."
      },
      {
        title: "Jak korzystać z modułu w praktyce",
        steps: [
          "Przed sezonem albo miesiącem wprowadź prognozy miesięczne lub roczne.",
          "Gdy masz dokładniejsze informacje, dodaj prognozy dzienne dla konkretnych dni.",
          "Jeżeli pogoda ma wpływać na zapotrzebowanie, pobierz prognozę dla widocznego okresu i ustaw procentową korektę dla wybranych dni.",
          "Utwórz plan zapotrzebowania i wygeneruj grafik z włączoną opcją uwzględniania korekty pogodowej.",
          "W szczegółach grafiku ustaw dla ważnych dni %COL TARGET, SPMH TARGET, budżet roboczogodzin oraz minimalną i maksymalną obsadę.",
          "Sprawdź czy zaplanowany grafik mieści się w targetach i czy nie ma braków lub nadmiaru obsady.",
          "Jeżeli wskaźniki są złe, zmień liczbę osób, godziny albo skład obsady i ponownie oceń wynik.",
          "W dniu pracy wpisuj sprzedaż wykonaną lub uzupełnij ją po zakończeniu dnia.",
          "RCP dostarczy rzeczywiste godziny pracy i pozwoli policzyć WYKONANIE.",
          "Porównaj PLAN z WYKONANIEM i sprawdź, skąd wzięło się odchylenie.",
          "Jeżeli ruch jest słabszy niż zakładano i system pokazuje nadmiar, użyj Puszczamy do domu do bezpiecznej symulacji wcześniejszego wyjścia."
        ],
        note: "Najważniejszy sens biznesowy modułu: grafik nie odpowiada już tylko na pytanie kto ma przyjść, ale również czy stać nas na taki grafik przy przewidywanej i rzeczywistej sprzedaży."
      }
    ]
  },
  {
    id: "reports",
    title: "Raporty kierownika",
    icon: "fas fa-chart-bar",
    roles: ["ADMIN", "MANAGER"],
    summary: "Godziny, koszty, RCP, braki, sprzedaż, obciążenie pracowników i eksport danych.",
    links: [{path: "/app/schedule-reports", label: "Raporty grafików", roles: ["ADMIN", "MANAGER"]}],
    articles: [
      {
        title: "Co pokazują raporty",
        bullets: [
          "Planowane i rzeczywiście przepracowane godziny.",
          "Planowany i rzeczywisty koszt pracy.",
          "Braki planowane, braki efektywne oraz ryzyko obsady wykryte na podstawie RCP.",
          "Spóźnienia, wcześniejsze wyjścia, braki rozpoczęcia i niepełne dane RCP.",
          "Sprzedaż, SPMH oraz %COL.",
          "Liczbę wniosków i ich statusy.",
          "Liczbę dni pracy i weekendów dla pracowników.",
          "Osoby pracujące ponad 5 dni albo mniej niż 3–4 dni w tygodniu.",
          "Najczęściej nieobecnego pracownika w wybranym zakresie.",
          "Średnią dzienną obsadę planowaną i efektywną wraz ze średnią stawką wykonania netto i brutto.",
          "Łączną liczbę godzin wszystkich pracowników każdego dnia oraz dzienny bilans wykonanie minus plan."
        ]
      },
      {
        title: "Raport dzienny, miesięczny i filtr pracownika",
        bullets: [
          "Przycisk Dzisiaj ustawia raport na jeden dzień.",
          "Przycisk Bieżący miesiąc ustawia pełny zakres bieżącego miesiąca.",
          "Możesz podać dowolny własny zakres dat oraz ograniczyć raport do konkretnego pracownika.",
          "W widoku dziennym widać obsadę planowaną i efektywną, braki planowane, ryzyka RCP oraz minuty ubytku obsady.",
          "Podsumowanie pracownika zawiera plan, wykonanie, frekwencję, spóźnienia, wcześniejsze wyjścia, braki RCP, nieobecności oraz planowany i rzeczywisty koszt."
        ],
        note: "Przy raporcie jednej osoby wskaźniki sprzedażowe całego działu nie są sztucznie przypisywane do tej osoby."
      },
      {
        title: "Dashboard menadżera – szybki podgląd",
        bullets: [
          "W górnej części raportu widzisz najważniejsze wskaźniki dla wybranego działu i okresu: godziny plan/wykonanie, normę i nadgodziny, frekwencję, koszty, braki kadrowe, SPMH/%COL oraz wnioski.",
          "Średnia obsada / dzień pokazuje przeciętną liczbę osób zaplanowanych oraz przeciętną obsadę efektywną po uwzględnieniu braków startu RCP.",
          "Przy średniej obsadzie prezentowana jest również średnia stawka wykonania netto i brutto. Jeżeli w danych brakuje części stawek, raport nie udaje kompletnej wartości i oznacza brak danych.",
          "Sekcja szybkich statystyk pokazuje pracownika z najmniejszą i największą liczbą godzin oraz pracownika z największą liczbą dni nieobecności w wybranym zakresie.",
          "Zakładka Dni / wnioski / obsada pokazuje łączne godziny wszystkich pracowników dla każdego dnia osobno: PLAN, WYKONANIE i bilans dnia.",
          "Bilans dnia jest liczony jako WYKONANIE minus PLAN. Wartość ujemna oznacza mniej przepracowanych godzin niż zaplanowano i zgodnie z wymaganiem jest zielona; wartość dodatnia oznacza więcej godzin i jest czerwona.",
          "W tabeli pracowników osobno widzisz tygodnie z więcej niż 5 dniami pracy oraz liczbę tygodni z mniej niż 3 i mniej niż 4 dniami pracy."
        ],
        example: "Przykład: jeżeli pracownik zostaje 2 godziny i 30 minut po planowanym końcu zmiany, bilans dnia wynosi +2,50 h. Przy wcześniejszym wyjściu bilans jest ujemny o rzeczywistą różnicę czasu.",
        note: "Wskaźniki dashboardu są liczone dla aktualnie wybranego działu, zakresu dat i – jeżeli zastosowano filtr – konkretnego pracownika."
      },
      {
        title: "Norma miesięczna UoP, bilans i niedobór",
        lead: "Norma miesięczna jest liczona dla okresów, w których pracownika obejmuje umowa o polityce UoP / EMPLOYMENT.",
        bullets: [
          "Dla 100% etatu system liczy standardowo 8 godzin za dzień od poniedziałku do piątku, pomijając dni wolne skonfigurowane w systemie. Dla niższego wymiaru dzienna i miesięczna norma jest proporcjonalnie zmniejszana.",
          "Jeżeli umowa zaczyna się albo kończy w środku miesiąca, norma jest liczona tylko dla dni objętych aktywną umową w analizowanym okresie.",
          "Przy mieszanych typach umów do wykonania względem normy UoP wliczane są godziny z dni rozliczanych polityką EMPLOYMENT, a pozostałe godziny są nadal widoczne w całkowitym wykonaniu.",
          "Bilans do normy = wykonane godziny UoP minus norma. Wartość dodatnia tworzy nadgodziny względem normy, a ujemna pokazuje niedobór do normy.",
          "Frekwencja porównuje liczbę dni faktycznej obecności z liczbą dni, na które pracownik był zaplanowany."
        ],
        warning: "Norma w tym raporcie jest technicznym wyliczeniem na podstawie aktualnej konfiguracji dni roboczych, dni wolnych i okresu umowy. Nie zastępuje indywidualnej interpretacji szczególnych systemów czasu pracy."
      },
      {
        title: "Nadgodziny względem normy a czas ponad grafik",
        bullets: [
          "Nadgodziny względem normy są liczone miesięcznie jako dodatnia różnica wykonanych godzin UoP ponad wyliczoną normę.",
          "Czas ponad grafik oznacza faktyczną pracę po planowanym końcu konkretnej zmiany.",
          "Te wartości są celowo rozdzielone. Zostanie 30 minut dłużej jednego dnia nie musi oznaczać 30 minut miesięcznych nadgodzin względem normy.",
          "Analogicznie pracownik może mieć dodatni bilans miesięczny nawet wtedy, gdy żadna pojedyncza zmiana nie zakończyła się później od planu."
        ],
        tip: "Do analizy konkretnego dnia używaj kolumn plan/RCP i ponad grafik. Do rozliczenia miesiąca patrz na normę, bilans do normy, nadgodziny i niedobór."
      },
      {
        title: "Raport według stawek i kosztów",
        bullets: [
          "Jeżeli pracownik ma różne stawki na różnych stanowiskach, raport rozdziela godziny zamiast łączyć je pod jedną uśrednioną stawką.",
          "Dla każdej pozycji widać stanowisko, dokument i politykę umowy, stawkę, źródło stawki i jej okres obowiązywania.",
          "Raport pokazuje profil ubezpieczeniowy, składki pracodawcy, prowizję agencji, firmę zatrudniającą i całkowity koszt na godzinę.",
          "Osobno prezentowane są godziny, wynagrodzenie bazowe i całkowity koszt pracodawcy dla PLANU oraz WYKONANIA.",
          "Kolumna Kompletny informuje, czy kalkulator miał wszystkie dane niezbędne do wyznaczenia pełnego kosztu pracodawcy."
        ]
      },
      {
        title: "Co oznacza niepełna kalkulacja kosztu",
        bullets: [
          "Niepełny koszt oznacza, że system potrafił ustalić część danych, ale brakuje elementu koniecznego do wiarygodnego całkowitego kosztu pracodawcy.",
          "Typowe przyczyny to brak stawki, brak właściwej podstawy lub stawek ZUS, brak konfiguracji kosztu ucznia/studenta albo KRUS oraz konflikt nakładających się stawek okresowych.",
          "W takim przypadku raport oznacza pozycję jako niekompletną i nie powinno się traktować wartości częściowych jako pełnego kosztu zatrudnienia.",
          "Liczba pozycji z brakami kosztu jest widoczna także w podsumowaniach raportów."
        ],
        tip: "Najpierw otwórz Stawki okresowe przy stanowisku pracownika i użyj kalkulacji na konkretny dzień. Wyświetlone ostrzeżenia zwykle wskazują brakującą konfigurację."
      },
      {
        title: "Raport pracodawcy: roboczogodziny, netto, brutto i pełny koszt",
        bullets: [
          "Raport pracodawcy opiera roboczogodziny na rzeczywistym wykonaniu z RCP, a nie wyłącznie na planie grafiku.",
          "Pokazuje efektywną stawkę netto/h, efektywną stawkę brutto/h, wynagrodzenie netto, wynagrodzenie brutto oraz całkowity koszt zatrudnienia.",
          "Stawka netto jest przechowywana niezależnie od brutto na tym samym poziomie źródła stawki: stawka okresowa pracownik + stanowisko, stawka domyślna pracownik + stanowisko, umowa godzinowa/miesięczna albo stawka bazowa stanowiska.",
          "System nie wylicza netto automatycznie z brutto, ponieważ bez danych podatkowych i składkowych takie przeliczenie mogłoby być błędne.",
          "Jeżeli w okresie obowiązywało kilka stawek, efektywna stawka jest średnią ważoną rzeczywistymi roboczogodzinami.",
          "Brak stawki netto, brutto lub elementu pełnej kalkulacji kosztu jest oznaczany osobno. Raport XLSX nie przedstawia częściowej wartości jako kompletnej.",
          "Umowa może mieć jawnie wpisany planowany koszt odprawy i komentarz HR. Raport sumuje odprawy umów kończących się w analizowanym okresie i pokazuje liczbę takich umów."
        ],
        tip: "Stawki netto uzupełnij tam, gdzie utrzymujesz odpowiadające im stawki brutto. Dla danych historycznych pozostawione NULL oznacza świadomy brak danych, a nie zero."
      },
      {
        title: "Szczegółowa ewidencja czasu",
        bullets: [
          "Plan od/do oraz planowana liczba minut.",
          "RCP od/do, rzeczywisty czas pracy i przerwy.",
          "Bilans względem grafiku, spóźnienia i wcześniejsze wyjścia.",
          "Czas ponad planowaną zmianę.",
          "Ryzyko danych RCP i ryzyko obsady.",
          "Nieobecności oraz sesje RCP bez odpowiadającej zmiany w grafiku oznaczone jako RCP_ONLY.",
          "Stawka godzinowa i całkowity koszt godzinowy użyty do rozliczenia wpisu."
        ]
      },
      {
        title: "Raport księgowy XLSX i rozliczenie pracownika",
        bullets: [
          "Raport księgowy XLSX obejmuje cały wybrany zakres i zawiera arkusze Podsumowanie, Norma miesięczna, SPMH lokalizacje, Stawki oraz Ewidencja czasu.",
          "Rozliczenie pracownika XLSX generuje analogiczne dane tylko dla wybranej osoby.",
          "W rozliczeniu zleceniobiorcy godziny pozostają rozbite według faktycznie użytych stawek i stanowisk, co pozwala rozliczyć jedną osobę pracującą za kilka stawek.",
          "Raport zachowuje rozróżnienie między wynagrodzeniem bazowym a całkowitym kosztem pracodawcy."
        ]
      },
      {
        title: "Eksport do Gang",
        bullets: [
          "Eksport Gang CSV można wygenerować zbiorczo dla działu albo indywidualnie dla wybranego pracownika.",
          "W Ustawieniach administrator definiuje kodowanie Windows-1250 lub UTF-8, separator, obecność nagłówka, format daty/czasu oraz dokładną kolejność i nazwy kolumn.",
          "Mapowanie ma postać KLUCZ=NAZWA, np. PESEL=PESEL lub WORKED_MINUTES=CZAS_PRACY. Backend waliduje dozwolone klucze, duplikaty i format profilu przed zapisem ustawień.",
          "Dzięki profilowi ten sam mechanizm może zostać dopasowany do konkretnej instalacji Wapro Gang bez zmiany kodu aplikacji."
        ],
        warning: "Dokumenty projektu nie zawierają profilu importowego docelowej instalacji Wapro Gang. Przed odbiorem produkcyjnym trzeba wpisać profil przekazany przez księgowość i wykonać jeden próbny import w tej instalacji."
      }
    ]
  },
  {
    id: "training",
    title: "Szkolenia i testy szkoleniowe",
    icon: "fas fa-graduation-cap",
    roles: ["ADMIN", "MANAGER", "HR"],
    summary: "Rejestrowanie kwalifikacji, obiegówek i wyników testów, które wpływają na ocenę pracownika oraz mogą wpływać na dopuszczenie do grafiku.",
    links: [{path: "/app/trainingx", label: "Testy szkoleniowe", roles: ["ADMIN", "MANAGER", "HR"]}],
    articles: [
      {
        title: "Szkolenia i certyfikaty w kartotece",
        bullets: [
          "Szkolenia oraz certyfikaty są zapisywane przy pracowniku.",
          "Do zapisanego szkolenia można dołączyć zdjęcie lub skan obiegówki szkoleniowej w formacie PDF, JPG albo PNG do 10 MB.",
          "Załącznik jest przechowywany przez backend i można go później pobrać albo usunąć z kartoteki pracownika.",
          "Można kontrolować ważność kwalifikacji.",
          "System może przypominać o zbliżającym się terminie ważności certyfikatu."
        ]
      },
      {
        title: "Ocena szkolenia: punkty, opis i osoba oceniająca",
        bullets: [
          "W kartotece pracownika przy każdym szkoleniu można zapisać osobno osobę prowadzącą szkolenie oraz osobę wystawiającą ocenę.",
          "Ocena szkolenia może zawierać ocenę punktową, datę oceny i niezależną ocenę opisową. Pole Uwagi organizacyjne pozostaje oddzielne od właściwej oceny opisowej.",
          "Ocena punktowa ukończonego szkolenia trafia do grupy Szkolenia w automatycznej ocenie ogólnej pracownika. Brak wpisanej oceny punktowej nie jest traktowany jako zero.",
          "Po ukończeniu szkolenia system może ustawić termin weryfikacji umiejętności miesiąc po zakończeniu i wysłać przypomnienie osobie prowadzącej.",
          "Do szkolenia można dołączyć skan lub zdjęcie obiegówki, a koszt szkolenia jest wykorzystywany przy rozliczeniu budżetu szkoleniowego pracownika."
        ]
      },
      {
        title: "Rozwój, umiejętności i rozmowy oceniające",
        lead: "Specyfikacja przewiduje ocenę punktową i opisową w kilku kategoriach oraz wskazanie osoby wystawiającej ocenę. W kartotece pracownika służy do tego sekcja Ocena ogólna.",
        steps: [
          "Otwórz kartotekę pracownika i przejdź do sekcji Ocena ogólna.",
          "W formularzu Nowa ocena punktowa i opisowa wybierz kategorię: Rozwój, Szkolenie, Umiejętności albo Rozmowa oceniająca.",
          "Podaj datę, liczbę punktów, wagę pojedynczej oceny i opis. HR lub administrator wskazuje osobę wystawiającą ocenę; dla kierownika system ustawia ją automatycznie jako zalogowanego kierownika.",
          "Zapisana ocena pojawia się w historii ocen pracownika i natychmiast uruchamia ponowne przeliczenie oceny ogólnej.",
          "Oceny z tej sekcji są najpierw liczone jako średnia ważona wewnątrz grupy Rozwój i rozmowy, a następnie grupa jest włączana do oceny ogólnej z wagą ustawianą w Ustawieniach."
        ],
        note: "Waga przy pojedynczym wpisie określa znaczenie konkretnej rozmowy lub oceny w grupie. Waga grupy Rozwój i rozmowy w Ustawieniach określa znaczenie całej tej grupy względem szkoleń, testów, umiejętności, stanowisk i zdarzeń krytycznych."
      },
      {
        title: "Testy szkoleniowe - cały proces od definicji do grafiku",
        lead: "Moduł quizów jest częścią procesu szkoleniowego pracownika. Test nie jest osobnym, oderwanym formularzem: wynika z typu szkolenia, może zostać przypisany na profil pracownika, jest oceniany automatycznie, zapisuje historię i wpływa na ocenę pracownika oraz reguły dopuszczenia do grafiku.",
        steps: [
          "W słowniku Typy szkoleń utwórz albo edytuj właściwy typ szkolenia i ustaw pole Forma szkolenia na Quiz w Qstaff.",
          "Przejdź do Testy szkoleniowe i w sekcji Definicje testów utwórz test dla tego typu szkolenia.",
          "Zdecyduj, czy test jest globalny, czy działowy. Ustaw nazwę, instrukcję, próg zaliczenia, maksymalną liczbę podejść i status aktywny.",
          "Dodaj pytania, odpowiedzi i oznacz odpowiedzi prawidłowe. Każde pytanie może być jednokrotnego albo wielokrotnego wyboru.",
          "W sekcji Przypisywanie testów pracownikom wybierz test, opcjonalny termin wykonania oraz odbiorców: konkretnych pracowników, całe działy lub osoby zajmujące wskazane stanowiska.",
          "Po przypisaniu pracownik widzi test w Panelu pracownika → Szkolenia i kwalifikacje. Może rozpocząć test, zaznaczyć odpowiedzi i użyć Zakończ i sprawdź.",
          "Qstaff automatycznie oblicza wynik procentowy, porównuje go z progiem zaliczenia i zapisuje podejście jako ZALICZONE albo NIEZALICZONE.",
          "W module Testy szkoleniowe wynik pojawia się w Historii wyników. Automatyczny wynik quizu jest historią audytową i nie można go ręcznie edytować ani usuwać.",
          "Po zapisaniu wyniku Qstaff przelicza ocenę ogólną pracownika. Niezaliczone podejścia są również uwzględniane przez regułę miesięcznego limitu niezaliczonych testów podczas sprawdzania dopuszczenia do grafiku."
        ],
        example: "Przykład: tworzysz typ szkolenia „Obsługa kasy” z formą Quiz, test „Obsługa kasy - sprawdzian” z progiem 80% i limitem 3 podejść, a następnie przypisujesz go wszystkim pracownikom stanowiska Kasjer z terminem do 30 czerwca. Każdy pracownik otrzymuje własne przypisanie, rozwiązuje ten sam test, a wynik jest zapisywany w jego historii.",
        note: "Oryginalne wymagania Qstaff zakładają połączenie testów z oceną pracownika i grafikiem, w tym możliwość niedopuszczenia do grafiku po określonej liczbie niezaliczonych testów w miesiącu."
      },
      {
        title: "Krok 1 - przygotowanie Typu szkolenia",
        steps: [
          "Otwórz słownik Typy szkoleń.",
          "Dodaj nowy typ szkolenia albo otwórz istniejący.",
          "W polu Forma szkolenia wybierz Quiz w Qstaff, jeżeli szkolenie ma kończyć się testem rozwiązywanym i ocenianym w Qstaff.",
          "Wybierz Inna forma szkolenia, jeżeli dany typ nie ma mieć quizu w Qstaff.",
          "Zapisz typ szkolenia. Dopiero typ oznaczony jako Quiz można wybrać przy tworzeniu nowej definicji testu."
        ],
        warning: "Zmiana typu szkolenia na Inna forma powoduje, że pracownik nie może wykonywać powiązanego quizu. Przy tworzeniu i edycji definicji testu backend wymaga formy Quiz."
      },
      {
        title: "Krok 2 - utworzenie testu i wybór zakresu",
        steps: [
          "Otwórz Testy szkoleniowe i znajdź sekcję Definicje testów.",
          "Kliknij Dodaj test.",
          "Wybierz Typ szkolenia. Lista nowych testów pokazuje typy oznaczone jako Quiz.",
          "Wybierz Dział albo pozostaw Globalny / wszystkie działy, jeżeli masz uprawnienia HR lub administratora.",
          "Podaj nazwę testu oraz opcjonalną instrukcję dla pracownika.",
          "Ustaw Próg zaliczenia % w zakresie 0-100.",
          "Ustaw Maksymalną liczbę podejść w zakresie 1-100.",
          "Pozostaw Test aktywny włączony, jeżeli test ma być dostępny do przypisywania i rozwiązywania.",
          "Dodaj pytania i odpowiedzi, a następnie kliknij Zapisz test."
        ],
        bullets: [
          "Test globalny nie jest przypisany do konkretnego działu i może być rozsyłany do pracowników różnych działów przez HR / administratora.",
          "Test działowy jest przeznaczony tylko dla jednego działu i może zostać przypisany wyłącznie osobom należącym do tego działu.",
          "Dla jednego typu szkolenia może istnieć jeden test globalny oraz po jednym odrębnym teście w każdym dziale.",
          "Kierownik nie tworzy testu globalnego. Może tworzyć, przeglądać i zmieniać testy tylko działów, którymi zarządza."
        ]
      },
      {
        title: "Krok 3 - definiowanie pytań i klucza odpowiedzi",
        bullets: [
          "Test musi zawierać co najmniej jedno pytanie.",
          "Każde pytanie musi zawierać co najmniej dwie odpowiedzi i przynajmniej jedną odpowiedź oznaczoną jako prawidłowa.",
          "Dla zwykłego pytania jednokrotnego wyboru dokładnie jedna odpowiedź może być prawidłowa.",
          "Włączenie Wiele poprawnych odpowiedzi zmienia pytanie na wielokrotnego wyboru i pozwala oznaczyć kilka prawidłowych odpowiedzi.",
          "Pracownik nie widzi informacji o tym, które odpowiedzi są prawidłowe. Do Panelu pracownika przesyłane są pytania i odpowiedzi bez klucza poprawności.",
          "Przy pytaniu wielokrotnego wyboru odpowiedź jest zaliczona tylko wtedy, gdy pracownik zaznaczy dokładnie cały prawidłowy zestaw. Brak jednej prawidłowej odpowiedzi albo zaznaczenie dodatkowej błędnej odpowiedzi powoduje 0 punktów za całe pytanie.",
          "Wynik całego testu to liczba w pełni poprawnie rozwiązanych pytań podzielona przez liczbę wszystkich pytań i przeliczona na procent."
        ],
        example: "Test ma 10 pytań, a pracownik rozwiązał poprawnie 8. Wynik wynosi 80,00%. Przy progu 75% test jest zaliczony; przy progu 85% - niezaliczony."
      },
      {
        title: "Krok 4 - przypisanie testu pracownikom",
        steps: [
          "W sekcji Przypisywanie testów pracownikom wybierz aktywny test.",
          "Opcjonalnie ustaw Termin wykonania.",
          "Wskaż odbiorców w jednej lub kilku grupach: Działy, Stanowiska albo Wybrani pracownicy.",
          "Przy wyborze działu test zostanie przypisany wszystkim pracownikom należącym do tego działu.",
          "Przy wyborze stanowiska test zostanie przypisany pracownikom mającym aktywne przypisanie do tego stanowiska.",
          "W polu Wybrani pracownicy możesz wyszukać konkretną osobę po imieniu, nazwisku lub loginie i zaznaczyć ją ręcznie.",
          "Kliknij Przypisz test. Qstaff łączy wszystkie wskazane grupy i nie tworzy podwójnego przypisania tej samej osobie.",
          "Po przypisaniu sprawdź tabelę Aktualne przypisania: pracownik, dział, termin, liczba podejść, liczba pozostałych podejść i status."
        ],
        note: "Ponowne przypisanie tego samego testu tej samej osobie aktualizuje istniejące przypisanie, ustawia nowy termin z formularza i ponownie je aktywuje zamiast tworzyć duplikat.",
        warning: "Termin wykonania jest obecnie informacją i oznaczeniem opóźnienia. Po jego przekroczeniu test jest oznaczony jako po terminie, ale sam termin nie blokuje technicznie wysłania odpowiedzi."
      },
      {
        title: "Wycofanie i przywrócenie testu na profilu pracownika",
        bullets: [
          "W tabeli Aktualne przypisania przy aktywnym przypisaniu użyj Wycofaj, aby usunąć test z bieżącej listy testów pracownika bez kasowania historii.",
          "Przypisanie otrzymuje status WYCOFANE. Poprzednie wyniki i historia pozostają w systemie.",
          "Użyj Przywróć, aby ponownie udostępnić to samo przypisanie pracownikowi.",
          "Jeżeli test miał już w przeszłości bezpośrednie przypisanie, jego wycofanie nie powoduje automatycznego powrotu do starszego mechanizmu udostępniania testu przez samo przypisanie szkolenia."
        ]
      },
      {
        title: "Wersjonowanie testu i liczba podejść",
        bullets: [
          "Nowy test zaczyna od wersji 1.",
          "Każde zapisanie zmian w definicji istniejącego testu zwiększa numer wersji.",
          "Limit podejść jest liczony osobno dla aktualnej wersji testu. Po zapisaniu nowej wersji pracownik otrzymuje pełną pulę podejść przewidzianą dla tej nowej wersji.",
          "Przy każdym automatycznym podejściu Qstaff zapisuje numer wersji, migawkę definicji pytań i odpowiedzi oraz migawkę odpowiedzi pracownika.",
          "Dzięki migawkom późniejsza edycja testu nie zmienia historycznego znaczenia wcześniej zapisanego wyniku.",
          "Po wykorzystaniu wszystkich podejść przycisk rozpoczęcia testu jest blokowany, a backend również odrzuca kolejne wysłanie odpowiedzi."
        ],
        warning: "Edycja testu jest jednocześnie utworzeniem nowej wersji w sensie historii podejść. Jeżeli zmieniasz tylko opis lub próg, również zwiększasz wersję i licznik podejść dla nowej wersji zaczyna się od zera."
      },
      {
        title: "Co dokładnie widzi i robi pracownik",
        bullets: [
          "W Panelu pracownika → Szkolenia i kwalifikacje znajduje się sekcja Testy do wykonania.",
          "Przy każdym teście widoczne są: nazwa, typ szkolenia, ewentualny dział, liczba pytań, próg zaliczenia, wykorzystane podejścia, maksymalna liczba podejść, termin oraz ostatni wynik.",
          "Jeżeli termin minął, informacja o terminie jest wyróżniona jako spóźniona.",
          "Przycisk Rozpocznij test pojawia się przed pierwszą próbą, a Wykonaj ponownie po zapisanym podejściu, o ile pozostały dostępne próby.",
          "Pracownik musi odpowiedzieć na każde pytanie. System nie pozwala zakończyć testu z pustą odpowiedzią.",
          "Po użyciu Zakończ i sprawdź pracownik od razu dostaje informację o wyniku procentowym i o tym, czy test został zaliczony.",
          "Historia jego podejść pozostaje widoczna w sekcji Wyniki testów."
        ]
      },
      {
        title: "Historia wyników, wynik automatyczny i wpis ręczny",
        bullets: [
          "Automatyczne rozwiązanie quizu tworzy wpis historii powiązany z konkretnym testem i wersją. Taki wpis jest oznaczony jako Wynik automatyczny · historia audytowa.",
          "Automatycznego wyniku nie można edytować ani usunąć z panelu administracyjnego.",
          "Sekcja Rejestracja wyniku testu służy do ręcznego dodania wyniku spoza automatycznego quizu, np. testu papierowego albo wyniku przeniesionego z innego źródła.",
          "Przy wyniku ręcznym wybiera się dział, pracownika, szkolenie, datę i godzinę, wynik 0-100%, status Zaliczony oraz opcjonalne uwagi.",
          "Wynik ręczny można później edytować albo usunąć. Kierownik może zarządzać wynikami wyłącznie pracowników swoich działów.",
          "Każde dodanie, zmiana lub usunięcie wyniku ręcznego powoduje ponowne przeliczenie oceny pracownika."
        ],
        warning: "Wpis ręczny jest pełnoprawnym wynikiem szkoleniowym. Jeżeli oznaczysz go jako NIEZALICZONY, jest uwzględniany także przy liczeniu miesięcznego limitu niezaliczonych testów dla grafiku."
      },
      {
        title: "Wpływ testów na ocenę ogólną pracownika",
        bullets: [
          "Qstaff liczy średnią procentową ze wszystkich zapisanych wyników testów pracownika, dla których zapisano wynik procentowy.",
          "Średnia procentowa jest przeliczana na skalę używaną przez ocenę szkoleń i trafia do kategorii Testy szkoleniowe w ocenie ogólnej.",
          "Znaczenie tej kategorii zależy od pola Waga testów szkoleniowych w ocenie ogólnej w Ustawieniach aplikacji.",
          "Po automatycznym rozwiązaniu quizu oraz po dodaniu, zmianie lub usunięciu wyniku ręcznego ocena jest przeliczana bez oczekiwania na okresowe zadanie systemowe.",
          "Generator grafiku, gdy korzysta z kryterium Najwyższa ocena, bierze pod uwagę bieżącą ocenę ogólną zawierającą również komponent testów."
        ],
        example: "Jeżeli pracownik ma wyniki 60%, 80% i 100%, średnia testów wynosi 80%. Jeżeli skala oceny szkoleń ma maksimum 5, komponent testów odpowiada 4,00 przed zastosowaniem jego wagi w ocenie ogólnej."
      },
      {
        title: "Niezaliczone testy a dopuszczenie do grafiku",
        lead: "To jest niezależna reguła od samej średniej ocen. Pracownik może mieć wysoką ocenę ogólną, ale zostać twardo odrzucony z konkretnej zmiany, jeżeli osiągnął miesięczny limit niezaliczonych testów.",
        bullets: [
          "Ustawienie Miesięczny limit niezaliczonych testów szkoleniowych określa, od ilu niezaliczonych wyników pracownik nie może zostać przydzielony do zmiany w danym miesiącu.",
          "Domyślna wartość ustawienia wynosi 2.",
          "Wartość 0 wyłącza tę blokadę.",
          "Przy ocenie kandydata do zmiany system liczy niezaliczone podejścia od początku miesiąca zmiany do sprawdzanego okresu w tym miesiącu.",
          "Po osiągnięciu limitu pracownik otrzymuje twardy powód odrzucenia i nie jest kwalifikowany do obsady tej zmiany.",
          "Powód odrzucenia jest dostępny w diagnostyce kwalifikacji kandydata do grafiku."
        ],
        example: "Przy limicie 2 pracownik, który ma już dwa niezaliczone wyniki w miesiącu zmiany, nie może zostać automatycznie przydzielony do kolejnej zmiany w tym miesiącu. Przy limicie 0 ta reguła nie blokuje pracownika."
      },
      {
        title: "Uprawnienia - kto może zarządzać quizami",
        bullets: [
          "ADMIN może tworzyć testy globalne i działowe, przypisywać je pracownikom oraz przeglądać historię wyników.",
          "HR ma taki sam zakres zarządzania quizami jak administrator w zakresie modułu testów.",
          "MANAGER widzi i zarządza wyłącznie testami działowymi należącymi do działów, którymi zarządza.",
          "MANAGER może przypisać test tylko pracownikom należącym do jego działów; dodatkowo test działowy może trafić wyłącznie do pracownika należącego do działu tego testu.",
          "EMPLOYEE i MINION nie mają dostępu do definicji ani przypisywania testów. Widzą wyłącznie własne testy i własną historię w Panelu pracownika."
        ],
        warning: "Konto kierownika musi być powiązane z pracownikiem i faktycznie zarządzać działem. W przeciwnym razie backend nie pozwoli mu zarządzać testami działowymi."
      },
      {
        title: "Usuwanie, dezaktywacja i zachowanie historii",
        bullets: [
          "Test bez przypisań i bez historii podejść można usunąć z listy Definicje testów.",
          "Testu, który ma już przypisania albo historię podejść, nie można usunąć. Qstaff wymaga wtedy wyłączenia go przez przełącznik Test aktywny.",
          "Wyłączenie testu powoduje, że pracownik nie może go otworzyć ani rozwiązać, ale definicja, przypisania i historia pozostają w bazie.",
          "Wycofanie pojedynczego przypisania działa tylko dla wskazanego pracownika i nie dezaktywuje testu dla pozostałych osób."
        ],
        note: "Zasada blokowania usuwania chroni historię audytową i zapobiega utracie kontekstu zapisanych wyników."
      },
      {
        title: "Dlaczego pracownik nie widzi testu - lista kontrolna",
        steps: [
          "Sprawdź, czy Typ szkolenia ma ustawioną formę Quiz w Qstaff.",
          "Sprawdź, czy definicja testu ma włączony status Test aktywny.",
          "Sprawdź Aktualne przypisania i upewnij się, że przypisanie pracownika ma status AKTYWNE.",
          "Jeżeli jest to test działowy, sprawdź, czy pracownik należy do właściwego działu.",
          "Sprawdź, czy pracownik nie wykorzystał już maksymalnej liczby podejść dla aktualnej wersji testu.",
          "Jeżeli korzystasz ze starszych danych bez bezpośredniego przypisania testu, test globalny może być widoczny na podstawie szkolenia przypisanego w kartotece pracownika. Po utworzeniu bezpośredniego przypisania jego status staje się rozstrzygający.",
          "Termin po dacie nie ukrywa testu - jest jedynie oznaczony jako przekroczony."
        ]
      }
    ]
  },
  {
    id: "hr-meetings",
    title: "Spotkania HR",
    icon: "fas fa-calendar-check",
    roles: ["ADMIN", "HR", "MANAGER"],
    summary: "Konsultacje indywidualne rezerwowane przez pracowników oraz spotkania grupowe z kontrolą konfliktów z grafikiem, zaproszeniami i odpowiedziami uczestników.",
    links: [{path: "/app/hr-appointments", label: "Otwórz Spotkania HR", roles: ["ADMIN", "HR", "MANAGER"]}],
    articles: [
      {
        title: "Dwa rodzaje spotkań HR",
        bullets: [
          "Konsultacje indywidualne służą do publikowania wolnych godzin, które pojedynczy pracownik może sam zarezerwować.",
          "Spotkania grupowe służą do zapraszania pracowników ze wszystkich dostępnych działów, z wybranych działów albo wskazanych osób na jedno wspólne wydarzenie, np. spotkanie organizacyjne lub świąteczne.",
          "Oba rodzaje spotkań mogą mieć miejsce stacjonarne, link do spotkania online oraz dodatkową informację dla pracowników."
        ],
        note: "Qstaff zarządza terminami, zaproszeniami i odpowiedziami. Jeżeli podasz link do Teams, Meet lub Zoom, samo połączenie odbywa się w zewnętrznym narzędziu."
      },
      {
        title: "Konsultacje indywidualne - blok dostępności i automatyczne terminy",
        lead: "Nie trzeba tworzyć każdego terminu osobno. HR może wystawić cały blok dostępności i podzielić go automatycznie na krótsze konsultacje.",
        steps: [
          "W module Spotkania HR wybierz Konsultacje indywidualne i kliknij Dodaj dostępność.",
          "Wybierz dział, dla którego mają być widoczne terminy, oraz wpisz nazwę konsultacji.",
          "Podaj początek i koniec całego bloku, np. 10:00-14:00.",
          "Wybierz długość pojedynczego spotkania: 15, 20, 30, 45 albo 60 minut.",
          "Opcjonalnie podaj miejsce, link do spotkania online i informację dla pracownika.",
          "Kliknij Utwórz terminy. System utworzy osobne, niezależnie rezerwowalne okienka w całym bloku."
        ],
        example: "Blok 10:00-14:00 i długość 30 minut tworzy osiem terminów: 10:00-10:30, 10:30-11:00, ... 13:30-14:00.",
        warning: "Długość całego bloku musi być wielokrotnością długości pojedynczej konsultacji. System nie utworzy też bloku nakładającego się na aktywne terminy HR tego samego działu."
      },
      {
        title: "Rezerwacja konsultacji przez pracownika",
        bullets: [
          "Pracownik widzi przyszłe, aktywne i niezarezerwowane terminy przeznaczone dla jego działu.",
          "Przy rezerwacji podaje obowiązkowo temat spotkania oraz opcjonalną wiadomość dla HR.",
          "Rezerwacja jest natychmiastowa - nie wymaga dodatkowego zatwierdzenia przez HR, a wybrany termin przestaje być dostępny dla innych osób.",
          "System nie pozwala pracownikowi utworzyć nakładających się rezerwacji konsultacji HR.",
          "Pracownik może anulować przyszłą rezerwację; zwolnione okienko ponownie staje się dostępne.",
          "HR może później oznaczyć rezerwację jako odbytą, anulowaną przez HR albo jako nieobecność pracownika i dopisać informację widoczną dla pracownika."
        ],
        note: "Zarezerwowanego terminu nie można przenieść, przypisać do innego działu ani wyłączyć bez wcześniejszego anulowania aktywnej rezerwacji."
      },
      {
        title: "Spotkanie grupowe - wybór odbiorców",
        lead: "Spotkanie grupowe jest zaproszeniem na jedno wspólne wydarzenie, a nie terminem do rezerwacji przez jedną osobę.",
        steps: [
          "Przejdź do Spotkania HR → Spotkania grupowe i kliknij Nowe spotkanie grupowe.",
          "Wpisz nazwę, początek i koniec spotkania oraz opcjonalnie miejsce, link online i opis dla uczestników.",
          "W sekcji Odbiorcy zaproszenia wybierz Wszystkie działy albo zaznacz dowolną liczbę wybranych działów.",
          "W polu Dodatkowo wybrane osoby możesz wskazać konkretne osoby niezależnie od wyboru działów. Wyszukiwanie działa po imieniu, nazwisku i dziale.",
          "Ta sama osoba wskazana przez dział i ręcznie jest liczona tylko raz.",
          "Przed zapisaniem obowiązkowo użyj funkcji Sprawdź uczestników i konflikty z grafikiem."
        ],
        note: "Administrator i HR mogą wybierać pracowników ze wszystkich dostępnych działów. Kierownik działa w zakresie działów i pracowników, do których ma uprawnienia. Pracownicy zarchiwizowani nie są zapraszani."
      },
      {
        title: "Kontrola konfliktów spotkania z grafikiem",
        lead: "Przed wysłaniem zaproszeń system porównuje czas spotkania z przypisanymi zmianami wszystkich wybranych pracowników.",
        bullets: [
          "Dla każdej osoby system pokazuje wynik Może uczestniczyć albo Konflikt z grafikiem.",
          "Przy konflikcie widać dokładne godziny kolidującej zmiany, stanowisko, nazwę grafiku i jego status.",
          "Nad listą widoczna jest liczba wszystkich odbiorców oraz liczba osób z konfliktem.",
          "Konflikt jest ostrzeżeniem, a nie blokadą. HR nadal może wysłać zaproszenie takiej osobie.",
          "Po zmianie daty, godzin albo listy odbiorców wcześniejszy podgląd traci ważność i przed zapisaniem trzeba ponownie sprawdzić uczestników i konflikty.",
          "Pracownik, który otrzymał zaproszenie mimo konfliktu, widzi przy nim wyraźną informację, że w tym czasie ma zmianę w grafiku."
        ],
        warning: "Zaproszenie na spotkanie nie usuwa ani nie modyfikuje zmiany pracownika. Ewentualną zmianę grafiku trzeba obsłużyć osobno."
      },
      {
        title: "Zaproszenia, powiadomienia i odpowiedzi pracowników",
        bullets: [
          "Po utworzeniu spotkania każdy zaproszony otrzymuje powiadomienie w Qstaff. Zgodnie z ustawieniami komunikacji pracownika informacja jest również wysyłana e-mailem i/lub SMS-em.",
          "Powiadomienie zawiera termin, miejsce lub link online, opis spotkania oraz ostrzeżenie o konflikcie z grafikiem, jeżeli taki konflikt występuje.",
          "W Panelu pracownika zaproszony wybiera jedną z trzech odpowiedzi: Będę, Wstępnie tak albo Na pewno nie.",
          "Odpowiedź można zmienić do momentu rozpoczęcia zaplanowanego spotkania.",
          "HR widzi dla spotkania liczbę zaproszonych, odpowiedzi Będę, Wstępnie tak, Na pewno nie, braków odpowiedzi oraz konfliktów z grafikiem.",
          "Po rozwinięciu Uczestnicy i odpowiedzi HR widzi status każdej konkretnej osoby."
        ]
      },
      {
        title: "Edycja, odwołanie i zakończenie spotkania grupowego",
        bullets: [
          "Zaplanowane spotkanie, które jeszcze się nie rozpoczęło, można edytować. Po zmianie danych lub odbiorców trzeba ponownie sprawdzić konflikty z grafikiem.",
          "Po zapisaniu zmian aktualnie zaproszeni pracownicy otrzymują powiadomienie o aktualizacji spotkania.",
          "Spotkanie można odwołać. Zaproszeni pracownicy otrzymują wtedy powiadomienie o odwołaniu, a status spotkania zmienia się na Anulowane.",
          "Po odbyciu wydarzenia HR może oznaczyć je jako Zakończone.",
          "Zakończonego spotkania nie można później anulować, a anulowanego nie można oznaczyć jako zakończone."
        ]
      }
    ]
  },
  {
    id: "communication",
    title: "Komunikacja SMS i e-mail",
    icon: "fas fa-paper-plane",
    roles: ["ADMIN", "MANAGER"],
    summary: "Powiadomienia systemowe wysyłane zgodnie z preferencjami kontaktu pracownika. SMS może działać testowo albo przez rzeczywistą bramkę SMSAPI z aktualizacją statusu dostarczenia.",
    links: [
      {path: "/app/schedule-notifications", label: "Powiadomienia grafików", roles: ["ADMIN", "MANAGER"]},
      {path: "/app/communication-simulator", label: "Symulator SMS / e-mail", roles: ["ADMIN"]}
    ],
    articles: [
      {
        title: "Jak działa wysyłka",
        bullets: [
          "Qstaff korzysta z pól Komunikacja przez e-mail i Komunikacja przez SMS zapisanych przy pracowniku.",
          "Dla e-maila w pierwszej kolejności używany jest adres firmowy, a jeśli go nie ma - prywatny.",
          "Dla SMS używany jest telefon podstawowy, a następnie dodatkowy.",
          "Powiadomienia obejmują m.in. publikację i zmianę grafiku, decyzje dotyczące wniosków, wiadomości do kierownika, spotkania HR oraz przypomnienia o certyfikatach.",
          "Wiadomości dotyczące grafiku nadal zapisują status wysyłki osobno dla e-mail i SMS."
        ]
      },
      {
        title: "Powiadomienia grafików - wysyłka przez kierownika",
        lead: "Centrum Powiadomienia grafików służy do ręcznego wysyłania informacji dotyczących konkretnego opublikowanego grafiku i do kontroli odbioru.",
        steps: [
          "Otwórz Nawigacja → Powiadomienia grafików.",
          "Wybierz dział, a następnie konkretny opublikowany grafik. Kierownik widzi wyłącznie działy, do których ma uprawnienia; administrator może wybrać dowolny dział.",
          "W polu Odbiorcy wybierz osoby przypisane do grafiku, wszystkich pracowników działu albo jednego pracownika działu.",
          "Wpisz tytuł i treść. Jeżeli pozostawisz je puste, system użyje standardowej informacji o wybranym grafiku.",
          "Kliknij Wyślij powiadomienia. Dla każdego odbiorcy powstaje osobny wpis w Qstaff, dzięki czemu można później sprawdzić jego odczyt i potwierdzenie.",
          "Po wysyłce sprawdź sekcję Historia i weryfikacja odbioru. Statusy można odświeżać, aby pobrać późniejszy callback dostarczenia SMS."
        ],
        bullets: [
          "Osoby przypisane do grafiku - wiadomość otrzymują wyłącznie pracownicy mający aktywną zmianę w tym grafiku.",
          "Wszyscy pracownicy działu - wiadomość otrzymują wszyscy aktywni pracownicy możliwi do przypisania w tym dziale, także jeśli w danym grafiku nie mają zmiany.",
          "Jeden pracownik działu - wysyłka indywidualna do wskazanej osoby.",
          "Każdy dział może wysłać swój grafik niezależnie od pozostałych działów. Wysyłka nie wymaga publikowania wszystkich działów jednocześnie.",
          "E-mail i SMS są uruchamiane zgodnie z ustawieniami komunikacji konkretnego pracownika. Brak włączonego kanału nie blokuje powiadomienia wewnętrznego Qstaff."
        ],
        warning: "Do pracowników należy wysyłać wyłącznie grafik opublikowany. Centrum celowo nie udostępnia grafików roboczych do ręcznej wysyłki."
      },
      {
        title: "Automatyczne powiadomienia o publikacji i zmianach",
        bullets: [
          "Przy pierwszej publikacji grafiku system automatycznie informuje pracowników przypisanych do aktywnych zmian.",
          "Po zmianie danych opublikowanego grafiku system tworzy powiadomienie o aktualizacji.",
          "Dodanie, zmiana lub usunięcie konkretnej zmiany w opublikowanym grafiku tworzy powiadomienie dla pracownika, którego ta zmiana dotyczy.",
          "Decyzje dotyczące próśb, urlopów i otwartych zmian oraz kolejne etapy zamiany zmian również generują powiadomienia dla właściwych uczestników procesu.",
          "Wycofanie opublikowanego grafiku generuje informację, że grafik nie jest już aktywnym grafikiem pracowników."
        ],
        note: "Ręczna wysyłka z Centrum powiadomień jest uzupełnieniem automatycznych komunikatów, a nie ich zamiennikiem."
      },
      {
        title: "Jak rozumieć potwierdzenie odbioru i statusy kanałów",
        bullets: [
          "Nieodczytane / Odczytano dotyczy otwarcia powiadomienia wewnątrz Qstaff.",
          "Bez potwierdzenia / Potwierdzono oznacza świadome użycie przez pracownika przycisku Potwierdź odbiór. To jest właściwy mechanizm potwierdzenia zapoznania się z komunikatem w Qstaff.",
          "NOT_REQUESTED - dany kanał nie został wybrany w ustawieniach komunikacji pracownika.",
          "QUEUED - wiadomość została przyjęta do kolejki operatora.",
          "SENT - wiadomość została przekazana do wysyłki, ale samo SENT nie potwierdza jeszcze doręczenia SMS.",
          "DELIVERED - operator SMS potwierdził doręczenie wiadomości do telefonu odbiorcy.",
          "UNDELIVERED lub FAILED - wiadomość nie została doręczona albo wystąpił błąd wysyłki.",
          "NO_ADDRESS - pracownik ma włączony kanał, ale nie ma poprawnego adresu e-mail lub numeru telefonu.",
          "Historia pokazuje osobno stan Qstaff, e-mail i SMS, dlatego potwierdzenie pracownika i techniczne doręczenie SMS nie są ze sobą mylone."
        ],
        tip: "Do sprawdzania, czy pracownik faktycznie zapoznał się z komunikatem, używaj kolumny Potwierdzenie w Qstaff. DELIVERED potwierdza techniczne dostarczenie SMS, a nie przeczytanie jego treści."
      },
      {
        title: "Tryb fake, SMSAPI i potwierdzenie dostarczenia",
        bullets: [
          "Domyślnie kanały pozostają w trybie fake, dzięki czemu testy nie wysyłają wiadomości do prawdziwych odbiorców.",
          "Po ustawieniu qstaff.communication.sms.mode=smsapi i danych dostępowych SMS-y są wysyłane przez SMSAPI.",
          "Qstaff zapisuje identyfikator wiadomości nadany przez operatora oraz bieżący status wysyłki.",
          "Callback SMSAPI aktualizuje status wiadomości, w tym DELIVERED albo UNDELIVERED, oraz czas potwierdzonego dostarczenia.",
          "W module Komunikacja SMS / e-mail administrator może wysłać test i sprawdzić identyfikator oraz status operatora."
        ],
        warning: "Status SENT oznacza przyjęcie wiadomości do obsługi. Potwierdzeniem doręczenia jest dopiero status DELIVERED otrzymany z callbacku operatora."
      }
    ]
  },
  {
    id: "employee-zone",
    title: "Strefa pracownika i powiadomienia",
    icon: "fas fa-user-circle",
    roles: ["EMPLOYEE", "ADMIN"],
    summary: "Panel samoobsługowy pracownika: grafik, wnioski, dostępność, nieobecności, kwalifikacje, dokumenty, rezerwacja spotkań HR i kontakt z firmą.",
    links: [
      {path: "/app/employee-panel", label: "Panel pracownika", roles: ["EMPLOYEE"]},
      {path: "/app/employee-profile", label: "Moje dane", roles: ["EMPLOYEE", "ADMIN"]},
      {path: "/app/my-schedule", label: "Mój grafik", roles: ["EMPLOYEE"]},
      {path: "/app/notifications", label: "Powiadomienia", roles: ["EMPLOYEE"]}
    ],
    articles: [
      {
        title: "Panel pracownika",
        bullets: [
          "Ekran Start pokazuje dział, stanowiska, status ucznia/studenta, aktualną umowę i najważniejsze liczniki.",
          "Z panelu można przejść bezpośrednio do własnego grafiku, powiadomień, danych i Bazy Wiedzy.",
          "Zakładka Dostępność pokazuje zaakceptowane okresy dostępności oraz historię deklaracji oczekujących na decyzję kierownika.",
          "Zakładka Urlopy i nieobecności pokazuje faktycznie zapisane nieobecności oraz wnioski o wolne i urlop.",
          "Zakładka Szkolenia i kwalifikacje pokazuje szkolenia, certyfikaty, aktywne testy do wykonania oraz historię wyników testów szkoleniowych.",
          "Zakładka Dokumenty pokazuje pliki z Twojego folderu pracowniczego, w tym zapisane umowy, udostępnia regulaminy i materiały opublikowane przez firmę oraz pozwala przesłać dokument do HR. Pliki z folderu można podejrzeć w obsługiwanych formatach i pobrać.",
          "Zakładka Spotkania HR pokazuje zaproszenia na spotkania grupowe, pozwala odpowiedzieć Będę / Wstępnie tak / Na pewno nie oraz udostępnia wolne terminy konsultacji indywidualnych do samodzielnej rezerwacji.",
          "Zakładka Kontakt służy do pytania do wybranego kierownika albo anonimowego zgłoszenia. Przy pytaniu wybierasz konkretnego przełożonego z listy kierowników przypisanych do Twoich działów. Starsze prośby o spotkanie pozostają widoczne w historii zgłoszeń."
        ]
      },
      {
        title: "Jak zgłosić wolne albo urlop",
        steps: [
          "W Panelu pracownika kliknij Zgłoś wolne / urlop albo użyj tej samej funkcji w widoku Mój grafik.",
          "Wybierz Prośbę o wolne albo Urlop / nieobecność.",
          "Podaj zakres dat. Dla urlopu wybierz również typ nieobecności.",
          "Wyślij wniosek. Do momentu decyzji kierownika możesz go anulować.",
          "Po zatwierdzeniu urlop trafia do rejestru nieobecności. Zatwierdzona prośba o wolne zapisuje pracownika jako niedostępnego w tym okresie."
        ],
        warning: "Samo wysłanie wniosku nie zmienia grafiku. Zmiana zaczyna obowiązywać dopiero po zatwierdzeniu przez kierownika."
      },
      {
        title: "Jak zgłosić niestandardową dostępność",
        steps: [
          "Otwórz Dostępność i wybierz Nowa deklaracja.",
          "Wybierz Mogę pracować albo Preferuję pracę.",
          "Podaj okres oraz opcjonalnie godziny.",
          "Jeżeli deklaracja ma dotyczyć tylko wybranych dni tygodnia, włącz powtarzanie tygodniowe i zaznacz dni.",
          "Wyślij deklarację do zatwierdzenia. Po akceptacji pojawi się w kalendarzu dostępności i będzie uwzględniana przez generator grafiku."
        ],
        note: "Niedostępności nie zgłasza się przez deklarację dostępności. Do tego służy prośba o wolne albo urlop, ponieważ wymaga osobnego workflow akceptacji."
      },
      {
        title: "Jak umówić indywidualną konsultację z HR",
        steps: [
          "Otwórz zakładkę Spotkania HR w Panelu pracownika.",
          "W sekcji Wolne terminy HR wybierz jedno z dostępnych okienek opublikowanych dla Twojego działu i kliknij Zarezerwuj.",
          "Wpisz temat spotkania oraz opcjonalną informację dla HR. Po zapisaniu termin od razu staje się zajęty dla innych pracowników.",
          "W sekcji Moje konsultacje z HR zobaczysz godzinę, miejsce albo link do spotkania online oraz ewentualną informację od HR.",
          "Przed rozpoczęciem spotkania możesz anulować własną rezerwację. Zwolniony termin ponownie pojawi się na liście wolnych terminów."
        ],
        note: "Rezerwujesz pojedyncze okienko, nawet jeżeli HR udostępnił wcześniej kilkugodzinny blok konsultacji. System dzieli taki blok na osobne terminy."
      },
      {
        title: "Jak odpowiedzieć na zaproszenie na spotkanie grupowe",
        steps: [
          "Otwórz zakładkę Spotkania HR. Zaproszenia grupowe są pokazane nad wolnymi terminami konsultacji.",
          "Sprawdź nazwę, termin, opis, miejsce lub link do spotkania.",
          "Jeżeli przy zaproszeniu widzisz komunikat o konflikcie z grafikiem, oznacza to, że w czasie spotkania masz przypisaną zmianę. Samo zaproszenie nie usuwa tej zmiany.",
          "Wybierz odpowiedź Będę, Wstępnie tak albo Na pewno nie.",
          "Do rozpoczęcia zaplanowanego spotkania możesz zmienić swoją odpowiedź."
        ],
        note: "HR widzi Twoją aktualną odpowiedź oraz informację, czy przy wysłaniu zaproszenia występował konflikt z grafikiem."
      },
      {
        title: "Moje dane",
        lead: "Pracownik ma dostęp wyłącznie do własnego profilu. Może zmienić podstawowe dane kontaktowe i hasło, ale nie ma dostępu do administracyjnej kartoteki innych osób."
      },
      {
        title: "Powiadomienia o grafiku",
        bullets: [
          "Publikacja grafiku.",
          "Zmiany w opublikowanym grafiku.",
          "Decyzje dotyczące wniosków o wolne, urlop i dostępność.",
          "Propozycje i decyzje dotyczące zamian zmian."
        ],
        note: "Powiadomienie może wymagać potwierdzenia odbioru, dzięki czemu kierownik może zweryfikować, czy informacja została przeczytana."
      }
    ]
  },
  {
    id: "faq",
    title: "Najczęstsze problemy",
    icon: "fas fa-life-ring",
    roles: ["ALL"],
    summary: "Szybkie odpowiedzi na problemy, które najczęściej pojawiają się przy pracy z Qstaff.",
    articles: [
      {
        title: "Plan nie pojawia się przy grafiku",
        answer: "Sprawdź zgodność działu, zakresu i typu planu oraz czy plan jest aktywny i ma pozycje w okresie grafiku."
      },
      {
        title: "Generator nie przydzielił ani jednej osoby",
        answer: "Najpierw sprawdź ważność umów. Potem aktywność i etap zatrudnienia, oznaczenie Niechciany, stanowiska, dostępność, nieobecności, kolizje, kwalifikacje i reguły czasu pracy. Najdokładniejszą odpowiedź daje Diagnostyka obsady przy konkretnej pozycji."
      },
      {
        title: "Pracownik ma być dostępny, ale system go odrzuca",
        answer: "Sama dostępność nie wystarcza. Pracownik może nie mieć właściwego stanowiska, ważnej umowy, szkolenia/certyfikatu albo może kolidować z inną zmianą lub regułą czasu pracy."
      },
      {
        title: "Pracownik twierdzi, że nie widzi grafiku",
        answer: "Sprawdź, czy grafik został opublikowany i czy dana osoba faktycznie ma przypisaną zmianę. Otwarta zmiana bez pracownika nie pojawi się jako jego własny grafik."
      },
      {
        title: "RCP jest w rejestrze, ale nie ma go przy grafiku",
        answer: "Zdarzenie może dotyczyć dnia lub pracownika, dla którego nie istnieje pasująca zmiana. Taki czas nadal może pojawić się w historii pracy i raportach jako RCP bez grafiku."
      },
      {
        title: "Koszt wygląda na niepełny",
        answer: "Sprawdź stawkę pracownika, stanowisko, aktywną umowę, status uczeń/student/KRUS, konfigurację ZUS oraz prowizję firmy zatrudniającej."
      },
      {
        title: "Nie mogę zatwierdzić urlopu",
        answer: "Sprawdź limit urlopów ustawiony dla działu. W trybie Blokuj backend odrzuci zatwierdzenie, jeżeli limit zostałby przekroczony."
      }
    ]
  }
];


const employeeModules = [
  {
    id: "employee-start",
    title: "Jak korzystać z Qstaff jako pracownik",
    icon: "fas fa-compass",
    roles: ["EMPLOYEE"],
    summary: "Krótki przewodnik po funkcjach dostępnych z konta pracownika.",
    articles: [
      {
        title: "Co możesz zrobić z konta pracownika",
        bullets: [
          "Sprawdzić swój opublikowany grafik w widoku dziennym, tygodniowym, dwutygodniowym, miesięcznym albo dla własnego zakresu dat.",
          "Złożyć prośbę o wolne lub wniosek urlopowy i śledzić jego status.",
          "Zgłosić dodatkową dostępność albo preferowany termin pracy.",
          "Zgłosić chęć przejęcia otwartej zmiany oraz zaproponować zamianę zmiany z innym pracownikiem.",
          "Sprawdzić własne dane, umowę, szkolenia, certyfikaty i informacje pracownicze udostępnione w Panelu pracownika.",
          "Wykonać przypisane testy szkoleniowe, pobrać materiały i dokumenty oraz przesłać dokument do HR.",
          "Zarezerwować dostępny termin spotkania z HR albo wysłać zgłoszenie do kierownika.",
          "Odczytać i potwierdzić powiadomienia dotyczące grafiku i wniosków."
        ],
        note: "Ta wersja Bazy Wiedzy pokazuje wyłącznie funkcje dostępne dla kont pracowniczych. Nie zawiera instrukcji administracyjnych, kadrowych ani dotyczących układania grafików przez kierownika."
      },
      {
        title: "Najważniejsze miejsca w aplikacji",
        bullets: [
          "Tablica - szybki podgląd najbliższej pracy i najważniejszych informacji.",
          "Panel pracownika - dostępność, urlopy, szkolenia, dokumenty, spotkania HR i kontakt z firmą.",
          "Moje dane - Twój własny profil.",
          "Mój grafik - opublikowane zmiany oraz operacje związane z grafikiem.",
          "Powiadomienia - informacje o publikacji i zmianach grafiku oraz decyzjach dotyczących wniosków.",
          "Baza Wiedzy - instrukcje dotyczące funkcji dostępnych dla Ciebie."
        ]
      }
    ]
  },
  {
    id: "employee-dashboard",
    title: "Tablica",
    icon: "fas fa-th-large",
    roles: ["EMPLOYEE"],
    summary: "Szybki podgląd tego, co jest dla Ciebie najważniejsze po zalogowaniu.",
    links: [{path: "/app/dashboard", label: "Otwórz Tablicę", roles: ["EMPLOYEE"]}],
    articles: [
      {
        title: "Co zobaczysz na Tablicy",
        bullets: [
          "Informację, czy masz zaplanowaną zmianę dzisiaj i jutro.",
          "Najbliższe zmiany wynikające z opublikowanego grafiku.",
          "Najważniejsze powiadomienia i skróty do funkcji pracowniczych.",
          "Szybkie przejście do własnego grafiku, danych, Panelu pracownika i Bazy Wiedzy."
        ],
        tip: "Jeżeli nie widzisz oczekiwanej zmiany, sprawdź Mój grafik. Pracownik widzi tylko zmiany z opublikowanych grafików, które są faktycznie do niego przypisane."
      }
    ]
  },
  {
    id: "employee-schedule",
    title: "Mój grafik",
    icon: "fas fa-calendar-week",
    roles: ["EMPLOYEE"],
    summary: "Twój opublikowany grafik, otwarte zmiany, zamiany oraz wnioski związane z czasem pracy.",
    links: [{path: "/app/my-schedule", label: "Otwórz Mój grafik", roles: ["EMPLOYEE"]}],
    articles: [
      {
        title: "Jak przeglądać własny grafik",
        bullets: [
          "Możesz przełączać widok między dniem, tygodniem, dwoma tygodniami, miesiącem i własnym zakresem dat.",
          "Na kalendarzu widzisz swoje przypisane zmiany wraz z godzinami i stanowiskiem.",
          "Podsumowanie pokazuje liczbę zmian, dni i godzin pracy, wnioski oczekujące, otwarte zmiany w wybranym okresie oraz propozycje zamiany wymagające Twojej decyzji.",
          "Widok pokazuje również wnioski dotyczące dni widocznych w kalendarzu oraz ich aktualny status.",
          "Zmiana pojawi się u Ciebie dopiero wtedy, gdy należy do opublikowanego grafiku i została przypisana do Twojego konta pracownika."
        ]
      },
      {
        title: "Jak zgłosić wolne albo urlop",
        steps: [
          "W widoku Mój grafik kliknij Nowy wniosek.",
          "Wybierz Prośbę o wolne albo Urlop / nieobecność.",
          "Wskaż zakres dat. Przy urlopie wybierz także odpowiedni typ nieobecności.",
          "Wyślij wniosek i poczekaj na decyzję kierownika.",
          "Wniosek oczekujący na decyzję możesz anulować."
        ],
        warning: "Samo wysłanie wniosku nie zmienia grafiku. Wniosek zaczyna obowiązywać dopiero po zatwierdzeniu zgodnie z regułami systemu."
      },
      {
        title: "Jak zgłosić dodatkową dostępność",
        steps: [
          "Otwórz formularz deklaracji dostępności z Panelu pracownika albo funkcji dostępnej przy grafiku.",
          "Wybierz Mogę pracować albo Preferuję pracę.",
          "Podaj zakres dat oraz opcjonalnie godziny.",
          "Jeżeli deklaracja ma dotyczyć wybranych dni tygodnia, ustaw powtarzanie tygodniowe i zaznacz dni.",
          "Wyślij deklarację do zatwierdzenia."
        ],
        note: "Prośbę o niedostępność składaj jako wolne albo urlop. Deklaracja dostępności służy do zgłaszania terminów, w których możesz lub szczególnie chcesz pracować."
      },
      {
        title: "Otwarte zmiany",
        bullets: [
          "Otwarta zmiana to zmiana, która nie ma jeszcze przypisanego pracownika.",
          "Widzisz tylko otwarte zmiany z aktualnie wybranego zakresu dat, do których spełniasz warunki przydziału.",
          "Jeżeli spełniasz warunki, możesz wysłać zgłoszenie chęci przejęcia takiej zmiany.",
          "Samo zgłoszenie nie oznacza jeszcze przydzielenia zmiany. Na tę samą zmianę może zgłosić się kilka osób.",
          "Ostateczna decyzja należy do kierownika. Po przydzieleniu zmiana pojawi się w Twoim grafiku, a pozostałe zgłoszenia zostaną zamknięte."
        ]
      },
      {
        title: "Zamiana zmiany z innym pracownikiem",
        steps: [
          "Przy swojej zmianie wybierz możliwość zaproponowania zamiany.",
          "Wybierz jedną z dostępnych zmian innego pracownika.",
          "Wyślij propozycję. Drugi pracownik musi ją zaakceptować albo odrzucić.",
          "Po akceptacji drugiej osoby propozycja trafia do decyzji kierownika.",
          "Dopiero zatwierdzenie przez kierownika aktualizuje grafik."
        ]
      }
    ]
  },
  {
    id: "employee-portal",
    title: "Panel pracownika",
    icon: "fas fa-user-circle",
    roles: ["EMPLOYEE"],
    summary: "Twoja samoobsługa: dostępność, nieobecności, szkolenia, dokumenty, spotkania HR i kontakt.",
    links: [
      {path: "/app/employee-panel", label: "Otwórz Panel pracownika", roles: ["EMPLOYEE"]},
      {path: "/app/employee-profile", label: "Moje dane", roles: ["EMPLOYEE"]}
    ],
    articles: [
      {
        title: "Zakładki Panelu pracownika",
        bullets: [
          "Start - podstawowe informacje o Tobie, dziale, stanowiskach i najważniejsze skróty.",
          "Dostępność - zaakceptowane okresy dostępności oraz historia wysłanych deklaracji.",
          "Urlopy i nieobecności - zapisane nieobecności oraz wnioski o wolne i urlop.",
          "Szkolenia i kwalifikacje - szkolenia, certyfikaty, aktywne testy oraz historia wyników.",
          "Dokumenty - Twoje umowy i inne pliki z folderu pracowniczego, materiały udostępnione przez firmę oraz dokumenty przekazywane do HR.",
          "Spotkania HR - zaproszenia grupowe, odpowiedzi na zaproszenia, wolne terminy konsultacji i Twoje rezerwacje.",
          "Kontakt - pytanie do kierownika albo zgłoszenie do firmy."
        ]
      },
      {
        title: "Test szkoleniowy - co widzisz przed rozpoczęciem",
        bullets: [
          "W zakładce Szkolenia i kwalifikacje sekcja Testy do wykonania pokazuje aktywne testy przypisane do Twojego profilu.",
          "Przy teście widzisz nazwę, szkolenie, ewentualny dział, liczbę pytań, wymagany próg zaliczenia oraz liczbę wykorzystanych i maksymalnych podejść.",
          "Jeżeli osoba przypisująca ustawiła termin, widzisz datę wykonania. Po jej przekroczeniu termin jest oznaczony jako spóźniony.",
          "Jeżeli test był już wykonywany, widzisz również ostatni wynik i informację Zaliczony / Niezaliczony.",
          "Gdy pozostały próby, możesz użyć Rozpocznij test albo Wykonaj ponownie. Po wykorzystaniu całego limitu pojawia się informacja Brak kolejnych podejść."
        ],
        note: "Przekroczenie terminu nie blokuje obecnie samego rozwiązania testu. Oznacza przede wszystkim, że wykonanie jest po terminie."
      },
      {
        title: "Jak rozwiązać quiz",
        steps: [
          "Otwórz Panel pracownika → Szkolenia i kwalifikacje.",
          "W sekcji Testy do wykonania kliknij Rozpocznij test albo Wykonaj ponownie.",
          "Przeczytaj instrukcję widoczną nad pytaniami, jeżeli została dodana przez osobę tworzącą test.",
          "Przy pytaniu jednokrotnego wyboru zaznacz jedną odpowiedź. Przy pytaniu wielokrotnego wyboru możesz zaznaczyć kilka odpowiedzi.",
          "Odpowiedz na wszystkie pytania. Nie można wysłać testu z pytaniem pozostawionym bez odpowiedzi.",
          "Kliknij Zakończ i sprawdź.",
          "Qstaff od razu obliczy wynik i pokaże komunikat Test zaliczony albo Test niezaliczony wraz z wynikiem procentowym."
        ],
        warning: "W pytaniu wielokrotnego wyboru pytanie jest poprawne tylko wtedy, gdy zaznaczysz dokładnie wszystkie prawidłowe odpowiedzi i żadnej błędnej."
      },
      {
        title: "Podejścia, wynik i historia",
        bullets: [
          "Każde zakończone podejście zużywa jedną próbę dla aktualnej wersji testu.",
          "Po zakończeniu testu wynik automatycznie trafia do sekcji Wyniki testów w Panelu pracownika.",
          "Historia pokazuje datę, szkolenie, wynik procentowy oraz status Zaliczony / Niezaliczony.",
          "Jeżeli autor testu zmieni jego definicję i zapisze nową wersję, licznik prób dla nowej wersji rozpoczyna się od początku. Poprzednie wyniki nadal pozostają w historii.",
          "Wyniki testów są częścią oceny pracownika. Niezaliczone wyniki mogą także mieć znaczenie dla dopuszczenia do grafiku zgodnie z ustawionym miesięcznym limitem."
        ]
      },
      {
        title: "Jak wynik testu wpływa na Twoją ocenę i grafik",
        bullets: [
          "Wynik testu nie jest tylko informacją widoczną w historii. Qstaff uwzględnia wyniki testów szkoleniowych podczas wyliczania Twojej oceny ogólnej.",
          "Do oceny brana jest średnia procentowa zapisanych wyników testów, a następnie przeliczana na skalę ocen używaną w systemie.",
          "Waga testów w ocenie ogólnej jest ustawiana przez administratora. Dlatego wynik testu może mieć większy albo mniejszy wpływ na końcową ocenę pracownika.",
          "Niezależnie od średniej ocen Qstaff może stosować osobną regułę miesięcznego limitu niezaliczonych testów.",
          "Po osiągnięciu ustawionego limitu niezaliczonych testów pracownik może zostać niedopuszczony do przydzielenia do grafiku w danym miesiącu.",
          "Samo pojedyncze niezaliczenie nie oznacza automatycznie blokady grafiku. Decyduje limit skonfigurowany w systemie."
        ],
        note: "Jeżeli nie wiesz, dlaczego nie możesz zostać przydzielony do zmiany, skontaktuj się z kierownikiem lub HR. Powód kwalifikacji do grafiku jest widoczny po stronie osoby planującej grafik."
      },
      {
        title: "Kiedy test jest niedostępny albo nie można go rozpocząć",
        bullets: [
          "Test może nie być widoczny, jeżeli nie został przypisany do Twojego profilu albo przypisanie zostało wycofane.",
          "Test nie jest dostępny do rozwiązania, jeżeli został dezaktywowany przez osobę zarządzającą szkoleniami.",
          "Jeżeli wykorzystasz maksymalną liczbę podejść dla aktualnej wersji testu, przycisk rozpoczęcia zostanie zablokowany i pojawi się informacja Brak kolejnych podejść.",
          "Przekroczenie terminu wykonania samo w sobie nie blokuje testu, jeżeli test jest nadal aktywny i pozostały Ci podejścia.",
          "Po utworzeniu nowej wersji testu przez osobę zarządzającą szkoleniami otrzymujesz nową pulę podejść dla tej wersji, a wcześniejsze wyniki pozostają w historii."
        ]
      },
      {
        title: "Dokumenty i materiały",
        bullets: [
          "W sekcji Moje dokumenty pracownicze i umowy widzisz pliki z własnego folderu pracowniczego, również z podfolderu Umowy. Obsługiwane pliki można podejrzeć, a wszystkie pobrać.",
          "Możesz pobierać regulaminy, wzory dokumentów, materiały szkoleniowe i otwierać linki opublikowane przez firmę.",
          "Do HR możesz przesłać legitymację uczniowską / studencką albo inny dokument.",
          "Na liście dokumentów przekazanych do HR możesz pobrać wcześniej przesłany plik albo go usunąć."
        ],
        note: "Pliki zapisane w folderze pracowniczym są tylko do odczytu z poziomu Panelu pracownika. Dokumenty przekazane do HR pozostają oddzielną listą zarządzaną przez pracownika i uprawnione osoby po stronie firmy."
      },
      {
        title: "Indywidualna konsultacja z HR",
        steps: [
          "Przejdź do zakładki Spotkania HR.",
          "W sekcji Wolne terminy HR wybierz okienko opublikowane dla Twojego działu.",
          "Podaj temat i opcjonalną wiadomość dla HR.",
          "Zarezerwuj termin. W Moich konsultacjach z HR zobaczysz godzinę, miejsce albo link do spotkania online.",
          "Przyszłą rezerwację możesz anulować; termin wróci wtedy do puli wolnych terminów."
        ]
      },
      {
        title: "Zaproszenie na spotkanie grupowe",
        bullets: [
          "Zaproszenie pojawia się w zakładce Spotkania HR bez potrzeby rezerwowania terminu.",
          "Widzisz nazwę, termin, opis, miejsce lub link do spotkania oraz ewentualne ostrzeżenie o konflikcie z Twoją zmianą w grafiku.",
          "Na zaproszenie odpowiadasz: Będę, Wstępnie tak albo Na pewno nie.",
          "Odpowiedź możesz zmieniać do rozpoczęcia zaplanowanego spotkania.",
          "Jeżeli spotkanie zostanie zmienione albo odwołane, otrzymasz odpowiednie powiadomienie."
        ]
      },
      {
        title: "Kontakt z firmą",
        bullets: [
          "Przy pytaniu do kierownika wybierasz konkretnego przełożonego z listy wynikającej z działów i stanowisk przypisanych do Twojego profilu.",
          "Pytanie jest zapisywane w Qstaff i trafia do obsługi po stronie kierownika. Jeżeli wybrany kierownik ma włączoną komunikację e-mail lub SMS, Qstaff dodatkowo wysyła mu powiadomienie tym kanałem.",
          "W historii własnych zgłoszeń przy pytaniu widzisz, do którego kierownika zostało skierowane.",
          "Możesz przekazać zgłoszenie anonimowe. W widoku kierownika autor takiego zgłoszenia nie jest ujawniany; techniczne powiązanie z kontem pozostaje wyłącznie w danych systemowych.",
          "Historia wysłanych zgłoszeń oraz odpowiedzi kierownika pozostaje dostępna w Panelu pracownika.",
          "Spotkanie z HR rezerwuje się osobno w zakładce Spotkania HR."
        ],
        warning: "Jeżeli lista kierowników jest pusta, administrator lub HR powinien uzupełnić kierownika albo zastępcę kierownika w dziale pracownika."
      },
      {
        title: "Jak zadać pytanie wybranemu kierownikowi",
        steps: [
          "Otwórz Panel pracownika → Kontakt i wybierz Nowe zgłoszenie.",
          "W polu Rodzaj wybierz Pytanie do kierownika.",
          "W polu Kierownik wybierz osobę, do której chcesz skierować pytanie. Lista pokazuje przełożonych przypisanych do działów, w których pracujesz.",
          "Wpisz treść i kliknij Wyślij.",
          "Po wysłaniu pytanie pojawi się w Twojej historii. Przy wpisie zobaczysz nazwisko wybranego kierownika oraz późniejszą odpowiedź, gdy zostanie udzielona."
        ],
        note: "Anonimowe zgłoszenie działa inaczej: nie wybierasz konkretnego odbiorcy, a kierownik w swoim widoku nie otrzymuje danych identyfikujących autora."
      },
      {
        title: "Moje dane",
        lead: "Ekran Moje dane dotyczy wyłącznie Twojego profilu. Nie daje dostępu do kartotek innych pracowników ani do ustawień administracyjnych.",
        note: "Jeżeli nie możesz zmienić określonej informacji kadrowej, oznacza to, że pole jest przeznaczone do obsługi przez HR lub administratora."
      }
    ]
  },
  {
    id: "employee-notifications",
    title: "Powiadomienia",
    icon: "fas fa-bell",
    roles: ["EMPLOYEE"],
    summary: "Informacje o zmianach dotyczących Twojego grafiku i wniosków.",
    links: [{path: "/app/notifications", label: "Otwórz Powiadomienia", roles: ["EMPLOYEE"]}],
    articles: [
      {
        title: "Jakie informacje możesz otrzymywać",
        bullets: [
          "Publikacja grafiku obejmującego Twoje zmiany.",
          "Zmiana w już opublikowanym grafiku.",
          "Decyzja dotycząca wniosku o wolne, urlopu lub dostępności.",
          "Propozycja zamiany zmiany oraz kolejne decyzje w tym procesie.",
          "Zaproszenie na spotkanie grupowe HR, zmiana jego terminu lub danych oraz odwołanie spotkania."
        ]
      },
      {
        title: "Potwierdzanie odbioru",
        lead: "Niektóre powiadomienia mogą wymagać potwierdzenia, że zapoznałeś się z informacją.",
        steps: [
          "Otwórz Powiadomienia.",
          "Przeczytaj treść wiadomości.",
          "Jeżeli widoczny jest przycisk Potwierdź odbiór, użyj go po zapoznaniu się z informacją.",
          "Po potwierdzeniu system zapisuje datę i godzinę odbioru.",
          "Przy powiadomieniu możesz też zobaczyć stan wysyłki e-mail i SMS. Status dostarczono przy SMS oznacza techniczne potwierdzenie operatora i nie zastępuje Twojego przycisku Potwierdź odbiór."
        ]
      }
    ]
  },
  {
    id: "employee-faq",
    title: "Najczęstsze pytania pracownika",
    icon: "fas fa-life-ring",
    roles: ["EMPLOYEE"],
    summary: "Szybkie odpowiedzi na typowe problemy w części pracowniczej.",
    articles: [
      {
        title: "Nie widzę swojej zmiany",
        answer: "Sprawdź Mój grafik i właściwy zakres dat. Pracownik widzi przypisane do siebie zmiany z opublikowanych grafików. Jeżeli kierownik nie opublikował grafiku albo zmiana nie jest przypisana do Ciebie, nie pojawi się jako Twoja zmiana."
      },
      {
        title: "Wysłałem wniosek, ale grafik się nie zmienił",
        answer: "To prawidłowe zachowanie. Wniosek o wolne, urlop, dostępność, otwartą zmianę albo zamianę ma własny proces akceptacji. Samo wysłanie wniosku nie zmienia opublikowanego grafiku."
      },
      {
        title: "Nie mogę wybrać daty urlopu albo wolnego",
        answer: "System może wymagać minimalnego wyprzedzenia dla wniosków. Formularz ogranicza daty zgodnie z aktualną polityką ustawioną w Qstaff."
      },
      {
        title: "Nie mogę zgłosić się do otwartej zmiany",
        answer: "Zmiana może być poza wybranym okresem, już obsadzona albo możesz nie spełniać warunków wymaganych do jej przejęcia. Odśwież widok i sprawdź aktualne otwarte zmiany."
      },
      {
        title: "Nie widzę wolnych terminów spotkania z HR",
        answer: "Lista konsultacji indywidualnych pokazuje tylko przyszłe, aktywne i niezarezerwowane terminy opublikowane dla Twojego działu. Spotkania grupowe są osobnymi zaproszeniami i pojawiają się w tej samej zakładce niezależnie od listy wolnych okienek."
      },
      {
        title: "Mam konflikt z grafikiem przy zaproszeniu na spotkanie HR",
        answer: "To ostrzeżenie oznacza, że w czasie spotkania masz przypisaną zmianę w grafiku. Możesz nadal odpowiedzieć Będę, Wstępnie tak albo Na pewno nie, ale zaproszenie samo nie usuwa ani nie zmienia Twojej zmiany. HR również widzi ten konflikt."
      },
      {
        title: "Nie widzę testu szkoleniowego",
        answer: "W Panelu pracownika pojawiają się aktywne testy przypisane bezpośrednio do Twojego profilu. Dla zgodności ze starszymi danymi system może też pokazać aktywny quiz globalny wynikający z przypisanego szkolenia, jeżeli nie istnieje dla niego osobne przypisanie. Test może być niewidoczny, gdy został wycofany, jest nieaktywny, dotyczy innego działu albo typ szkolenia nie ma formy Quiz."
      },
      {
        title: "Termin testu minął - czy mogę go jeszcze rozwiązać?",
        answer: "Tak, o ile test i Twoje przypisanie są nadal aktywne oraz masz niewykorzystane podejścia. Qstaff oznacza przekroczony termin, ale obecnie sama data terminu nie blokuje wysłania testu."
      },
      {
        title: "Wykorzystałem wszystkie podejścia do testu",
        answer: "Po wykorzystaniu maksymalnej liczby podejść dla aktualnej wersji testu nie możesz rozpocząć kolejnej próby. Historia dotychczasowych wyników pozostaje widoczna. Kolejne podejścia będą możliwe dopiero wtedy, gdy osoba zarządzająca testem udostępni nową wersję albo zmieni konfigurację zgodnie z przyjętym procesem szkoleniowym."
      },
      {
        title: "Dlaczego nie mogę wykonać testu ponownie?",
        answer: "Najczęściej wykorzystano maksymalną liczbę podejść dla aktualnej wersji testu. Panel pokazuje liczbę wykorzystanych i pozostałych prób. Test może być również wycofany z Twojego profilu albo wyłączony przez osobę zarządzającą szkoleniami."
      },
      {
        title: "Czy niezaliczony test może zablokować mnie w grafiku?",
        answer: "Może. Qstaff ma ustawienie miesięcznego limitu niezaliczonych testów szkoleniowych. Po osiągnięciu ustawionego limitu pracownik jest twardo odrzucany przy kwalifikacji do zmiany w tym miesiącu. Wartość 0 wyłącza tę regułę."
      }
    ]
  }
];

const roleFromAuth = roles => {
  if (roles.includes(ROLES.ADMIN)) return "ADMIN";
  if (roles.includes(ROLES.HR)) return "HR";
  if (roles.includes(ROLES.MANAGER)) return "MANAGER";
  if (roles.includes(ROLES.EMPLOYEE) || roles.includes(ROLES.MINION)) return "EMPLOYEE";
  return "USER";
};

const knowledgeRolesFromAuth = roles => {
  if (roles.includes(ROLES.ADMIN)) return ["ADMIN", "MANAGER", "HR", "EMPLOYEE", "USER"];
  const result = [];
  if (roles.includes(ROLES.MANAGER)) result.push("MANAGER");
  if (roles.includes(ROLES.HR)) result.push("HR");
  if (roles.includes(ROLES.EMPLOYEE) || roles.includes(ROLES.MINION)) result.push("EMPLOYEE");
  if (roles.includes(ROLES.USER) || result.length === 0) result.push("USER");
  return result;
};

const canSeeForRole = (itemRoles, role) => {
  if (!itemRoles || itemRoles.length === 0 || itemRoles.includes("ALL")) return true;
  if (role === "ALL") return true;
  return itemRoles.includes(role);
};

const canAccessForRoles = (itemRoles, allowedRoles) => {
  if (!itemRoles || itemRoles.length === 0 || itemRoles.includes("ALL")) return true;
  return itemRoles.some(role => allowedRoles.includes(role));
};

const normalizeSearchText = value => String(value || "")
  .toLocaleLowerCase("pl-PL")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/ł/g, "l");

const searchMatches = (value, normalizedQuery) => normalizeSearchText(value).includes(normalizedQuery);

const articleTargetId = (moduleId, articleIndex) => `kb-${moduleId}-article-${articleIndex}`;
const articlePartTargetId = (moduleId, articleIndex, part, itemIndex = null) =>
  `${articleTargetId(moduleId, articleIndex)}-${part}${itemIndex === null ? "" : `-${itemIndex}`}`;

const articleSearchParts = (moduleId, article, articleIndex) => {
  const parts = [];
  const add = (part, label, value, itemIndex = null) => {
    if (!value) return;
    parts.push({
      label,
      text: value,
      targetId: articlePartTargetId(moduleId, articleIndex, part, itemIndex)
    });
  };

  add("lead", "Wprowadzenie", article.lead);
  add("answer", "Odpowiedź", article.answer);
  (article.steps || []).forEach((value, index) => add("step", `Krok ${index + 1}`, value, index));
  (article.bullets || []).forEach((value, index) => add("bullet", `Punkt ${index + 1}`, value, index));
  add("example", "Przykład", article.example);
  add("tip", "Wskazówka", article.tip);
  add("note", "Informacja", article.note);
  add("warning", "Uwaga", article.warning);
  return parts;
};

const KnowledgeBase = () => {
  const {auth} = useAuth();
  const authRoles = normalizeRoles(auth?.roles);
  const myRole = roleFromAuth(authRoles);
  const allowedKnowledgeRoles = knowledgeRolesFromAuth(authRoles);
  const employeeKnowledgeBase = (authRoles.includes(ROLES.EMPLOYEE) || authRoles.includes(ROLES.MINION))
    && !authRoles.some(role => [ROLES.ADMIN, ROLES.MANAGER, ROLES.HR].includes(role));
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [query, setQuery] = useState("");
  const [activeModule, setActiveModule] = useState(employeeKnowledgeBase ? "employee-start" : "start");

  const normalizedQuery = normalizeSearchText(query.trim());
  const knowledgeModules = employeeKnowledgeBase ? employeeModules : modules;
  const effectiveRoleFilter = employeeKnowledgeBase ? "EMPLOYEE" : roleFilter;

  const authorizedModules = useMemo(() =>
    knowledgeModules.filter(module => canAccessForRoles(module.roles, allowedKnowledgeRoles)),
  [knowledgeModules, allowedKnowledgeRoles]);

  const visibleModules = useMemo(() =>
    authorizedModules.filter(module => canSeeForRole(module.roles, effectiveRoleFilter)),
  [authorizedModules, effectiveRoleFilter]);

  const searchResults = useMemo(() => {
    if (!normalizedQuery) return [];
    const results = [];

    visibleModules.forEach(module => {
      if (searchMatches(module.title, normalizedQuery) || searchMatches(module.summary, normalizedQuery)) {
        results.push({
          kind: "Sekcja",
          moduleId: module.id,
          moduleTitle: module.title,
          articleTitle: null,
          label: "Opis sekcji",
          text: module.summary,
          targetId: `kb-${module.id}`
        });
      }

      module.articles.forEach((article, articleIndex) => {
        if (searchMatches(article.title, normalizedQuery)) {
          results.push({
            kind: "Temat",
            moduleId: module.id,
            moduleTitle: module.title,
            articleTitle: article.title,
            label: "Nagłówek tematu",
            text: article.lead || article.answer || article.note || module.summary,
            targetId: articleTargetId(module.id, articleIndex)
          });
        }

        articleSearchParts(module.id, article, articleIndex)
          .filter(part => searchMatches(part.text, normalizedQuery))
          .forEach(part => results.push({
            kind: "Fragment",
            moduleId: module.id,
            moduleTitle: module.title,
            articleTitle: article.title,
            label: part.label,
            text: part.text,
            targetId: part.targetId
          }));
      });
    });

    return results;
  }, [normalizedQuery, visibleModules]);

  const selectedModule = useMemo(() =>
    visibleModules.find(module => module.id === activeModule) || visibleModules[0] || null,
  [activeModule, visibleModules]);

  const quickLinks = useMemo(() => {
    if (employeeKnowledgeBase) {
      return [
        {moduleId: "employee-dashboard", label: "Tablica", icon: "fas fa-th-large"},
        {moduleId: "employee-schedule", label: "Mój grafik", icon: "fas fa-calendar-week"},
        {moduleId: "employee-portal", label: "Panel pracownika", icon: "fas fa-user-circle"},
        {moduleId: "employee-notifications", label: "Powiadomienia", icon: "fas fa-bell"},
        {moduleId: "employee-faq", label: "Pomoc", icon: "fas fa-life-ring"}
      ];
    }

    const common = [
      {moduleId: "dashboard", label: "Tablica", icon: "fas fa-th-large", roles: ["ADMIN", "HR", "MANAGER", "EMPLOYEE"]},
      {moduleId: "start", label: "Baza Wiedzy", icon: "fas fa-book-open", roles: ["ALL"]}
    ];
    const admin = [
      {moduleId: "employees", label: "Pracownicy", icon: "fas fa-users", roles: ["ADMIN", "HR", "MANAGER"]},
      {moduleId: "hr-meetings", label: "Spotkania HR", icon: "fas fa-calendar-check", roles: ["ADMIN", "HR", "MANAGER"]},
      {moduleId: "users-access", label: "Użytkownicy", icon: "fas fa-user-shield", roles: ["ADMIN"]},
      {moduleId: "contracts", label: "Umowy", icon: "fas fa-file-contract", roles: ["ADMIN", "HR"]}
    ];
    const planning = [
      {moduleId: "resource-plans", label: "Plany", icon: "fas fa-clipboard-list", roles: ["ADMIN", "MANAGER"]},
      {moduleId: "schedules", label: "Grafik", icon: "fas fa-calendar-alt", roles: ["ADMIN", "MANAGER"]},
      {moduleId: "rcp", label: "RCP", icon: "fas fa-clock", roles: ["ADMIN", "MANAGER"]}
    ];
    const employee = [
      {moduleId: "employee-zone", label: "Mój grafik", icon: "fas fa-calendar-week", roles: ["EMPLOYEE"]},
      {moduleId: "employee-zone", label: "Moje dane", icon: "fas fa-id-card", roles: ["EMPLOYEE", "ADMIN"]}
    ];
    return [...common, ...admin, ...planning, ...employee].filter(link => canAccessForRoles(link.roles, allowedKnowledgeRoles));
  }, [employeeKnowledgeBase, allowedKnowledgeRoles]);

  const openKnowledgeSection = moduleId => {
    setQuery("");
    if (!employeeKnowledgeBase) setRoleFilter("ALL");
    setActiveModule(moduleId);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.querySelector(".kb-layout")?.scrollIntoView({behavior: "smooth", block: "start"});
      });
    });
  };

  const openSearchResult = result => {
    setQuery("");
    setActiveModule(result.moduleId);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const target = document.getElementById(result.targetId) || document.getElementById(`kb-${result.moduleId}`);
        if (!target) return;
        const details = target.matches("details") ? target : target.closest("details");
        if (details) details.open = true;
        target.scrollIntoView({behavior: "smooth", block: "center"});
        target.classList.add("kb-search-target-flash");
        window.setTimeout(() => target.classList.remove("kb-search-target-flash"), 1800);
      });
    });
  };

  const renderArticle = (moduleId, article, index) => (
    <details className="kb-article" id={articleTargetId(moduleId, index)} key={`${article.title}-${index}`} open={index === 0}>
      <summary>
        <span>{article.title}</span>
        <i className="fas fa-chevron-down"></i>
      </summary>
      <div className="kb-article-body">
        {article.lead && <p className="kb-lead" id={articlePartTargetId(moduleId, index, "lead")}>{article.lead}</p>}
        {article.answer && <p id={articlePartTargetId(moduleId, index, "answer")}>{article.answer}</p>}
        {article.steps && (
          <ol className="kb-steps">
            {article.steps.map((step, stepIndex) => <li id={articlePartTargetId(moduleId, index, "step", stepIndex)} key={stepIndex}>{step}</li>)}
          </ol>
        )}
        {article.bullets && (
          <ul className="kb-bullets">
            {article.bullets.map((bullet, bulletIndex) => <li id={articlePartTargetId(moduleId, index, "bullet", bulletIndex)} key={bulletIndex}>{bullet}</li>)}
          </ul>
        )}
        {article.example && <div className="kb-callout kb-callout-example" id={articlePartTargetId(moduleId, index, "example")}><i className="fas fa-calculator"></i><span>{article.example}</span></div>}
        {article.tip && <div className="kb-callout kb-callout-tip" id={articlePartTargetId(moduleId, index, "tip")}><i className="fas fa-lightbulb"></i><span>{article.tip}</span></div>}
        {article.note && <div className="kb-callout kb-callout-note" id={articlePartTargetId(moduleId, index, "note")}><i className="fas fa-info-circle"></i><span>{article.note}</span></div>}
        {article.warning && <div className="kb-callout kb-callout-warning" id={articlePartTargetId(moduleId, index, "warning")}><i className="fas fa-exclamation-triangle"></i><span>{article.warning}</span></div>}
      </div>
    </details>
  );

  const renderModule = module => (
    <section className="kb-module" key={module.id} id={`kb-${module.id}`}>
      <div className="kb-module-header">
        <div className="kb-module-icon"><i className={module.icon}></i></div>
        <div>
          <div className="kb-module-title-row">
            <h2>{module.title}</h2>
            <div className="kb-role-badges">
              {(module.roles || ["ALL"]).map(role => <span key={role}>{ROLE_LABELS[role]}</span>)}
            </div>
          </div>
          <p>{module.summary}</p>
          {module.links && (
            <div className="kb-module-links">
              {module.links.filter(link => canAccessForRoles(link.roles, allowedKnowledgeRoles)).map(link => (
                <Link key={link.path} to={link.path}><i className="fas fa-arrow-right"></i>{link.label}</Link>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="kb-articles">
        {module.articles.map((article, index) => renderArticle(module.id, article, index))}
      </div>
    </section>
  );

  return (
    <div id="layoutSidenav_content" className="knowledge-base-page">
      <div className="container-fluid px-4 py-4">
        <header className="kb-hero kb-hero-full">
          <div className="kb-hero-copy">
            <div className="kb-eyebrow">Qstaff · instrukcja obsługi</div>
            <h1>Baza Wiedzy</h1>
            <p>{employeeKnowledgeBase
              ? "Instrukcje dotyczące Twojego grafiku, wniosków, dostępności, danych, szkoleń, dokumentów, spotkań HR i powiadomień."
              : "Co jest w programie, do czego służy i jak z tego korzystać - od dodania pracownika, przez plany i grafiki, aż po RCP, koszty i raporty."}</p>
            <div className="kb-current-role"><i className="fas fa-user-shield"></i> Twoja rola: <strong>{ROLE_LABELS[myRole]}</strong></div>
          </div>
          <div className="kb-hero-search">
            <label htmlFor="kb-search">Znajdź odpowiedź</label>
            <div className="kb-search-box">
              <i className="fas fa-search"></i>
              <input
                id="kb-search"
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder={employeeKnowledgeBase ? "np. grafik, urlop, dostępność, dokument, szkolenie…" : "np. umowa, brak obsady, urlop, RCP, SPMH…"}
              />
              {query && <button type="button" onClick={() => setQuery("")} aria-label="Wyczyść wyszukiwanie"><i className="fas fa-times"></i></button>}
            </div>
          </div>
        </header>

        <div className="kb-quick-grid">
          {quickLinks.map(link => (
            <button
              type="button"
              className={`kb-quick-card ${selectedModule?.id === link.moduleId ? "active" : ""}`}
              key={`${link.moduleId}-${link.label}`}
              onClick={() => openKnowledgeSection(link.moduleId)}
            >
              <i className={link.icon}></i>
              <span>{link.label}</span>
            </button>
          ))}
        </div>

        {employeeKnowledgeBase ? (
          <div className="kb-toolbar">
            <div>
              <strong>Instrukcja pracownika</strong>
              <span>Widzisz tylko tematy dotyczące funkcji dostępnych z Twojego konta.</span>
            </div>
          </div>
        ) : (
          <div className="kb-toolbar">
            <div>
              <strong>Zakres instrukcji:</strong>
              <span>widzisz tylko tematy wynikające z ról Twojego konta</span>
            </div>
            <div className="kb-role-filter">
              {["ALL", ...allowedKnowledgeRoles.filter(role => role !== "USER")].map(role => (
                <button
                  type="button"
                  key={role}
                  className={roleFilter === role ? "active" : ""}
                  onClick={() => {
                    setRoleFilter(role);
                    setActiveModule("start");
                  }}
                >
                  {role === "ALL" ? "Cała aplikacja" : ROLE_LABELS[role]}
                </button>
              ))}
            </div>
          </div>
        )}

        {normalizedQuery ? (
          <div className="kb-search-results">
            <div className="kb-search-results-heading">
              <div>
                <h2>Wyniki wyszukiwania</h2>
                <p>Znaleziono {searchResults.length} wyników. Wyszukiwanie obejmuje nagłówki, opisy, kroki, punkty, przykłady, wskazówki i ostrzeżenia.</p>
              </div>
            </div>
            {searchResults.length > 0 ? (
              <div className="kb-search-hit-list">
                {searchResults.map((result, index) => (
                  <button
                    type="button"
                    className="kb-search-hit"
                    key={`${result.moduleId}-${result.targetId}-${index}`}
                    onClick={() => openSearchResult(result)}
                  >
                    <span className="kb-search-hit-path">
                      <strong>{result.kind}</strong>
                      <span>{result.moduleTitle}{result.articleTitle ? ` → ${result.articleTitle}` : ""}</span>
                    </span>
                    <span className="kb-search-hit-label">{result.label}</span>
                    <span className="kb-search-hit-text">{result.text}</span>
                    <span className="kb-search-hit-open"><i className="fas fa-arrow-right"></i> Otwórz w Bazie Wiedzy</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="kb-empty">
                <i className="fas fa-search"></i>
                <strong>Nie znalazłem takiego tematu</strong>
                <span>Spróbuj krótszego hasła, np. „grafik”, „umowa”, „RCP”, „spotkanie” albo „urlop”.</span>
              </div>
            )}
          </div>
        ) : (
          <div className="kb-layout">
            <aside className="kb-index">
              <div className="kb-index-title">Spis treści</div>
              {visibleModules.map(module => (
                <button
                  type="button"
                  key={module.id}
                  className={selectedModule?.id === module.id ? "active" : ""}
                  onClick={() => setActiveModule(module.id)}
                >
                  <i className={module.icon}></i>
                  <span>{module.title}</span>
                </button>
              ))}
            </aside>

            <main className="kb-content">
              {selectedModule ? renderModule(selectedModule) : (
                <div className="kb-empty">Brak tematów dla wybranego zakresu.</div>
              )}
            </main>
          </div>
        )}

        <section className="kb-footer-guide">
          <div>
            <i className={employeeKnowledgeBase ? "fas fa-user-circle" : "fas fa-route"}></i>
            <div>
              <strong>{employeeKnowledgeBase ? "Najważniejsze funkcje pracownika" : "Najkrótsza droga do poprawnego grafiku"}</strong>
              <span>{employeeKnowledgeBase
                ? "Mój grafik → wnioski i dostępność → Panel pracownika → szkolenia i dokumenty → powiadomienia."
                : "Dział → stanowiska → pracownik → umowa i stawka → kwalifikacje i dostępność → plan zapotrzebowania → grafik → publikacja → RCP → raport."}</span>
            </div>
          </div>
          <button type="button" onClick={() => {
            setQuery("");
            if (!employeeKnowledgeBase) setRoleFilter("ALL");
            setActiveModule(employeeKnowledgeBase ? "employee-start" : "start");
            window.scrollTo({top: 0, behavior: "smooth"});
          }}>
            Wróć do początku
          </button>
        </section>
      </div>
</div>
  );
};

export default KnowledgeBase;
